import Phaser from 'phaser';
import { World } from './world.js';
import { bossHudState } from './boss.js';
import { NIGHT_DOG_ART, nightDogTexture, dogVisualScale } from '../dogart.js';
import { NIGHTS, FLOCK_START, BASE_LOSS, UNIT, PASU, NIGHT_SEC, LOSS_TABLE,
         STAB, PLAYER, pieceReq, ENEMY, GRASSHOPPER, RESCUE,
         SPAWN_SHAPE,
         BUILD, orbitRadius, spinRate, maxStones, EQUIP_FX,
         STONE_R, EFFECT, spinCap, ACT, ORBIT, DOG, DOGFX } from './config.js';
import { SLING, soft } from './config.js';
// ── 돌 셋의 **그림 채널** 【2026-07-30】 판정이 아니다 — scene 만 읽는다 ──────
//   LINEV  성긴 고른 돌 「선」  — 날아가는 돌 뒤로 돌을 더 놓는다(트레일이 아니다)
//   SKIPV  거친 납작한 돌      — **자국이 앞으로 줄줄이** + 돌이 통통 떠오른다
//   SETFX  세트 표시          — **색이 아니라** 거꾸로 도는 토막 링(충돌 6)
import { LINEV, SKIPV, SETFX,
         // 【2026-08-02】 벽 제거 — 화면 크기(바닥 타일)와 스폰·치움 반경의 정본
         FIELD } from './config.js';
import { SHAKE, BLOOD, DMGTEXT, HEARTH } from './mods.js';
import { GearShop } from './gearshop.js';   // 부품 상점 — 무기는 물맷돌 하나다(지팡이 삭제 2026-07-31)
import { pumpCombatGearShop } from './sceneflow.js';
import { GearUI } from './gearui.js';       // 왼쪽 낀 것 4칸 / 오른쪽 살 것 4칸
import { loadGearArt } from './gearart.js'; // 부위 아이콘 — 도착한 것만 로드한다
import { COMPACT_STATUS_HUD, PauseGearHud, pauseGearHudVisible,
         tierColor as gearTierColor } from './gearhud.js';
import { PLAYER_HP_BAR, playerHpBarState, resolveCombatAim } from './combatui.js';
// 밤 바닥 — **표는 `fieldart.js` 하나다.** 배포 스크립트도 같은 파일을 읽는다
//   (구 판은 scene 과 prepare-sites 가 목록을 각자 들고 있었다 — 표가 둘).
import { fieldMasterFor } from './fieldart.js';
import { AudioCues, CUES, lowHealthBreathMix } from './audio.js';
import { CombatVfx } from './vfx.js';
import { BEHAVIOR_AUDIO_BUDGET, cameraOffset, diamondBeat,
         diamondLocalAlpha, diamondSkipAccent, enemyBehaviorPose,
         enemyWarningProgress, isDiamondSetPresentation,
         orbitContactFeedback, orbitGhostCount, resolveBossAttackGeometry,
         compactDamageText, resolveBehaviorAudioEvent, resolveEnemyBehavior } from './presentation.js';
import { TrajectoryTrail, drawTrailBatch } from './trajectory.js';
import { FIRE } from './mods.js';
import { bakeAtlas } from '../textures.js';
import { WorkTimer, BUDGET_60 } from '../gputimer.js';
import { StandardGamepadInput, PlayerDamageHaptics,
         DualSenseUsbHaptics, gamepadFamily, gamepadLabels,
         playerHpDrop } from '../bridge/gamepad.js';
import { SPECIES, FRAME_ALPHA_BOTTOM, ATLAS_SPECIES, PREPARED_ONLY, ATLAS_KEY,
         bakeEnemyAtlas, sheetKeyOf, sheetUrlOf, frameName, anchorOffsetY,
         attackSheetKeyOf, attackSheetUrlOf, attackFrameName,
         speciesRosterForNight, makeSpeciesPicker, walkFpsFor } from './enemyart.js';

// **G7: 밤 화면 = 1920×1080 한 화면 고정.** 카메라 스크롤·타일링 없음.
// 플레이어는 World.movePlayer가 [20, W−20]×[20, H−20]으로 클램프하고,
// 스폰은 이 사각형 **바깥 테두리**에 붙는다(World.spawnPoint). 두 계산이 이 상수에 걸려 있다.
const W = 1920, H = 1080;
const GRASSHOPPER_FRAME = Object.freeze({ idle: 0, compress: 1, air: 2, land: 3 });
// presentation.forward is authored in source-cell pixels.  Even the most
// forceful pose may move the rendered body only this far from its collision
// centre; world position, sweep and targeting remain untouched.
const ENEMY_POSE_FORWARD_MAX = 2;
const EAGLE_COMBAT_KEY = 'eagle-combat-v2';
const EAGLE_COMBAT_URL = '/art/enemies/eagle/v2/combat-6x2.png';
const EAGLE_FLIGHT_KEY = 'eagle-flight-v1';
const EAGLE_FLIGHT_URL = '/art/enemies/eagle/v1/sheet-4x2.png';
const EAGLE_CELL = 128;
const EAGLE_BODY_ANCHOR_Y = 112 / EAGLE_CELL;
const EAGLE_IMPACT_HOLD = 0.20;
const EAGLE_LOW_PREPARE_HOLD = 0.75;
// New preview staging contract: N10 only, body silhouette only. This creates no
// world object and therefore cannot be targeted, deal damage, cast a shadow or drop loot.
const EAGLE_PREVIEW = Object.freeze({
  night: 'N10', start: NIGHT_SEC / 2, duration: 5,
  x0: -128, x1: W + 128, y: H * 0.25,
  frame: 0, tint: 0x120d0a, alpha: 0.72,
});
const HOWL_AT = SPAWN_SHAPE.midBands[0][0] * NIGHT_SEC - 30;
function grasshopperAirArc(e) {
  if (e.motionState !== 'air') return 0;
  const u = Math.max(0, Math.min(1, (Number(e.motionT) || 0) / GRASSHOPPER.phaseSec));
  return Math.sin(Math.PI * u);
}
const COL = { swarm: 0x2a3350, elite: 0x6b3030, hyena: 0x7a4a20 };   // ← 구 원(circle) 모드 전용
const WATCHMAN = '/art/player/픽셀파수꾼';
const REACTION = `${WATCHMAN}/reaction-v1`;
const FACE_ROW = { right: 0, left: 1, front: 2, back: 3 };
// 물맷돌 전용 8방향. 행은 북쪽부터 시계 방향이며, 지팡이의 4방향 리그와
// 섞지 않는다. 대각선은 각각 3/4 후면·3/4 정면으로 따로 그린 프레임이다.
const SLING_FACE_ROW = {
  back: 0, backRight: 1, right: 2, frontRight: 3,
  front: 4, frontLeft: 5, left: 6, backLeft: 7,
};
// 각 프레임에서 물매 끈이 빠져나오는 든손의 엄지·검지 자리.
// 캐릭터에는 물매를 굽지 않고 이 소켓에 런타임 끈·돌을 붙인다.
const SLING_HAND_SOCKET = {
  back:       [[42, 20], [41, 20], [41, 20], [42, 20]],
  backRight:  [[42, 22], [41, 22], [42, 22], [41, 22]],
  right:      [[25, 22], [26, 22], [25, 22], [25, 22]],
  frontRight: [[24, 21], [24, 21], [25, 21], [24, 21]],
  front:      [[21, 19], [23, 19], [22, 19], [22, 19]],
  frontLeft:  [[39, 21], [39, 21], [38, 21], [39, 21]],
  left:       [[38, 22], [37, 22], [38, 22], [38, 22]],
  backLeft:   [[21, 22], [22, 22], [21, 22], [22, 22]],
};
// 대기 시트의 든손 자리. 64칸 기준이다(걷기 표와 같은 좌표계).
//   A칸 = 걷기 0번과 동일 · B칸 = 아래로 1.5px(시트 256 기준 6px).
//   이 수는 그림보다 먼저 정한 계약이다. 그림이 이 자리에 손을 그린다.
//     3칸 시트로 바뀌면 가운데에 [x, y+0.75] 를 끼운다.
const SLING_IDLE_HAND_SOCKET = {
  back:       [[42, 20.0], [42, 21.5]],
  backRight:  [[42, 22.0], [42, 23.5]],
  right:      [[25, 22.0], [25, 23.5]],
  frontRight: [[24, 21.0], [24, 22.5]],
  front:      [[21, 19.0], [21, 20.5]],
  frontLeft:  [[39, 21.0], [39, 22.5]],
  left:       [[38, 22.0], [38, 23.5]],
  backLeft:   [[21, 22.0], [21, 23.5]],
};
const BODY_ANCHOR_Y = 60;       // 64 × 0.9375
const WALK_FPS = 6;
const IDLE_FPS = 15;             // 최종 채택값: 6 FPS 기준의 2.5배

// 개 전투 시트 공통 계약 — NIGHT_DOG_ART의 여덟 종류가 모두 6×2를 지킨다.
// 행은 우/좌, 열은 0 대기 · 1~2 보행 · 3 요격/돌진 · 4 버팀/준비 · 5 물기다.
// 종별 시트가 이 계약과 달라지는 날에는 여기서 추측하지 말고 dogart 메타데이터로
// 옮긴다. 지금은 실제 여덟 장을 전수 확인한 공통 표라 모든 개가 같은 런타임을 탄다.
const DOG_FRAME = Object.freeze({
  cols: 6,
  walk: Object.freeze([1, 2]),
  intercept: Object.freeze([3, 4]),
  attack: Object.freeze([4, 3, 5]),          // brace → lunge → bite
});
// 8종 96px 시트를 전수 측정한 공통 발바닥: 48px 셀의 y=42~43, 중앙에서 약 19px.
// 원점은 그대로 중앙에 두고 배율 차이만 이 거리만큼 되감아 기존 월드 기준선을 지킨다.
const DOG_FOOT_OFFSET = 19;


function faceFromVector(x, y) {
  if (Math.abs(x) > Math.abs(y)) return x >= 0 ? 'right' : 'left';
  return y >= 0 ? 'front' : 'back';
}
function slingFaceFromVector(x, y) {
  if (Math.abs(x) + Math.abs(y) < 0.001) return 'front';
  const octant = (Math.round(Math.atan2(y, x) / (Math.PI / 4)) + 8) % 8;
  return [
    'right', 'frontRight', 'front', 'frontLeft',
    'left', 'backLeft', 'back', 'backRight',
  ][octant];
}
// 삼각 화살표 하나. **방향 패널의 전부다** — 숫자도 눈금도 없다(명세 6-3 507행).
function arrow(g, cx, cy, a, s, color, alpha) {
  const px = (r, o) => [cx + Math.cos(a + o) * r, cy + Math.sin(a + o) * r];
  const [x1, y1] = px(s, 0), [x2, y2] = px(s * 0.62, 2.5), [x3, y3] = px(s * 0.62, -2.5);
  g.fillStyle(color, alpha);
  g.fillTriangle(x1, y1, x2, y2, x3, y3);
}

const BOSS_HUD = Object.freeze({ x: 600, y: 140, w: 720, h: 102, barX: 616, barW: 688 });
const BOSS_PHASES = 3;

function clamp01(v) { return Math.max(0, Math.min(1, Number(v) || 0)); }
function smooth01(v) {
  const k = clamp01(v);
  return k * k * (3 - 2 * k);
}

// 밤 화면은 필드·배우 위에 조명, 날씨, 전투 VFX, 화면 반응을 적극적으로 쌓는다.
// 다만 A8의 핵심은 유지한다: 시야를 빼앗아 난도를 만드는 것이 아니라, 수와 움직임으로
// 압박한다. 분위기 층은 실루엣과 위험 예고를 선명하게 만드는 쪽으로 튜닝한다.
const FIELD_BG = '#141b2b';

export class Graybox extends Phaser.Scene {
  constructor() { super('gb'); }
  init(d) {
    this.nightKey = d?.night ?? 'N3';
    this.weapon = d?.weapon ?? 'pasu';
    this.speed = d?.speed ?? 1;
    this.hp = d?.hp ?? undefined;      // 미지정이면 config 확정값(10-A 확정 ③ = 1000)
    // ── 8-6a / 측정 ⑦ ────────────────────────────────────────
    // 구 주석: *"빌드는 이펙트에만 걸린다. 월드 규칙은 무강화 그대로다."*
    // **【10-B 6-2】 그것이 회색박스의 가장 큰 구멍이었다.** 레벨업을 13~14회 세면서
    // 그 레벨업이 무기에 아무 영향도 주지 않았고, 그래서 「한 번도 세지지 않는
    // 플레이어」 위에서 스폰·안정성 상수가 전부 적합됐다.
    // 이제 월드가 `build:'ramp'`로 **실제로 세진다**(구매 횟수 → 중첩). 여기 buildKey는
    // ⑦의 시각 대조군(고정 빌드로 이펙트만 보기) 용도로만 남는다.
    this.buildKey = d?.build ?? 'none';
    this.worldBuild = d?.worldBuild ?? 'ramp';
    this.effectMode = d?.effect ?? 'soft';   // 'soft'(tanh 점근) | 'linear'(선형 — 대조군)
    this._swingAt = -9;
    // ── 적 렌더 경로 — **성능 대조군을 남긴다.** ───────────────────
    //   'sprite' = 12종 픽셀 스프라이트(단일 아틀라스 + 오브젝트 풀)  ← 기본
    //   'circle' = 구 회색박스의 원. **스프라이트 전/후 프레임 시간을 같은 기기에서
    //              한 번의 클릭으로 재기 위한 것**이지 폐기 대기 코드가 아니다.
    this.renderMode = d?.render ?? 'sprite';

    // ── 【7-2 / 0-E】 구출 모델 ─────────────────────────────────
    // **기본 꺼짐.** N22를 ①의 밀도 곡선 표본으로 쓸 때는 평범한 밤 모델로 돌려야
    // 다른 밤과 같은 자 위에 놓인다(world.js 41~44행). 켜면 필드 3배 + 카메라 추적.
    this.rescue = d?.rescue ?? false;
    // ── 【4-3 / 7장】 상점 ──────────────────────────────────────
    //   'real'  3택 1을 사람이 고른다 — **카드 중첩이 실제 뽑기에서 나온다**  ← 기본
    //   'model' 구 회색박스 — world.openShop 의 자동 구매 + stacksFromPicks 근사
    // 둘의 차이가 곧 「모델이 실제를 얼마나 맞혔는가」이므로 대조군으로 남긴다.
    this.cardMode = d?.cardMode ?? 'real';
    // ── 보기 설정 (판정과 무관) ────────────────────────────────
    // **둘 다 렌더 전용이다.** 월드는 이 값들을 한 번도 안 읽는다 — 헤드리스 측정에
    // 영향이 0이라는 뜻이고, 그래서 보기 좋을 대로 두어도 자(尺)가 안 흔들린다.
    //   viewZoom  = 카메라 배율. **1.0을 넘기면 경기장 가장자리가 잘린다** —
    //     G7 「한 화면 고정」·헌법 5조 화면 개체 밴드가 「다 보인다」를 전제하므로,
    //     **밴드를 눈으로 검수할 때는 1.0으로 돌려야 한다.**
    //   artScale  = 개체 그림 배율. 판정(히트박스·사거리·호)은 안 따라 커진다.
    this.viewZoom = d?.viewZoom ?? 1;
    this.artScale = d?.artScale ?? 1;
    // 몹만 한 겹 더 — 사장님 지시(지금의 1.5배). **판정은 안 따라 커진다**(ENEMY[].r).
    //   그림이 히트박스보다 커지므로, 「닿은 것처럼 보이는데 안 맞는」 구간이 생긴다.
    //   구 문제(몸이 히트박스보다 26~60% 좁다)의 **반대편**이다 — 어느 쪽이 나은지는
    //   사람이 쳐 봐야 갈린다. 히트박스를 같이 키우면 방금 수렴한 밸런스가 움직인다.
    this.enemyMul = d?.enemyMul ?? 1.5;
    this.enemyScale = this.artScale * this.enemyMul;
    // ⚠⚠ **개도 한 겹 필요하다** — 위 몹 배수가 개에는 안 걸려 있어서
    //   **잡몹(48×1.5)이 내 개(48×1.0)보다 컸다.** 「크기 = 정보」인 게임에서
    //   그 채널이 거꾸로 말하고 있었다. 근거·값의 유래는 `config.js` 의 `DOG.artMul`.
    this.dogMul = d?.dogMul ?? DOG.artMul;
    this.dogScale = this.artScale * this.dogMul;
    // **시드는 밤 시작 시 고정, 재시도 시 재생성**(명세 10장 668행). 재생성은 main.js 가
    // 한다 — 여기서 시각·난수로 만들면 「고정」이 거짓이 된다.
    this.cardSeed = d?.cardSeed ?? 20260728;
    this.forceFixed = d?.forceFixed ?? false;   // 고정 밤 보정(N28 기름) 강제 — 검증용
    this.soundOn = d?.sound ?? true;
    this.a11y = d?.a11y ?? false;               // 청각 이중화(옵트인 — 명세 10장 672행)
  }
  preload() {
    bakeAtlas(this);
    this.load.image('night-field', `/art/fields/${fieldMasterFor(this.nightKey)}`);
    // 【2026-07-31】 카드 아트 프리로드가 여기 있었다 — **카드 체계 삭제**(지팡이 삭제).
    // 적 시트 — 스폰하는 9종은 아틀라스로 굽고, 나머지 3종(독수리·곰·사자)은
    // **준비만** 한다(로드·검증은 하되 아틀라스에도 스폰 표에도 없다).
    for (const sp of ATLAS_SPECIES) {
      this.load.image(sheetKeyOf(sp), sheetUrlOf(sp));
      this.load.image(attackSheetKeyOf(sp), attackSheetUrlOf(sp));
    }
    for (const sp of PREPARED_ONLY) this.load.image(sheetKeyOf(sp), sheetUrlOf(sp));
    // 잃은 양 — **양이 밤에 등장하는 유일한 예외**(N22). 낮 디오라마의 양과 같은
    // 팔레트를 쓰되 각도가 다르다(낮 = 측면 / 밤 = 탑다운 60도).
    this.load.image('lost-sheep', '/art/sheep/v1/lost.png');
    // ── 부품 상점 아이콘 — **도착한 것만.** 표는 `gearart.js` 하나다 ──
    //   지금 18장(돌·받침·걸이). 끈 여섯은 그림이 없어 표에 없고, 화면은 그 자리에
    //   빈 칸(자리표시자)을 그대로 그린다.
    loadGearArt(this);
    this.load.image('gearshop-card-fabric', '/art/ui/gearshop/v1/gear-card-fabric-pixel-352x548-v1.png');
    this.load.image('gearshop-padlock', '/art/ui/gearshop/v1/gear-padlock-64-v2.png');
    this.load.image('watchman-shop-portrait',
      `${WATCHMAN}/watchman-sling-repair-portrait-320-v1.png`);
    this.load.spritesheet('watchman-walk',
      `${WATCHMAN}/sling-walk-8dir-v1/pixel-watchman-sling-walk-8dir-4frame-64-v1.png`,
      { frameWidth: 64, frameHeight: 64 });
    this.load.spritesheet('watchman-walk-hires',
      `${WATCHMAN}/sling-walk-8dir-v3/pixel-watchman-sling-walk-8dir-4frame-256-v3.png`,
      { frameWidth: 256, frameHeight: 256 });
    this.load.spritesheet('watchman-idle',
      `${WATCHMAN}/sling-walk-8dir-v3/pixel-watchman-sling-idle-8dir-2frame-256-v2.png`,
      { frameWidth: 256, frameHeight: 256 });
    // 【2026-07-31】 지팡이 시트 5종(upper·hands·lower·rig·front v2)의 프리로드가 여기 있었다
    //   — 지팡이 삭제. 자산 자체는 assets/ 에 보관한다(코드만 안 읽는다).
    // ── 보스 전투 시트 (6-2) ──────────────────────────────────
    // 규격은 assets/art/enemies/combat-v1.json 이 정본. 6열 × 2행(행 = 좌/우 방향).
    // 회색박스가 든 10밤 중 보스 밤은 N30(곰) 하나다 — 사자·N28 곰은 로드만 한다.
    this.load.spritesheet('boss-bear',
      '/art/enemies/bear/v3/combat-6x2.png', { frameWidth: 128, frameHeight: 128 });
    this.load.spritesheet('boss-bear-walk',
      '/art/enemies/bear/v3/walk-4x2-v1.png', { frameWidth: 128, frameHeight: 128 });
    this.load.spritesheet('boss-lion',
      '/art/enemies/lion/v3/combat-6x2.png', { frameWidth: 256, frameHeight: 256 });
    this.load.spritesheet('boss-lion-walk',
      '/art/enemies/lion/v3/walk-4x2-v1.png', { frameWidth: 256, frameHeight: 256 });
    this.load.spritesheet('watchman-hit',
      `${REACTION}/pixel-watchman-hit-4dir-3frame-64-v1.png`,
      { frameWidth: 64, frameHeight: 64 });
    this.load.spritesheet('watchman-down',
      `${REACTION}/pixel-watchman-down-4dir-3frame-96-v1.png`,
      { frameWidth: 96, frameHeight: 96 });
    for (const dog of NIGHT_DOG_ART) {
      this.load.spritesheet(dog.nightKey, `/${dog.nightFile}`,
        { frameWidth: 48, frameHeight: 48 });
    }
    this.load.spritesheet('water-puddle', '/art/objects/water-puddle/v1/sheet-3x1.png',
      { frameWidth: 96, frameHeight: 96 });
    this.load.spritesheet('piece-shard', '/art/objects/piece-shard/v1/sheet-2x1.png',
      { frameWidth: 16, frameHeight: 16 });
    this.load.image('sling-stone', '/art/objects/sling-stone/v1/stone.png');
    this.load.spritesheet('fire-zone', '/art/objects/fire-zone/v1/sheet-4x1.png',
      { frameWidth: 96, frameHeight: 96 });
    this.load.spritesheet('eagle-shadow', '/art/enemies/eagle/v1/shadow-3x1.png',
      { frameWidth: 96, frameHeight: 96 });
    this.load.spritesheet(EAGLE_COMBAT_KEY, EAGLE_COMBAT_URL,
      { frameWidth: EAGLE_CELL, frameHeight: EAGLE_CELL });
    this.load.spritesheet(EAGLE_FLIGHT_KEY, EAGLE_FLIGHT_URL,
      { frameWidth: EAGLE_CELL, frameHeight: EAGLE_CELL });
  }

  create() {
    // 카메라 배율 — 렌더 전용. 구출 밤은 추적이 따로 있으므로 중심만 여기서 잡는다.
    if (this.viewZoom !== 1) {
      this.cameras.main.setZoom(this.viewZoom);
      this.cameras.main.centerOn(1920 / 2, 1080 / 2);
    }
    const night = NIGHTS[this.nightKey];
    this.world = new World(night, {
      nightKey: this.nightKey, weapon: this.weapon, seed: 7, hp: this.hp,
      flockN: FLOCK_START[this.nightKey], baseLoss: BASE_LOSS[this.nightKey],
      build: this.worldBuild, rescue: this.rescue,
    });
    this.cameras.main.setBackgroundColor(FIELD_BG);   // A8 — 균일 밝기. 시야 제한 없음
    // ══════════════════════════════════════════════════════════
    //  ⚠⚠ **경계를 안 건다** 【벽 제거 2026-08-02】
    //    구 판은 `setBounds(0,0,W,H)` 로 카메라를 필드 안에 가뒀다. 들판에 끝이
    //    없어진 지금 그것을 두면 **다윗은 계속 걸어가는데 카메라가 경계에서 멈춰**
    //    화면 밖으로 사라진다. 벽이 없으므로 카메라를 가둘 자리도 없다.
    // ══════════════════════════════════════════════════════════
    // 바닥 — **끝없이 반복한다.** `tileSprite` 를 화면에 붙박고 타일 오프셋만
    //   카메라를 따라 밀면, 한 장짜리 그림으로 무한한 들판이 된다(`_tileFloor`).
    this.field = this.add.tileSprite(0, 0, FIELD.viewW, FIELD.viewH, 'night-field')
      .setOrigin(0, 0).setTileScale(2).setDepth(0).setScrollFactor(0);
    this.gGroundMarks = this.add.graphics().setDepth(6); // 피·착탄 자국·드랍 — 배우 아래
    this.gEnemy = this.add.graphics().setDepth(21);      // 예고 고리·돌진 꼬리·접촉 정보
    this.gUnit = this.add.graphics().setDepth(10);
    this.gFx = this.add.graphics().setDepth(30);
    // 「진짜 선」 실 — **돌 뒤(29)** 다. 돌보다 앞에 두면 실이 돌을 지우고, 그러면
    //   *"돌이 빽빽하면 고증"* 이 깨져 그냥 광선이 된다(LINEV.core 머리말).
    this.gLine = this.add.graphics().setDepth(29);
    this.gSheep = this.add.graphics().setDepth(22);   // 7-2 — 잃은 양(월드 좌표)
    // 다윗 머리 위 HP는 화면 HUD가 아닌 월드 좌표를 따른다. 카메라가
    // 스크롤·줌해도 못에 정확히 붙고, 피해 숫자는 위로 지나가게 깊이를 낮춘다.
    this.gPlayerHp = this.add.graphics().setDepth(55);
    // **화면에 붙는 층**(스크롤 안 함) — 방향 패널·가장자리 표시·HUD 바.
    // **비네트** — 화면 고정 레이어. 월드를 따라가면 안 된다(N22 카메라 추적 밤).
    //   HUD(95) 아래, 전투(30) 위 — 정보를 가리지 않으면서 화면 전체를 물들인다.
    this.gVig = this.add.graphics().setDepth(80).setScrollFactor(0);
    this.gPanel = this.add.graphics().setDepth(95).setScrollFactor(0);
    this.gBar = this.add.graphics().setDepth(90).setScrollFactor(0);
    this.combatVfx = new CombatVfx(this, Phaser, this.nightKey);
    if (this.combatVfx.storm) this.field.setTint(0xa8b7cd);

    // ── 적 스프라이트 ────────────────────────────────────────────
    // **단일 아틀라스 + 오브젝트 풀.** 마리당 add.sprite 는 하지 않는다 —
    // N30 동시 상한이 438이고 밤마다 수천 마리가 나고 죽는다. 풀은 한 번만 만들고
    // 죽은 자리는 visible=false 로 재사용한다(생성/파괴 0회).
    bakeEnemyAtlas(this, Phaser);
    for (const sp of PREPARED_ONLY)                    // 준비만 — 그려지지 않는다
      this.textures.get(sheetKeyOf(sp)).setFilter(Phaser.Textures.FilterMode.NEAREST);
    this.speciesRoster = speciesRosterForNight(this.nightKey);
    this.pickSpecies = makeSpeciesPicker(this.nightKey);
    // 층별 걷기 프레임율 — enemyart.walkFpsFor 규칙(지어낸 상수 0개)
    this.walkFps = {};
    for (const k of Object.keys(ENEMY)) {
      const anySp = Object.keys(SPECIES).find(s => SPECIES[s].tier === k) ?? 'field-rat';
      this.walkFps[k] = walkFpsFor(ENEMY[k].speed, SPECIES[anySp].span);
    }
    // 프레임 이름 8개(우 0~3 · 좌 4~7)를 미리 만들어 둔다 — 매 프레임 문자열 조립 금지
    this._frameNames = {};
    this._attackFrameNames = {};
    for (const sp of ATLAS_SPECIES) {
      this._frameNames[sp] = [
        frameName(sp, 'r', 0), frameName(sp, 'r', 1), frameName(sp, 'r', 2), frameName(sp, 'r', 3),
        frameName(sp, 'l', 0), frameName(sp, 'l', 1), frameName(sp, 'l', 2), frameName(sp, 'l', 3)];
      this._attackFrameNames[sp] = [
        attackFrameName(sp, 'r', 0), attackFrameName(sp, 'r', 1),
        attackFrameName(sp, 'r', 2), attackFrameName(sp, 'r', 3),
        attackFrameName(sp, 'l', 0), attackFrameName(sp, 'l', 1),
        attackFrameName(sp, 'l', 2), attackFrameName(sp, 'l', 3)];
    }
    // **씬 인스턴스는 재시작해도 같은 객체다**(Phaser 는 Scene 을 재사용한다).
    // 그래서 런 사이에 살아남는 필드를 여기서 되돌린다.
    //   ⚠ `_reported` 가 그중 하나였고, **구 구현의 버그**였다: 첫 밤이 끝나면 true 로
    //     남아 **두 번째 밤부터 리포트가 영영 안 나왔다**(패널이 "진행 중…"에서 멈춘다).
    //     재시도가 정식 진행 경로(4-1)라 두 번째 런이 오히려 기본이다.
    this._reported = false;
    this._barkN = 0; this._calm = 0; this._calmSaid = false; this._walkClock = 0;
    this._phase = 0; this._lambHush = false; this._frozen = false; this.escPaused = false;
    this.enemyPool = [];
    this._poolUsed = 0;
    this._artClock = 0;
    this._artRnd = 0x9e3779b9;      // 종 추첨용 결정적 난수(시드 고정 — 같은 밤은 같은 그림)
    this._enemyBehaviorScratch = {};
    this._enemyPoseScratch = {};
    this._bossGeometryScratch = { points: [] };
    this._bossAudioGeometryScratch = { points: [] };
    this._enemyBehaviorAudioSeen = new WeakMap();
    this._enemyBehaviorAudioScratch = {};
    this._behaviorAudioBuckets = Object.fromEntries(
      Object.keys(BEHAVIOR_AUDIO_BUDGET).map((id) => [id, { score: -Infinity, event: {} }]),
    );
    this._bossAudioStageKey = null;
    // 풀을 그 밤의 **동시 상한만큼 미리** 만든다. 밤 중간에 늘리면 그 프레임만 튄다.
    if (this.renderMode === 'sprite') this.growPool(this.world.concurrent - 1);

    // ── 성능 실측 (0-A 3차 측정 방법 승계) ───────────────────────
    // **FPS 는 안 쓴다.** vsync 천장 때문에 12배 부하에서도 세 경로가 같은 값을 냈다
    // (0-A 1·2차 실패). 유효값은 프레임 작업 시간(ms)뿐이다.
    this.work = new WorkTimer(this.game);
    // 「적이 몇 마리일 때의 ms인가」가 없으면 숫자가 아무 뜻이 없다 → 개체 수로 묶어 잰다.
    // postrender 시점에는 방금 그 프레임의 ms와 그것을 만든 개체 수를 **둘 다** 안다.
    // **두 채널을 따로 잰다.** 하나로는 무엇이 변했는지 못 읽는다:
    //   ① 제출(submit) = update + 렌더 제출까지의 순수 CPU 시간. **이번 작업이 바꾼 것이 여기다.**
    //   ② 총작업(work) = ① + gl.finish()+readPixels 로 GPU 완료까지 강제 동기화(0-A 3차 방법).
    //      ②는 창 상태·컴포지팅에 크게 흔들린다. 그래서 ①을 함께 남긴다.
    this._perf = { hiSum: 0, hiCnt: 0, allSum: 0, allCnt: 0,
                   cSum: 0, cHiSum: 0, cWorst: 0, peak: 0, worst: 0, warm: 0 };
    this._capHi = 0.9 * this.world.concurrent;
    // ⚠ **구 구현은 창을 잘못 잡고 있었다.** `_tPre` 를 'prerender' 에서 찍었는데
    //   Phaser 의 Game.step 은 **scene.update() 를 먼저 돌리고 그 다음에** PRE_RENDER 를
    //   쏜다(core/Game.js). 즉 측정 창에 update 가 통째로 빠져 있었고, 바로 위 주석이
    //   *"제출 = update + 렌더 제출"* 이라고 적은 것과 반대였다. 스프라이트↔원 대조가
    //   재던 것은 그리기 비용뿐이고 JS 절반은 세지 않았다.
    //   → **update 진입점에서 찍는다**(update() 첫 줄). 여기서는 GPU 질의만 연다.
    //   ※ 이 수정으로 「프레임 제출 시간」 수치는 **이전 차수와 비교 불가**해진다 —
    //     같은 이름으로 다른 것을 재고 있었으므로 그 비교는 원래 성립하지 않았다.
    this._onPre = () => { this.work.begin(); };
    this._onPost = () => {
      const cpuOnly = performance.now() - (this._tPre ?? performance.now());
      this.work.end();                                   // ← 여기서 GPU 완료까지 기다린다
      const P = this._perf;
      if (P.warm < 120) { P.warm++; return; }       // 셰이더 컴파일·첫 텍스처 업로드 제외
      // **멈춘 프레임은 표본이 아니다.** 상점이 열려 있는 동안(전투 정지) 프레임은
      // 계속 도는데 시뮬레이션은 0이다. 그것을 섞으면 사람이 카드를 오래 볼수록
      // 평균이 좋아진다 — 게다가 적 수는 멈추기 직전 값에 얼어 있어 「상한 근접」
      // 버킷까지 오염된다. 측정 도구가 플레이어의 망설임을 재게 둘 수는 없다.
      if (this._frozen) return;
      const ms = this.work.cpu[this.work.cpu.length - 1];
      if (ms === undefined) return;
      const n = this.world.enemies.length;
      P.allSum += ms; P.cSum += cpuOnly; P.allCnt++;
      if (n > P.peak) P.peak = n;
      if (ms > P.worst) P.worst = ms;
      if (cpuOnly > P.cWorst) P.cWorst = cpuOnly;
      if (n >= this._capHi) { P.hiSum += ms; P.cHiSum += cpuOnly; P.hiCnt++; }
    };
    this.game.events.on('prerender', this._onPre);
    this.game.events.on('postrender', this._onPost);
    this.events.once('shutdown', () => {
      this.game.events.off('prerender', this._onPre);
      this.game.events.off('postrender', this._onPost);
    });

    // ── 개체 배율 (보기 설정) ──────────────────────────────────
    // **판정이 아니라 그림만 키운다.** 히트박스·사거리·호 반경·안정성 반경은
    // 전부 그대로다 — 그것들을 같이 키우면 화면이 판정에 대해 거짓말을 하게 된다.
    //   부수 효과 하나: 물량층 몸이 히트박스보다 26~60% 좁게 그려지던 것(아트 검수
    //   대기 항목)이 1.3배에서 **줄어든다.** 그림이 판정에 가까워지는 방향이다.
    const ART = this.artScale;
    this.playerWalk = this.add.sprite(0, 0, 'watchman-walk-hires')
      .setOrigin(0.5, 0.9375).setDepth(25).setScale(ART * 0.25);
    const idleTexture = this.textures.exists('watchman-idle')
      ? this.textures.get('watchman-idle')
      : null;
    const idleFrames = idleTexture ? (idleTexture.frameTotal - 1) / 8 : 0;
    this._idleFrameCount = Number.isInteger(idleFrames) && idleFrames > 0
      && Object.values(SLING_IDLE_HAND_SOCKET).every((row) => row.length === idleFrames)
      ? idleFrames
      : 0;
    this.playerIdle = this._idleFrameCount
      ? this.add.sprite(0, 0, 'watchman-idle')
          .setOrigin(0.5, 0.9375).setDepth(25).setScale(ART * 0.25).setVisible(false)
      : null;
    // 【2026-07-31】 지팡이 스프라이트 6종(upper·rig·hands·lower·front v2 둘)이 여기 있었다 — 삭제.
    this.playerHit = this.add.sprite(0, 0, 'watchman-hit')
      .setOrigin(0.5, 0.9375).setDepth(25).setScale(ART).setVisible(false);
    this.playerDown = this.add.sprite(0, 0, 'watchman-down')
      .setOrigin(0.5, 82 / 96).setDepth(25).setScale(ART).setVisible(false);
    for (const key of ['watchman-walk',
                       'watchman-hit', 'watchman-down', ...NIGHT_DOG_ART.map((dog) => dog.nightKey),
                       'water-puddle', 'piece-shard', 'sling-stone', 'lost-sheep',
                       'fire-zone', 'eagle-shadow', EAGLE_COMBAT_KEY, EAGLE_FLIGHT_KEY]) {
      this.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST);
    }
    // v3는 제한 팔레트·이진 알파로 다시 구운 픽셀 시트다. 확대·축소 중에도 가장자리가
    // 녹지 않도록 월드의 다른 스프라이트와 같은 NEAREST 필터를 쓴다.
    this.textures.get('watchman-walk-hires').setFilter(Phaser.Textures.FilterMode.NEAREST);
    if (this._idleFrameCount) {
      this.textures.get('watchman-idle').setFilter(Phaser.Textures.FilterMode.NEAREST);
    }
    this.dogSprites = this.world.dogs.map((dog) =>
      this.add.sprite(dog.x, dog.y, nightDogTexture(dog.kind))
        .setOrigin(0.5, 0.5).setDepth(22).setScale(this.dogScale));
    // ⚠ **우물 수는 이제 고정이 아니다** 【무한 타일 2026-08-07】. 걸어 나가면 칸이
    //   새로 깔려 목록이 늘어난다 — 구 판은 여기서 만든 개수에 못 박혀 있어서
    //   **늘어난 우물이 통째로 안 보였다.** 풀은 `drawWater` 가 모자랄 때마다 늘린다.
    this.waterSprites = [];
    this.piecePool = [];
    this.shotPool = [];
    // ── 성긴 고른 돌 「선」 — 날아가는 돌 뒤로 깔리는 **돌들** ────────
    // ⚠ 트레일(빛)이 아니라 **스프라이트**다. A-1 이 *"빛나면 판타지고 돌이 빽빽하면
    //   고증이다"* 라고 못 박았으므로 자취를 밝히는 대신 같은 돌을 더 놓는다.
    this.lineStonePool = [];
    // ── 묵은 차돌 — **바닥에 남은 돌** ──────────────────────────────
    // 안 그리면 「어디로 주우러 가야 하나」가 화면에서 사라진다(A-3 순환이 끊긴다).
    this.orbitStonePool = [];
    this.orbitTrails = [];
    this._orbitTrailShots = 0;
    this._orbitTrailPasses = [
      { width: 6.2, alpha: 0.12, color: 0xffffff },
      { width: 2.2, alpha: 0.50, color: 0xffffff },
      { width: 0.85, alpha: 0.72, color: 0xfff4dc, start: 0.62 },
    ];
    this.firePool = [];
    this.eagleShadowPool = [];
    this.eagleBodyPool = [];
    this.eaglePreviewSprite = this.add.sprite(0, 0, EAGLE_FLIGHT_KEY, EAGLE_PREVIEW.frame)
      .setOrigin(0.5).setDepth(20).setScrollFactor(0).setVisible(false);
    this.corpsePool = [];
    this.dmgPool = [];
    // Phaser reuses the Scene instance after stop/start, but destroys its
    // GameObjects. Never retain destroyed pooled or lazily-created objects.
    this._comboTxt = null;
    this.sheepSprite = null;
    this.bossSprite = null;
    this._bossImpactSeq = -1;
    this._finaleDarkOverlay = null;
    this._finaleDarkStamp = '';
    this._finaleSheep = [];
    this._finaleSheepPose = [];
    this._finaleArrowPresence = 0;
    this._finaleReunionLight = 0;
    this._finaleVisualActive = false;
    this._downClock = 0;

    this.hud = this.add.text(14, 132, '', {
      fontFamily: 'ui-monospace, Consolas, monospace', fontSize: '16px',
      color: '#d6c4a3', backgroundColor: 'rgba(13,11,10,.78)', padding: { x: 11, y: 9 },
    }).setDepth(100);

    // 기본 HUD는 전투를 가리지 않는 상용 화면용 컴팩트 패널이다.
    // `full`만 회색박스 계측 텍스트를 추가한다.
    // 서체【폴리싱 2026-08-08】: 한글은 자체 포함 Noto Sans KR로 통일한다(여정은 nightboot,
    // 실험실은 main.js 가 싣는다). 계기판(hud·타이머·콤보)는 모노를 지킨다 —
    // 타이머·콤보는 숫자 폭이 흔들리면 안 되고, 상점은 작은 글자가 뭉개진다.
    this.stabTxt = this.add.text(20, 18, '', {
      fontFamily: '"Noto Sans KR Variable", "Noto Sans KR", "Malgun Gothic", system-ui, sans-serif', fontSize: '22px',
      fontStyle: 'bold', color: '#f1e6cf',
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 5, fill: true },
    }).setDepth(100);
    this.lossTxt = this.add.text(20, 92, '', {
      fontFamily: '"Noto Sans KR Variable", "Noto Sans KR", "Malgun Gothic", system-ui, sans-serif', fontSize: '16px',
      fontStyle: 'bold', color: '#e5bd68',
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 4, fill: true },
    }).setDepth(100);
    this.hudSheep = this.add.image(20, 92, 'lost-sheep')
      .setOrigin(0, 0.5).setScale(0.58).setDepth(100).setScrollFactor(0);
    // **HUD는 카메라를 따라 흐르지 않는다.** 구출 밤에서 카메라가 움직이면 화면 좌표에
    // 놓인 것들이 전부 필드 밖으로 끌려간다 — 상시 HUD 4개(명세 10장 661행)가 그 대상이다.
    for (const o of [this.hud, this.stabTxt, this.lossTxt]) o.setScrollFactor(0);

    // **G11: 밤 타이머는 달이 아니다.** 필드에 하늘이 없으므로 "달이 지평선에 닿으면"은
    // 성립하지 않는다. 뱀서 문법대로 **화면 타이머**로 남은 시간을 그대로 보여준다.
    this.timeTxt = this.add.text(W / 2, 18, '', {
      fontFamily: 'ui-monospace, Consolas, monospace', fontSize: '34px',
      fontStyle: 'bold', color: '#f1e6cf',
      shadow: { offsetX: 0, offsetY: 3, color: '#000000', blur: 6, fill: true },
    }).setOrigin(0.5, 0).setDepth(100).setLetterSpacing(2);
    // 카드 이름에 이미 「밤 8 — 」이 있다 — 구 문구는 「8번째 밤 · 밤 8 — …」로 겹말이었다.
    this.qText = this.add.text(W / 2, 66, night.name, {
      fontFamily: '"Noto Sans KR Variable", "Noto Sans KR", "Malgun Gothic", system-ui, sans-serif', fontSize: '21px',
      fontStyle: 'bold', color: '#e5bd68', align: 'center',
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 5, fill: true },
    }).setOrigin(0.5, 0).setDepth(100).setLetterSpacing(1);
    this.bossHudTitle = this.add.text(W / 2, BOSS_HUD.y + 8, '', {
      fontFamily: '"Noto Sans KR Variable", "Noto Sans KR", "Malgun Gothic", system-ui, sans-serif',
      fontSize: '20px', fontStyle: 'bold', color: '#f0dbc0', align: 'center',
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 3, fill: true },
    }).setOrigin(0.5, 0).setDepth(101).setScrollFactor(0).setVisible(false);
    this.bossHudMeta = this.add.text(W / 2, BOSS_HUD.y + 76, '', {
      fontFamily: '"Noto Sans KR Variable", "Noto Sans KR", "Malgun Gothic", system-ui, sans-serif', fontSize: '16px',
      fontStyle: 'bold', color: '#f0dbc0', align: 'center',
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 3, fill: true },
    }).setOrigin(0.5, 0).setDepth(101).setScrollFactor(0).setVisible(false);
    // 여정용 night.html 은 같은 밤 이름을 DOM 존 타이틀로 이미 연출한다. 그 입구에서
    // Canvas 제목까지 겹치면 두 목소리가 동시에 소리친다. 실험실(gb.html)에는 DOM 제목이
    // 없으므로 기존 Canvas 제목을 그대로 남긴다.
    this._externalNightTitle = typeof document !== 'undefined'
      && !!document.getElementById('ntitle');
    for (const o of [this.timeTxt, this.qText]) o.setScrollFactor(0);

    // 이동은 키보드/왼쪽 스틱, 물맷돌 방향은 포인터/오른쪽 스틱이다.
    // 조준 입력이 없을 때만 기존 자동 조준으로 돌아간다.
    const k = this.input.keyboard;
    this.keys = k.addKeys({ up: 'W', down: 'S', left: 'A', right: 'D',
      up2: 'UP', down2: 'DOWN', left2: 'LEFT', right2: 'RIGHT',
      dualSense: 'O' });
    this.gamepad = new StandardGamepadInput();
    this.dualSenseHaptics = new DualSenseUsbHaptics({
      onStatus: (detail) => this.syncDualSenseHapticsControl(detail),
    });
    this.playerDamageHaptics = new PlayerDamageHaptics({ externalHaptics: this.dualSenseHaptics });
    this.installDualSenseHapticsControl();
    this._padFamily = 'xbox';
    this._padLabels = gamepadLabels('xbox');
    this._mouseAimInside = !!this.game.canvas?.matches?.(':hover');
    this._mouseAimWorld = new Phaser.Math.Vector2();
    this._combatAim = { aimX: 0, aimY: 0, aimActive: false, source: 'auto' };
    this._mouseAimOver = () => { this._mouseAimInside = true; };
    this._mouseAimOut = () => { this._mouseAimInside = false; };
    this._mouseAimMove = (pointer) => {
      // 터치가 activePointer를 바꿔도 마우스 조준 상태를 키지 않는다.
      if (pointer === this.input.mousePointer) this._mouseAimInside = true;
    };
    this.input.on('gameover', this._mouseAimOver);
    this.input.on('gameout', this._mouseAimOut);
    this.input.on('pointermove', this._mouseAimMove);

    // ══════════════════════════════════════════════════════════════
    //  【4-3 / 명세 7장】 레벨업 상점 — **게임의 유일한 능동 선택 지점**
    //  world.openShop() 은 「살 수 있는 만큼 자동으로 산다」는 **봇 정책**이고,
    //  그것은 헤드리스 측정(최악 지출 시나리오)에서 계속 필요하다. 그래서
    //  **잠긴 world.js 를 안 고치고 여기서 가로챈다.** 장부는 그대로 갱신된다.
    // ══════════════════════════════════════════════════════════════
    // **대조군에서는 결 화면을 안 연다.** 아래 주석이 적어 둔 그대로 —
    // *"대조군은 손대지 않는 것이 대조군이다."* 결 3택은 사람이 고르는 화면이고,
    // 무강화·최대빌드·자동구매는 사람이 아무것도 안 고르는 자로 남아야 한다.
    // 그쪽에서는 world 가 시드로 뽑아 둔 갈래를 그대로 쓴다(autoBranch).
    const humanCards = this.cardMode === 'real' && this.world.build === 'ramp';
    // 부품 상점 — 무기는 물맷돌 하나다(지팡이 삭제 2026-07-31. 구 카드 상점 분기 제거).
    this.shop = new GearShop(this.world, {
      weapon: this.weapon, nightKey: this.nightKey, seed: this.cardSeed,
      jackpot: this.world.jackpot,               // 귀한 돌 — 낮이 보냄
    });
    // **고정 빌드(none/mid/max)에서는 상점을 아예 안 연다.**
    //   구 구현은 openShop 만 가로채고 refreshWeapon 은 ramp 에서만 갈아끼웠다.
    //   그러면 「무강화 = 구 회색박스와 같은 자」 대조군에서 ⓐ world 의 자동 구매
    //   봇 정책이 사라져 지출·지갑이 구 기준과 달라지고, ⓑ 「숨 고르기」가 밤당
    //   최대 HP의 200%까지 회복시켜 **대조군이 대조군이 아니게 된다.**
    //   대조군은 손대지 않는 것이 대조군이다.
    this.shopActive = humanCards;
    if (this.shopActive) {
      this.world.openShop = () => this.shop.request();
      // **중첩을 모델이 아니라 실제 뽑기에서 읽는다**(10-B 6-2 의 다음 칸).
      // 구 구현은 여기서 `refreshWeapon` 을 통째로 갈아끼웠는데, 그러면 **mods 계산이
      // 통째로 빠져** 사람이 치는 경로에서만 카드 11종이 안 먹었다. 지금은
      // shop.syncWorld() 가 실제 중첩을 world 에 넘기고 world 가 그것을 정본으로 읽는다.
      this.world.refreshWeapon();
    }
    this.shopUI = new GearUI(this, this.shop);   // 장비창 — 왼쪽 낀 것 / 오른쪽 살 것
    // ── 사운드 (디자인방향 10장) ─────────────────────────────────
    // **배속에서는 끈다.** 배속은 측정 도구지 연출이 아니다(이 파일 219행과 같은 규율) —
    // ×12에서 예고음이 12배로 쏟아지면 판정을 재는 게 아니라 소음을 재게 된다.
    this.audio = new AudioCues({
      on: this.soundOn && this.speed === 1,
      a11y: this.a11y,
      // Phaser가 이미 만든 컨텍스트와 출력 체인을 함께 쓴다. 페이지당 별도
      // AudioContext를 하나 더 만들지 않아 자동재생 잠금과 수명주기가 갈리지 않는다.
      context: this.sound?.context,
      destination: this.sound?.destination,
    });
    this._cueDone = {};
    this._combatSoundPrev = { attacks: 0, contacts: 0, kills: 0, pickups: 0, hurt: 0, bossHits: 0 };
    this._worldCueN = 0;
    this._playerHapticHp = Math.max(0, Number(this.world.player.hp) || 0);
    this._lightningSoundOn = false;
    this._dangerBeatAt = -9;

    // ── 【명세 10장】 일시정지 창구는 **둘**이다 ────────────────────
    //   *"레벨업 상점이 열린 동안(전투 정지) + ESC"*
    // 구 배선은 ESC 를 상점 「닫기」에 물려 놨었다 — 명세가 「멈춤」이라고 지정한 키가
    // 「풀림」에 걸려 있던 셈이다. 상점 닫기는 Space 하나로 두고 ESC 는 제 일을 한다.
    this.escPaused = false;
    this._escHandler = () => {
      if (this.shop.open || this.world.finale) return; // 상점/피날레의 전용 화면을 덮지 않는다
      this.escPaused = !this.escPaused;
      this.syncAudioPause();
    };
    k.on('keydown-ESC', this._escHandler);
    this._audioVisibilityHandler = () => this.syncAudioPause();
    document.addEventListener('visibilitychange', this._audioVisibilityHandler);
    this._scenePauseAudio = () => this.audio.setPaused('pause');
    this._sceneResumeAudio = () => this.syncAudioPause();
    this.events.on(Phaser.Scenes.Events.PAUSE, this._scenePauseAudio);
    this.events.on(Phaser.Scenes.Events.RESUME, this._sceneResumeAudio);
    this.buildPausePanel();
    this.installAudioControls();
    // 현재 장착 4칸은 전투 상단을 점유하지 않는다. ESC와 Menu/Options가 공유하는
    // 이 일시정지 화면 안에서만 최신 world.gear를 읽는다.
    this.pauseGearHud = new PauseGearHud(this);
    this.events.once('shutdown', () => {
      k.off('keydown-ESC', this._escHandler);
      this.input.off('gameover', this._mouseAimOver);
      this.input.off('gameout', this._mouseAimOut);
      this.input.off('pointermove', this._mouseAimMove);
      document.removeEventListener('visibilitychange', this._audioVisibilityHandler);
      this.events.off(Phaser.Scenes.Events.PAUSE, this._scenePauseAudio);
      this.events.off(Phaser.Scenes.Events.RESUME, this._sceneResumeAudio);
      if (this._audioControl?.root) this._audioControl.root.hidden = true;
      this.removeAudioControls();
      this.audio.dispose();
      this.playerDamageHaptics.stop();
      this.dualSenseHaptics?.destroy();
      this._dualSenseButton?.removeEventListener('click', this._dualSenseConnect);
      this.shopUI.destroy();
      this.combatVfx = null;
    });
  }

  // 번호가 붙은 서사 큐를 빌리지 않고, 전투 합성 SFX와 같은 버스에서 짧은 작업대
  // 재질음만 낸다. 세계 규칙은 이 표현층 호출의 존재를 모른다.
  onShopAction(action) { this.audio.shop(action); }

  syncAudioPause() {
    const mode = document.hidden ? 'hidden'
      : this.escPaused ? 'pause'
        : this.shop?.open ? 'shop' : 'none';
    this.audio?.setPaused(mode);
  }

  currentGamepadLabels() { return this._padLabels ?? gamepadLabels('xbox'); }

  installDualSenseHapticsControl() {
    const root = typeof document === 'undefined' ? null
      : document.getElementById('dualsense-rumble');
    const button = root ? document.getElementById('dualsense-rumble-connect') : null;
    if (!root || !button || !this.dualSenseHaptics?.supported) return;
    this._dualSenseRoot = root;
    this._dualSenseButton = button;
    this._dualSenseConnect = async () => {
      button.disabled = true;
      const connected = this.dualSenseHaptics.connected
        || await this.dualSenseHaptics.request();
      if (connected) await this.dualSenseHaptics.test();
      button.disabled = false;
    };
    button.addEventListener('click', this._dualSenseConnect);
    // 전투 화면을 가리지 않도록 기본은 접어 둔다. 키보드 O가 이 UI의
    // 노출 상태만 바꾸고 연결·진동 상태에는 손대지 않는다.
    root.hidden = true;
    this.syncDualSenseHapticsControl({ status: this.dualSenseHaptics.status });
    this.dualSenseHaptics.restore();
  }

  toggleDualSenseHapticsControl() {
    const root = this._dualSenseRoot;
    if (!root) return false;
    root.hidden = !root.hidden;
    return !root.hidden;
  }

  syncDualSenseHapticsControl({ status = 'idle', error = null } = {}) {
    const root = this._dualSenseRoot;
    const button = this._dualSenseButton;
    const helper = typeof document === 'undefined' ? null
      : document.getElementById('dualsense-rumble-status');
    if (!root || !button) return;
    root.dataset.state = status;
    const copy = {
      idle: ['듀얼센스 진동 연결', 'USB · Chrome 최초 1회 허용'],
      requesting: ['장치 선택 중…', 'Chrome 창에서 DualSense를 선택하세요'],
      testing: ['진동 보내는 중…', 'USB 출력 보고서 확인 중'],
      connected: ['듀얼센스 진동 테스트', '연결됨 · 눌러서 다시 확인'],
      error: ['듀얼센스 진동 다시 연결', 'USB 연결을 확인하고 다시 시도하세요'],
    }[status] ?? ['듀얼센스 진동 연결', 'USB · Chrome 최초 1회 허용'];
    button.textContent = copy[0];
    button.disabled = status === 'requesting' || status === 'testing';
    if (helper) helper.textContent = copy[1];
    if (error) root.title = String(error?.message ?? error);
    else root.removeAttribute('title');
  }

  syncGamepadFamily(pad) {
    if (!pad.connected || pad.family === this._padFamily) return;
    this._padFamily = pad.family;
    this._padLabels = gamepadLabels(pad.family);
    const L = this._padLabels;
    this.pauseContinue?.setText(`[ ESC / ${L.pause} / ${L.confirm} ]   계속하기`);
  }

  syncHapticsConnectionControl(pad) {
    const root = this._dualSenseRoot;
    if (!root) return;
    const raw = Array.from(globalThis.navigator?.getGamepads?.() ?? [])
      .find((item) => Number(item?.index) === Number(pad?.index));
    const standardOutput = !!(raw?.vibrationActuator ?? raw?.hapticActuators?.[0]);
    const dualSenseNeedsPermission = pad?.connected
      && /dualsense/i.test(String(pad.id ?? ''))
      && !standardOutput
      && !this.dualSenseHaptics.connected;
    root.hidden = !(dualSenseNeedsPermission
      || this.dualSenseHaptics.connected
      || this.dualSenseHaptics.status === 'error');
  }

  installAudioControls() {
    const root = document.getElementById('audio-control');
    const mute = document.getElementById('audio-mute');
    const master = document.getElementById('audio-master');
    const value = document.getElementById('audio-master-value');
    if (!root || !mute || !master) return;
    this._audioControl = { root, mute, master, value };
    // 실플레이 위에 상시 노출하지 않고 ESC 패널이 열린 동안만 보인다.
    root.hidden = true;
    this._syncAudioControl = () => {
      const s = this.audio.settings();
      root.dataset.muted = String(s.muted);
      mute.setAttribute('aria-pressed', String(s.muted));
      mute.textContent = s.muted ? '소리 꺼짐' : '소리 켜짐';
      master.value = String(Math.round(s.master * 100));
      master.setAttribute('aria-valuetext', `${Math.round(s.master * 100)}퍼센트`);
      if (value) value.textContent = `${Math.round(s.master * 100)}%`;
      this.refreshPauseSettings();
    };
    this._audioMuteClick = () => {
      this.audio.toggleMuted();
      this._syncAudioControl();
    };
    this._audioMasterInput = () => {
      this.audio.setVolume('master', Number(master.value) / 100);
      this._syncAudioControl();
    };
    this._audioMuteKey = (event) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key !== 'm' && event.key !== 'M') return;
      if (event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      event.preventDefault();
      this.audio.toggleMuted();
      this._syncAudioControl();
    };
    mute.addEventListener('click', this._audioMuteClick);
    master.addEventListener('input', this._audioMasterInput);
    window.addEventListener('keydown', this._audioMuteKey);
    this._syncAudioControl();
  }

  removeAudioControls() {
    const c = this._audioControl;
    if (!c) return;
    c.root.hidden = true;
    c.mute.removeEventListener('click', this._audioMuteClick);
    c.master.removeEventListener('input', this._audioMasterInput);
    window.removeEventListener('keydown', this._audioMuteKey);
    this._audioControl = null;
  }

  // 표준 배치: Xbox A/B/X/Y/Menu · PlayStation ×/○/□/△/Options.
  handleGamepad(pad) {
    if (!pad.connected) return;
    this.syncGamepadFamily(pad);
    const P = pad.pressed;
    if (this.shop.open) {
      // 규칙 패널은 상점 위의 모달이다. 열린 동안 같은 프레임의 구매·리롤 입력이
      // 뒤로 새지 않으며, Menu/Options와 취소 버튼은 패널만 먼저 닫는다.
      if (this.shopUI.rulesOpen) {
        if (P.pause || P.cancel) this.shopUI.toggleRules(false);
        return;
      }
      if (P.pause) { this.shopUI.toggleRules(true); return; }
      if (!this._shopWasOpen || this.shopUI.hover < 0) this.shopUI.select(0);
      if (P.left || P.up) this.shopUI.select(-1);
      if (P.right || P.down) this.shopUI.select(1);
      if (P.confirm) this.shopUI.buySelected();
      if (P.lock) this.shopUI.lockSelected();
      if (P.reroll) this.shopUI.doReroll();
      if (P.cancel) this.shopUI.close();
      return;
    }
    // 피날레는 자체 입력과 진행 상태를 갖는다. 숨겨진 escPaused만 켜져 수색/재회를
    // 멈추는 일이 없도록 Menu/Options 일시정지는 여기서 소비하지 않는다.
    if (this.world.finale) return;
    if (this.escPaused) {
      if (P.confirm || P.cancel || P.pause) this.escPaused = false;
      return;
    }
    if (P.pause) this.escPaused = true;
  }

  // ── ESC 일시정지 ─────────────────────────────────────────────
  // 규칙층에는 손대지 않는다. 이 패널은 이미 있던 escPaused 를 읽어 전투 위에 별도의
  // 어두운 막과 정보 계층을 얹을 뿐이다. 존재하지 않는 재시작·즉시 이탈 버튼은 만들지
  // 않고, 실제 여정에 있는 종료 동선(밤 종료 → 결과창 → 아침으로)만 적는다.
  buildPausePanel() {
    const depth = 260;
    const pw = 1000, ph = 780, px = (W - pw) / 2, py = (H - ph) / 2;
    const g = this.pausePanel = this.add.graphics()
      .setDepth(depth).setScrollFactor(0).setVisible(false);
    g.fillStyle(0x03050a, 0.86); g.fillRect(0, 0, W, H);
    // 가장자리 한 겹을 더 어둡게 해 전장의 움직임보다 패널이 먼저 읽히게 한다.
    g.fillStyle(0x000000, 0.20); g.fillRect(0, 0, W, 92);
    g.fillRect(0, H - 92, W, 92);
    g.fillStyle(0x0d1018, 0.98); g.fillRoundedRect(px, py, pw, ph, 10);
    g.lineStyle(3, 0x8b7048, 0.96); g.strokeRoundedRect(px, py, pw, ph, 10);
    g.lineStyle(1, 0xe0c58b, 0.24); g.strokeRoundedRect(px + 9, py + 9, pw - 18, ph - 18, 6);
    g.fillStyle(0xc69b54, 0.9); g.fillRect(px + 34, py + 34, 4, 104);
    g.lineStyle(1, 0x8b7048, 0.55);
    g.lineBetween(px + 54, py + 165, px + pw - 54, py + 165);
    g.lineBetween(px + 54, py + 438, px + pw - 54, py + 438);
    g.lineBetween(px + pw / 2, py + 468, px + pw / 2, py + 614);

    const sans = '"Noto Sans KR Variable", "Noto Sans KR", "Malgun Gothic", system-ui, sans-serif';
    const mono = 'ui-monospace, Consolas, monospace';
    const text = (x, y, value, style, ox = 0) => this.add.text(x, y, value, style)
      .setOrigin(ox, 0).setDepth(depth + 2).setScrollFactor(0).setVisible(false);
    this.pauseKicker = text(px + 58, py + 42, '밤의 숨이 멎었다', {
      fontFamily: sans, fontSize: '17px', color: '#c7ad7b', letterSpacing: 2,
    });
    this.pauseTitle = text(px + 58, py + 70, '일시정지', {
      fontFamily: sans, fontSize: '50px', fontStyle: 'bold', color: '#f1e6cf',
      shadow: { offsetX: 0, offsetY: 3, color: '#000000', blur: 6, fill: true },
    });
    this.pauseGearTitle = text(W / 2, py + 190, '현재 장착', {
      fontFamily: sans, fontSize: '20px', fontStyle: 'bold', color: '#c7ad7b',
      align: 'center', letterSpacing: 2,
    }, 0.5);
    this.pauseControls = text(px + 58, py + 468,
      '조작\n\nWASD · 방향키     이동\n왼쪽 스틱 · D-pad 이동\n포인터 · 오른쪽 스틱 조준\n스틱 우선 · 입력 없으면 자동', {
        fontFamily: mono, fontSize: '18px', color: '#f1e6cf', lineSpacing: 6,
      });
    const sound = this.audio.on ? '켜짐' : this.speed > 1 ? '배속 중 꺼짐' : '꺼짐';
    const sensory = this.audio.a11y ? '켜짐' : '꺼짐';
    this.pauseSettings = text(px + pw / 2 + 38, py + 468,
      `이번 밤의 설정\n\n오디오             ${sound}\n청각 신호 보조     ${sensory}`, {
        fontFamily: mono, fontSize: '19px', color: '#f1e6cf', lineSpacing: 9,
      });
    this.pauseRoute = text(W / 2, py + 635,
      '밤이 끝나면 결과 화면의  ‘아침으로’  돌아갑니다', {
        fontFamily: sans, fontSize: '16px', color: '#a99a84', align: 'center',
      }, 0.5);

    const by = py + 678, bw = 330, bh = 68;
    g.fillStyle(0x33271b, 1); g.fillRoundedRect(W / 2 - bw / 2, by, bw, bh, 5);
    g.lineStyle(2, 0xd0ab67, 0.9); g.strokeRoundedRect(W / 2 - bw / 2, by, bw, bh, 5);
    g.fillStyle(0xf1e6cf, 0.07); g.fillRect(W / 2 - bw / 2 + 4, by + 4, bw - 8, 2);
    this.pauseContinue = text(W / 2, by + 20, '[ ESC / MENU / A ]   계속하기', {
      fontFamily: mono, fontSize: '21px', fontStyle: 'bold', color: '#f1e6cf',
      align: 'center',
    }, 0.5);
    this._pauseObjects = [g, this.pauseKicker, this.pauseTitle, this.pauseGearTitle, this.pauseControls,
      this.pauseSettings, this.pauseRoute, this.pauseContinue];
  }

  refreshPauseSettings() {
    if (!this.pauseSettings || !this.audio) return;
    const s = this.audio.settings();
    const sound = !s.enabled ? (this.speed > 1 ? '배속 중 꺼짐' : '꺼짐')
      : s.muted ? '음소거 · M' : `켜짐 · ${Math.round(s.master * 100)}%`;
    const sensory = this.audio.a11y ? '켜짐' : '꺼짐';
    this.pauseSettings.setText(
      `이번 밤의 설정\n\n오디오             ${sound}\n청각 신호 보조     ${sensory}`,
    );
  }

  syncPausePanel() {
    this.refreshPauseSettings();
    const visible = pauseGearHudVisible({
      escPaused: this.escPaused,
      shopOpen: this.shop.open,
      finale: !!this.world.finale,
    });
    for (const o of this._pauseObjects ?? []) o.setVisible(visible);
    if (this._audioControl?.root) this._audioControl.root.hidden = !visible;
    // 구매·강화 뒤 처음 Pause를 여는 바로 그 프레임의 실제 장비를 읽는다. 닫거나
    // 상점/피날레가 앞에 오면 배경·아이콘·글자·등급점을 모두 함께 숨긴다.
    this.pauseGearHud?.sync(this.world.gear, visible);
  }

  update(_t, dtMs) {
    this._tPre = performance.now();              // ← 프레임 CPU 창의 진짜 시작점(위 주석)
    const w = this.world;
    this._dtReal = Math.min(dtMs, 50) / 1000;    // 8-6a 회전은 **실시간**으로 돈다(배속 무관)
    const dt = this._dtReal * this.speed;

    const K = this.keys;
    const pad = this.gamepad.read();
    this._activeGamepad = pad;
    this.syncHapticsConnectionControl(pad);
    if (Phaser.Input.Keyboard.JustDown(K.dualSense)) {
      this.toggleDualSenseHapticsControl();
    }
    const keyX = (K.right.isDown || K.right2.isDown ? 1 : 0)
      - (K.left.isDown || K.left2.isDown ? 1 : 0);
    const keyY = (K.down.isDown || K.down2.isDown ? 1 : 0)
      - (K.up.isDown || K.up2.isDown ? 1 : 0);
    const keyboardMoving = keyX !== 0 || keyY !== 0;
    const axisX = keyboardMoving ? keyX : pad.moveX;
    const axisY = keyboardMoving ? keyY : pad.moveY;
    const rawPlayerMoving = Math.hypot(axisX, axisY) > 0.04;
    // The world owns the reunion lock. Raw held keys must not leak a walking
    // pose into the moment after David has reached the flock.
    this._playerMoving = !w.finale?.playerLocked && rawPlayerMoving;

    // ── 명세 7장 「조각 게이지가 차면 … 열리는 동안 전투는 멈춘다」 ──────
    // 멈추는 방법은 하나뿐이다: **world.step 을 안 부른다.** 시간(w.t)도 스폰도
    // 안정성 적분도 전부 step 안에 있으므로 이 한 줄이 곧 일시정지다
    // (명세 10장 「일시정지 창구 = 상점이 열린 동안 + ESC」).
    // 프레임 **시작**에서 먼저 연다 — 직전 프레임에 닫힌 세션의 다음 요청이 남아 있으면
    // 그 사이에 전투 한 프레임(×12에서 최대 0.6초)을 끼우지 않는다.
    // gainPieces 의 while 루프가 한 프레임에 레벨업을 여럿 낼 수 있어서 생기는 자리다.
    pumpCombatGearShop(this.shop, w);
    this.handleGamepad(pad);
    const frozen = this._frozen = this.shop.open || this.escPaused;
    // 클릭 여부나 이동량을 보지 않는다. 캔버스 안에 있는 현재 마우스 좌표를
    // 매 프레임 카메라 월드 좌표로 바꾸므로, 포인터가 멈춰 있어도 조준은
    // 유지된다. 오른쪽 스틱이 잡히면 resolveCombatAim이 항상 그쪽을 먼저 고른다.
    const aim = resolveCombatAim({
      gamepad: pad,
      pointer: this.input.mousePointer,
      camera: this.cameras.main,
      player: w.player,
      mouseInside: this._mouseAimInside,
      mouseAllowed: !frozen && !w.finale && !w.over,
      pointerWorld: this._mouseAimWorld,
    }, this._combatAim);
    this.syncAudioPause();
    // **애니메이션 시계도 함께 멈춘다.** 규칙층만 멈추고 걷기 프레임이 계속 돌면
    // 화면에서는 적이 제자리걸음을 한다 — 「전투가 멈췄다」가 그림에서 거짓말이 된다.
    if (!frozen) {
    if (this._playerMoving) this._walkClock = (this._walkClock ?? 0) + this._dtReal;
    // 적의 걷기는 **실시간**으로 돈다(배속과 무관) — 배속은 측정 도구지 연출이 아니다.
    this._artClock += this._dtReal;
    this._downClock = w.over && w.result === '다운'
      ? this._downClock + this._dtReal
      : 0;
    }

    if (!frozen) {
      w.step(dt, {
        axisX, axisY,
        aimX: aim.aimX, aimY: aim.aimY, aimActive: aim.aimActive,
        up: axisY < 0, down: axisY > 0, left: axisX < 0, right: axisX > 0,
      });
      // Player HP feedback is sampled immediately after simulation. It must not
      // depend on VFX, combat-SFX counters, or cues() returning early on death.
      this.syncPlayerHpFeedback();
      // 레벨업이 쌓아 둔 요청을 **그 프레임에** 연다 — *"그 자리에서 열린다"*.
      // 한 프레임에 레벨업이 둘 이상 뜰 수 있어서 큐다(gainPieces 의 while 루프).
      // N40은 이 step 안에서 전투→피날레로 바뀔 수 있다. 그 프레임에 남은
      // 레벨업 요청이 상점을 열면 다음 프레임부터 World.step이 멎어 재회 시간이
      // 영구 정지한다. 전투 중일 때만 열고, 피날레/종료 요청은 버린다.
      pumpCombatGearShop(this.shop, w);
      this.cues(dt);
    }

    this.draw();
    this.drawDamageText();
    this.drawHud();
    if (this.shop.open && !this._shopWasOpen) this.onShopAction('open');
    this._shopWasOpen = this.shop.open;
    this.shopUI.draw();
    this.syncPausePanel();
    if (w.over && !this._reported) {
      this._reported = true;
      // ══════════════════════════════════════════════════════════
      //  ⚠⚠ **리포트가 여정을 막으면 안 된다** 【2026-07-31】
      //
      //  실제로 막았다. 카드 체계를 지우며 `world.stack` 게터가 사라졌는데 아래 표가
      //  그것을 계속 읽어, 밤이 끝나는 순간 TypeError 가 났다. 그 예외가 `__gbReport`
      //  호출 자체를 삼켜 **성적이 낮으로 안 넘어갔고, 여정이 밤 화면에서 멈췄다.**
      //
      //  이 표는 **진단용**이다 — `nightboot` 이 스스로 적어 둔 대로 *"리포트는 안 쓴다.
      //  값은 살아 있는 World 에서 뽑는다"*. 그런데 그 진단이 게임의 유일한 진행 경로를
      //  붙들고 있었다. **끊는다** — 표를 만들다 터져도 밤은 반드시 낮으로 넘어간다.
      //  터진 사실은 삼키지 않고 표의 첫 줄에 적어 화면에 남긴다.
      // ══════════════════════════════════════════════════════════
      let payload;
      try {
        payload = this.endReport();
      } catch (e) {
        payload = { '⚠ 보고 생성 실패': `${e?.message ?? e} — 성적은 그대로 넘어간다` };
        console.error('[밤 끝 보고] 표를 못 만들었다:', e);
      }
      window.__gbReport?.(this.nightKey, payload);
    }
  }

  // 밤 끝 진단표. **여기서 터져도 여정은 안 멈춘다**(위 try/catch).
  endReport() {
      const w = this.world;
      const P = this._perf, S = this.work.stats();
      return ({
        ...w.report(),
        // **한 표에 서로 모순되는 두 행을 남기지 않는다.**
        //   world.report() 의 '빌드' 가 찍는 것과 여기 '무기 실효' 가 어긋나지 않게 덮어쓴다.
        //   ⚠⚠ **여기가 밤을 낮으로 못 돌려보내던 자리다** 【2026-07-31】.
        //     지팡이·카드 삭제 때 `world.stack`(카드 중첩 근사 모델) 게터를 지웠는데
        //     이 줄이 `w.stack.toFixed(2)` 로 그것을 계속 읽었다. 밤이 끝나는 순간
        //     **TypeError 가 나면서 `__gbReport` 가 통째로 안 불렸고**, 성적이 낮으로
        //     안 넘어가 여정이 밤 화면에서 멈췄다. 폐기된 모델이므로 **그 항을 지운다** —
        //     되살리지 않는다(비교 대상이 이제 존재하지 않는다).
        ...(this.shopActive ? {
          // ⚠ 「sRate 0 · sAlt 0」을 영구히 찍던 자리 — 구 카드 API 스텁이라 늘 0 이었다
          //   (거짓 계측 · 폐기 2026-08-07, gearshop.js 주석).
          '빌드': `부품 실측 — 구매 ${w.buysThisNight}회·집기 ${w.picksThisNight}회`
            + ` · 축 ${(w.axis * 100).toFixed(0)}%`,
        } : {}),
        ...(this.shopActive ? this.shop.report() : {}),
        ...(this.rescue ? { '구출 모델': '켜짐 — 필드 3배 + 카메라 추적(8-4 유일 예외)' } : {}),
        '렌더 경로': this.renderMode === 'sprite' ? '스프라이트(아틀라스+풀)' : '원(구 회색박스)',
        '프레임 제출 시간(CPU)': `평균 ${(P.allCnt ? P.cSum / P.allCnt : 0).toFixed(2)}ms · 최악 ${P.cWorst.toFixed(2)}ms`,
        '프레임 총작업 시간': `평균 ${(P.allCnt ? P.allSum / P.allCnt : 0).toFixed(2)}ms`
          + ` · p95 ${S.cpuP95?.toFixed(2) ?? '-'}ms · 최악 ${P.worst.toFixed(2)}ms`
          + ` (예산 ${BUDGET_60.toFixed(1)}ms · ${this.work.mode})`,
        '상한 근접 구간': P.hiCnt
          ? `적 ≥${this._capHi | 0} 인 ${P.hiCnt}프레임: 제출 ${(P.cHiSum / P.hiCnt).toFixed(2)}ms`
            + ` · 총작업 ${(P.hiSum / P.hiCnt).toFixed(2)}ms (최대 동시 ${P.peak})`
          : `미도달 (최대 동시 ${P.peak})`,
      });
  }

  // ══════════════════════════════════════════════════════════════
  //  사운드 큐 발화 (디자인방향 10장) — **트리거는 여기, 소리는 audio.js.**
  //  큐 번호는 그 문서의 것이고 재번호하지 않는다.
  // ══════════════════════════════════════════════════════════════
  combatSounds() {
    const w = this.world, m = w.m, A = this.audio;
    const now = {
      attacks: (m.shots ?? 0) + (m.swings ?? 0),
      contacts: (m.shotHits ?? 0) + (m.swingHits ?? 0) + (m.orbitHits ?? 0),
      kills: (m.killsPlayer ?? 0) + (m.killsDog ?? 0),
      pickups: m.pickedNight ?? 0,
      hurt: m.hits ?? 0,
      bossHits: m.hitsBoss ?? 0,
    };
    const prev = this._combatSoundPrev;

    let recent = null;
    for (let i = (w.pops?.length ?? 0) - 1; i >= 0; i--) {
      const p = w.pops[i];
      if (w.t - p.t <= 0.12) { recent = p; break; }
    }
    const pan = recent ? Math.max(-1, Math.min(1, recent.x / (W / 2) - 1)) : 0;
    const tier = w.mods?.tier ?? 1;

    if (now.attacks > prev.attacks) A.weaponShot(tier, 0, isDiamondSetPresentation(w.mods));
    if (now.contacts > prev.contacts) {
      A.combatHit(now.contacts - prev.contacts, pan);
      if (tier >= 6) A.diamondImpact(isDiamondSetPresentation(w.mods), pan);
    }
    if (now.kills > prev.kills) {
      A.combatKill(now.kills - prev.kills, !!recent?.big, pan);
      A.comboRise(recent?.n ?? 1, pan);
    }
    if (now.pickups > prev.pickups) A.pickup(now.pickups - prev.pickups, 0);
    if (now.hurt > prev.hurt) A.playerHit(0, now.bossHits > prev.bossHits ? 1.30 : 1);

    this._combatSoundPrev = now;
  }

  behaviorSounds() {
    const w = this.world, p = w.player, buckets = this._behaviorAudioBuckets;
    for (const id of Object.keys(BEHAVIOR_AUDIO_BUDGET)) buckets[id].score = -Infinity;

    const groundEnemies = w.enemies;
    const flyingThreats = w.eagles ?? [];
    const audibleCount = groundEnemies.length + flyingThreats.length;
    for (let i = 0; i < audibleCount; i++) {
      const e = i < groundEnemies.length
        ? groundEnemies[i] : flyingThreats[i - groundEnemies.length];
      let memory = this._enemyBehaviorAudioSeen.get(e);
      if (!memory) {
        memory = { key: null };
        this._enemyBehaviorAudioSeen.set(e, memory);
      }
      const event = resolveBehaviorAudioEvent(e, memory, this._enemyBehaviorAudioScratch);
      if (!event.active || !BEHAVIOR_AUDIO_BUDGET[event.bucket]) continue;
      const dx = event.x - p.x, dy = event.y - p.y;
      // 카메라 밖 먼 행동을 가짜 근접 경보로 만들지 않는다. 화면 테두리 바로 밖의
      // 실제 돌입은 들리도록 배우 반지름보다 넉넉한 240px 여백만 둔다.
      if (Math.abs(dx) > W * 0.5 + 240 || Math.abs(dy) > H * 0.5 + 240) continue;
      const distance = Math.hypot(dx, dy);
      const score = event.importance - distance / 180;
      const bucket = buckets[event.bucket];
      if (score <= bucket.score) continue;
      bucket.score = score;
      Object.assign(bucket.event, event, { dx, distance });
    }

    for (const id of ['danger', 'action', 'recover']) {
      const bucket = buckets[id];
      if (bucket.score === -Infinity) continue;
      const event = bucket.event;
      const pan = clamp01(event.dx / W + 0.5) * 2 - 1;
      const near = 1 - clamp01(event.distance / 1500);
      this.audio.behaviorSignal(event.family, event.stage, pan,
        event.weight * (0.72 + near * 0.28));
    }
  }

  bossBehaviorSounds() {
    const b = this.world.boss;
    if (!b || b.dead || this.world.finale) return;
    const geometry = resolveBossAttackGeometry(b, this._bossAudioGeometryScratch);
    if (!geometry.visible) return;
    const key = `${geometry.seq}:${geometry.stage}`;
    if (this._bossAudioStageKey === key) return;
    this._bossAudioStageKey = key;
    // signal은 같은 전이에서 world.cues의 boss-lock/bearDig가 먼저 발화한다.
    if (geometry.stage !== 'commit' && geometry.stage !== 'recovery') return;
    const dx = geometry.originX - this.world.player.x;
    const pan = clamp01(dx / W + 0.5) * 2 - 1;
    this.audio.bossAction(geometry.stage, geometry.shape, pan,
      0.88 + Math.max(1, Number(b.phase) || 1) * 0.12);
  }

  syncPlayerHpFeedback() {
    const w = this.world;
    const hp = Math.max(0, Number(w.player.hp) || 0);
    const damage = playerHpDrop(this._playerHapticHp, hp);
    // 적에게 준 피해는 진동시키지 않는다. 다윗의 HP가 실제로 줄어든 프레임만
    // Xbox 표준 진동과 DualSense USB 진동의 공통 피격 신호가 된다.
    if (damage > 0) {
      // PlayStation/DualSense는 기존 활성 패드 경로를 그대로 쓴다. Xbox만 별도
      // 출력기로 보내 두 종류가 함께 연결돼도 HP 피격 신호를 놓치지 않는다.
      this.playerDamageHaptics.hurt(damage);
    }
    this._playerHapticHp = hp;
    // The existing breath cue is restored as the HP channel itself: silent at
    // 50%+, rising continuously below half HP, and full at zero HP.
    this.audio.loop('breath', lowHealthBreathMix(hp, w.maxHp));
  }

  cues(dt) {
    const w = this.world, p = w.player, A = this.audio, D = this._cueDone;

    // ⑯ 사자 활성 = 1·12·2가 실제로 사라진다. flock은 CUES 계약대로 남고,
    // 사자가 쓰러지거나 피날레로 넘어가면 즉시 원래 믹스로 돌아온다.
    A.setSilence(!!(w.boss?.kind === 'lion' && !w.boss.dead && !w.finale));

    // BossFSM이 남긴 큐를 순서대로 한 번씩 소비한다. 종전에는 배열만 쌓이고 Scene이
    // 읽지 않아 곰의 큐 14가 정의돼 있어도 단 한 번도 울리지 않았다.
    while (this._worldCueN < (w.cues?.length ?? 0)) {
      const event = w.cues[this._worldCueN++];
      const payload = event?.c;
      const id = typeof payload === 'string' ? payload : payload?.c;
      if (id === 'bearDig') A.play('bearDig');
      else if (id === 'boss-lock') A.bossSignal('lock', payload?.phase ?? 1);
      else if (id === 'boss-phase') A.bossSignal('phase', payload?.phase ?? 1);
      else if (id === 'boss-hp-tick') A.bossSignal('tick', 0.75 + (payload?.tick ?? 1) / 24);
    }
    this.bossBehaviorSounds();

    // ── 밤이 끝났다 ─────────────────────────────────────────────
    // ⑮ 첫 새소리(생존 확정)를 한 번 울리고 **지속 큐를 전부 끈다.**
    // ⚠ 이 분기가 없으면 밤이 끝난 뒤에도 아래 A.loop 들이 매 프레임 되살아난다 —
    //   world.step 은 over 면 즉시 반환하지만 update 루프는 계속 돌기 때문이다.
    //   실제로 「무리 소리」가 리포트 위로 계속 깔렸다.
    if (w.over) {
      A.setSilence(false);
      if (!D.dawn && w.result !== '다운') { D.dawn = true; A.play('dawn'); }
      A.stopAll();
      return;
    }

    // Every night owns a quiet environmental bed; the visual storm profile owns thunder.
    A.ambience(this.nightKey, 1);
    if (this.combatVfx?.storm) {
      const lightning = this.combatVfx?.lightningAt(this._artClock ?? 0) ?? 0;
      if (lightning > 0.72 && !this._lightningSoundOn) {
        A.thunder(0.85 + lightning * 0.35);
        this._lightningSoundOn = true;
      } else if (lightning < 0.02) {
        this._lightningSoundOn = false;
      }
    }
    const danger = Math.max(0, Math.min(1, (62 - w.stability) / 34));
    const beatGap = 1.05 - danger * 0.40;
    if (danger > 0.28 && w.t - this._dangerBeatAt >= beatGap) {
      A.dangerPulse(danger);
      this._dangerBeatAt = w.t;
    }
    this.combatSounds();
    this.behaviorSounds();

    // ① 하울링 — *"본대 도착 직전"*. **시각은 잠긴 코드에서 파생한다:**
    //   world.spawnRateAt 이 본대 스파이크를 p=0.30(= 216초)에 두었고,
    //   명세 6장이 *"하울링 후 30초"*라고 적었다 → 216 − 30 = **186초**.
    //   N12·N23은 cfg.howl=false — **그 밤의 성격이 곧 이 침묵이다.**
    if (w.cfg.howl && !D.howl && w.t >= HOWL_AT) { D.howl = true; A.play('howl'); }

    // ② 개 짖음 — 연출. 표식이 새로 생긴 프레임에만.
    if (w.barks.length > (this._barkN ?? 0)) A.play('bark');
    this._barkN = w.barks.length;

    // ⑥ 무리 소리 — **규모만.** 방향은 싣지 않는다(디자인방향 390행).
    //   무리 20(첫 밤) → 0.5 / 40(후반) → 1.0. **임의값 — 미검증**(체감 곡선 미측정).
    A.loop('flock', Math.min(1, 0.5 + (w.flockN - 20) / 40));

    // ⑨ 새끼 울음 — **마지막 밤** 방향 화살표의 **청각 이중화**. 화살표 이후에만.
    //   (구 N22 전용이던 것이 40일 개편에서 마지막 밤으로 이관 — 발화 조건은 w.rescue 라
    //    배선이 자동으로 따라왔고, 이 주석의 이름만 낡아 있었다.)
    //   *"가까울수록 커지고 발견 직전 2초 그친다."* 「2초」를 거리로 옮기는 자는
    //   **소년의 속도**다 — 남은 거리 ÷ 이동 속도 < 2초면 마지막 몇 걸음이다.
    if (w.rescue && w.panelOn && w.sheep && !w.foundAt) {
      const d = Math.hypot(w.sheep.x - p.x, w.sheep.y - p.y);
      this._lambHush = d / PLAYER.speed < 2;
      A.loop('lamb', this._lambHush ? 0 : Math.max(0.12, 1 - d / (Math.hypot(1920, 1080) * 1.2)));
    } else A.loop('lamb', 0);

    // ⑫ 수금 — 평온 확인. *"문턱의 정본은 시스템명세"* 인데 그 문턱이 명세에 없다.
    //   → **정산표의 손실 0 칸(86%+)** 을 쓴다. 「잃을 양이 없는 상태」가 이 게임에서
    //     평온의 유일한 정의다. 반경이 5초 내내 비어 있어야 한다(임의값 — 미검증).
    this._calm = (w.inRad === 0 && w.stability >= LOSS_TABLE[0].min) ? (this._calm ?? 0) + dt : 0;
    if (this._calm > 5 && !this._calmSaid) { this._calmSaid = true; A.play('lyre'); }
    if (this._calm === 0) this._calmSaid = false;

    // 일반 적의 tell/commit/recover는 behaviorSounds가 계약 seq/state를 읽어 한 번씩
    // 집계한다. 구 `e.tell` 플래그와 enemy 객체의 `_telled` 렌더 돌연변이는 쓰지 않는다.

  }

  draw() {
    const w = this.world, p = w.player;
    const finaleActive = !!w.finale;
    if (finaleActive && !this._finaleVisualActive) this.enterFinaleVisualState();
    else if (!finaleActive) this._finaleVisualActive = false;
    // 카메라 추적 — 구출 밤만. 다른 밤은 경계가 화면과 같아 스크롤이 0이다.
    // ── 화면 흔들림 ────────────────────────────────────────────
    // **조작을 뺏기는 순간(넉백)과 내가 크게 때린 순간(충격파)에만** 흔든다.
    // 아무 때나 흔들면 그때부터 흔들림은 정보가 아니라 소음이다(기둥 2).
    // Contact shake belongs to combat. A lion hit can leave a few frames on
    // world.shake at the N40 handoff, but the search camera must start still.
    const sh = finaleActive ? 0 : (w.shake ?? 0);
    let ox = 0, oy = 0;
    if (sh > 0) {
      const q = cameraOffset(
        w.shakeKind ?? 'normal',
        Math.max(0, w.t - (w._shakeAt ?? w.t)),
        sh,
        w.shakeDx ?? 0,
        w.shakeDy ?? 0,
      );
      ox = q.x * SHAKE.px;
      oy = q.y * SHAKE.px;
    }
    // ⚠⚠ **카메라는 언제나 다윗을 따라간다** 【벽 제거 2026-08-02】
    //   구 판은 구출 밤(N22)만 따라가고 나머지는 고정이었다 — 필드 = 화면이라 그래도
    //   됐기 때문이다. 벽이 없어진 지금 고정으로 두면 **다윗이 화면 밖으로 걸어 나간다.**
    //   N22 가 쓰던 그 줄을 전 밤으로 넓힌다(스폰을 통일한 것과 같은 이유·같은 자리).
    this.cameras.main.centerOn(p.x + ox, p.y + oy);
    // 바닥은 화면에 붙박여 있고 **무늬만 세계를 따라 흐른다** — 그래서 끝이 없다.
    //   `setTileScale(2)` 이므로 카메라 이동량을 2로 나눠야 지면이 안 미끄러진다.
    if (this.field) {
      const cam = this.cameras.main;
      this.field.tilePositionX = cam.scrollX / 2;
      this.field.tilePositionY = cam.scrollY / 2;
    }

    // Resolve render species before the grounding pass so every enemy gets a
    // frame-accurate foot anchor on the very first visible frame.
    if (this.renderMode === 'sprite') {
      for (const e of w.enemies) if (e._sp === undefined) this.initEnemyArt(e);
    }
    if (finaleActive) this.clearFinaleCombatVfx();
    else this.combatVfx?.draw(w, p, this.artScale, this.tierColor());

    const ground = this.gGroundMarks; ground.clear();
    this.drawBlood(ground);     // 지면 자국 — 배우와 그림자 아래
    this.drawFireGlow(ground);  // 화염의 지면 반사광
    this.drawDecals(ground);    // 지나온 착탄 자리
    this.drawSkipMarks(ground); // 물수제비 자국 — **앞으로 줄줄이** + 튐 그림자

    const g = this.gEnemy; g.clear();
    if (this.showHearth ?? false) this.drawHearth(g, p);   // VFX 조명 위에 얹는 작은 불티
    this.drawFires();           // 지면 효과 — 적보다 **아래**에 깔린다
    this.drawDrops(g);          // 희귀 필드 드랍은 배우 위에서 상호작용을 알린다
    this._equipPulse = this.drawEquipFlash(g) || 1;   // 장착 순간 — 화면이 그 색으로 번쩍
    if (this.gVig) { this.gVig.clear(); this.drawVignette(this.gVig); }
    this.drawCombo(g, p);       // 연쇄 처치 — 몇을 한꺼번에 지웠나
    this.drawImpacts(g);        // 착탄 광역 · 회전 링 (구 「오버킬 파문」·올무는 지웠다)
    this.drawCorpses();
    this.drawEnemies(g, p);
    if (w.boss) this.drawBoss(g, w.boss, p);
    else this.bossSprite?.setVisible(false);
    this.drawEagles();
    if (w.finale) this.hideFinaleWeaponVisuals();
    else this.drawShotSprites(w.shots);

    // 개 — 무리가 아니라 나를 따라다닌다
    const u = this.gUnit; u.clear();
    this.drawDogs();
    // 개 짖음 — **연출.** 정예를 처음 본 자리에 표식만 남는다. 규칙은 아무것도 이걸 안 읽는다
    for (const b of w.barks) {
      const a = 1 - (w.t - b.t) / 4;
      u.lineStyle(2, 0x8fd0ff, 0.5 * a);
      u.strokeCircle(b.x, b.y, UNIT * (0.8 + (1 - a) * 2));
    }

    // ── 【10-B 1】 「물웅덩이」 — 회복 3체제 ① ───────────────
    // 필드에 **처음으로 갈 곳이 생긴다.** 마른 샘은 어둡고, 찬 샘은 숨을 쉰다.
    // 마른 샘도 계속 보인다 — **언제 다시 차는지가 정보**이기 때문이다.
    this.drawWater();

    // 필드의 조각 — 치즈이자 레벨 게이지(A12). 밤이 끝나면 남은 것은 전량 회수(A19)
    this.drawPieces();
    // 바닥에 남은 **차돌** — 조각과 나란히 놓인다. 둘 다 「주우러 가는 것」이지만
    // 조각은 수입이고 차돌은 **무기**다(world 가 루프를 따로 두는 이유와 같다).

    // 플레이어 + 무기 사거리/반경 + **안정성 판정 반경**
    const f = this.gFx; f.clear();
    // ⚠ **개 이펙트는 여기서 부른다 — `gFx.clear()` 뒤여야 한다.** 위 `drawDogs()`
    //   자리에서 부르면 무는 호를 이 `clear()` 가 그 프레임에 통째로 지운다.
    //   `gUnit`(u)은 프레임당 한 번만 지워지므로 지금 그려도 남는다.
    this.drawDogFx(u, f);
    // ── 반경 표시 (스위치) ─────────────────────────────────────
    // **배선은 남기고 스위치만 끈다**(사장님 지시). 기본 「끔」.
    //   플레이어 중심의 원들이 화면을 통째로 덮고 있었다 — 특히 발밑 광원 3겹이
    //   필드와 적을 가렸다(목업은 적 4~5마리 화면이었고 실제는 수백이다).
    //   ⚠ 안정성 반경은 **연출이 아니라 규칙이 보이는 자리**다 — 끄면 「왜 깎이는지」가
    //     화면에서 사라진다. 그래서 지우지 않고 **되켤 수 있게** 둔다.
    const RAD = this.showRadii ?? false;
    if (RAD) {
      // 안정성 판정 반경 — 이 안에 적이 하나라도 있으면 계속 깎이고, 비면 회복한다(B1·B2)
      f.lineStyle(2, w.inRad > 0 ? 0xff6b6b : 0x7ee081, 0.30);
      f.strokeCircle(p.x, p.y, w.stabRadius);
    }
    const vis = this._vis = this.effect();   // 프레임당 한 번만 — effect()가 회전 위상을 전진시킨다
    if (w.weapon === 'pasu' && !w.finale) {
      if (RAD) {
        f.lineStyle(1, 0x445070, 0.35);
        f.strokeCircle(p.x, p.y, vis.range);       // 사거리(정보) — 조준·실제 비행과 같은 최종값
      }
      // ⚠ **레벨 게이지 링은 껐다**(사장님 지시 2026-07-29).
      //   **배선은 남기고 스위치만 끈다** — 되켤 때 다시 안 짜도 된다.
      //   되켜려면 아래 한 줄의 주석을 풀면 된다.
      // this.drawLevelRing(f, p);
      this.drawAimLine(f, p);      // 【v2 D5】 밀집도 채널 — 조준선
      // ── 8-6a 도는 물맷돌 ──────────────────────────────────
      // 궤도 반경 ← 사거리 / 회전 속도 ← 연사 / 도는 돌 개수 ← 투사체 수.
      // **이 셋이 스탯 창을 대신한다.** 궤도 원 자체도 옅게 그려 반경 변화가 읽히게 한다.
      // ── 무릿매 ───────────────────────────────────────────
      // **원을 안 그린다.** 구 구현은 사거리·연사·투사체 수를 큰 궤도 원의
      // 반경·속도·점 개수로 그렸는데, 화면에서는 캐릭터 위에 도형이 하나 떠 있는
      // 것으로만 읽혔다. 강화는 이제 **날아가는 돌**에서 읽는다(config.SLING 주석).
      this.drawSling(f, p);
    }
    // 【2026-07-31】 지팡이 호·잔상 겹·되치기·충격파 렌더가 여기 있었다 — 무기 삭제.
    // 흡수 반경은 「그러모으기」로 늘어난다 — 실제 판정과 1:1로 그린다(월드가 쓰는 값 그대로)
    if (RAD) {
      f.lineStyle(1, 0xffd166, 0.25);
      f.strokeCircle(p.x, p.y, PLAYER.pickup * (w.mods?.pickupMul ?? 1));
    }
    this.drawPlayer(p, vis);
    this.drawPlayerHp(p);
    this.drawRescue(p);
  }

  hideFinaleWeaponVisuals() {
    this.hidePoolTail(this.shotPool, 0);
    this.hidePoolTail(this.lineStonePool, 0);
    this.hidePoolTail(this.orbitStonePool, 0);
    this.gLine.clear();
    for (const trail of this.orbitTrails) trail?.reset();
    this._orbitCount = 0;
  }

  enterFinaleVisualState() {
    this._finaleVisualActive = true;

    // The combat camera can be inside a Phaser shake/flash or a follow ease at
    // the exact frame the lion fight hands off to the search. Reset those
    // presentation-only effects without changing normal combat behaviour.
    const cam = this.cameras.main;
    cam.stopFollow?.();
    cam.resetFX?.();
    cam.setFollowOffset?.(0, 0);
    cam.setLerp?.(1, 1);
    cam.setRotation?.(0);
    cam.centerOn(this.world.player.x, this.world.player.y);

    this._bossImpactSeq = -1;
    this._equipPulse = 1;
    this._swingAt = -9;
    this.bossSprite?.setVisible(false);
    this._comboTxt?.setVisible(false);
    this.dmgPool?.forEach((o) => o.setVisible(false));
    this.gFx?.clear();
    this.gLine?.clear();
    this.clearFinaleCombatVfx();
  }

  clearFinaleCombatVfx() {
    const vfx = this.combatVfx;
    if (!vfx) return;
    // Keep the night's stable colour grade so the handoff does not brighten for
    // one frame; remove only dynamic combat/weather response layers.
    vfx.grade?.clear();
    vfx.drawFieldGrade?.();
    for (const layer of [vfx.ground, vfx.glow, vfx.impact,
      vfx.weather, vfx.screenGlow, vfx.screenGrade]) layer?.clear();
    vfx.keyLight?.setVisible(false);
  }

  pooledSprite(pool, index, key, depth, originY = 0.5) {
    while (pool.length <= index) {
      pool.push(this.add.sprite(0, 0, key)
        .setOrigin(0.5, originY).setDepth(depth).setVisible(false));
    }
    return pool[index];
  }

  hidePoolTail(pool, used) {
    for (let i = used; i < pool.length; i++) pool[i].setVisible(false);
  }

  // ── 강화는 **날아가는 돌**에서 읽는다 (사장님 지시 2026-07-28) ────
  // 궤도 원이 들고 있던 채널을 여기로 옮긴다. 지표가 아니라 실물이라 더 정직하다:
  //   투사체 수 → 부채꼴로 나가는 **발의 수** (세는 채널 — v2 D4 유지)
  //   연사      → 나가는 **빈도**
  //   사거리    → **날아간 거리** (구 「궤도 반경」이라는 대역이 아니라 그 자체)
  //   발당 피해 → **돌 크기** (v1 5-1 「돌의 크기 ← 발당 피해」)
  //   관통      → **자취 길이** (v1 5-1 「돌 뒤 잔상 길이 ← 관통 수」)
  // 크기는 tanh 로 눌러 둔다 — 뎀이 몇 배가 돼도 화면에서 바위가 되지는 않는다.
  shotVisScale(shot) {
    if (shot.shard) return 0.55;
    const k = (shot.dmg ?? PASU.dmg) / PASU.dmg;          // 무강화 = 1
    return 1 + soft(0.30 * (k - 1), 0.85);                 // 상한 ≈ 1.85배
  }
  drawShotSprites(shots) {
    const M = this.world.mods || {};
    const col = this.tierColor();
    for (let i = 0; i < shots.length; i++) {
      const shot = shots[i];
      // 「깨지는 돌」의 파편은 **같은 돌의 조각**이다 — 같은 그림을 작게 쓴다.
      // 다른 그림을 주면 새 무기로 읽힌다(디자인방향 4장 「한 물건은 한 모양」).
      // **등급 색이 날아가는 돌까지 통과한다** — 도는 돌 · 날아가는 돌 · 파편 ·
      //   착탄 광역 · 회전 링이 **같은 색**이라야 「무기가 통째로 세졌다」로 읽힌다.
      // ── 거친 납작한 돌 — **통통 떠오른다**(A-2) ────────────────────
      // 미끄러지는 구간에서 돌이 물 위로 떠오르고 자국 자리에서 바닥에 닿는다.
      // 그림자는 `drawSkipMarks` 가 지면 레이어에 깐다(그림자가 배우 위에 있으면 안 된다).
      // ⚠ 회전도 같이 빨라진다 — 납작한 돌은 **눕혀 던지는** 돌이다.
      const hop = M.skips > 0 && !shot.shard ? this.skipHop(shot) : 0;
      // ── 고른 돌 「진짜 선」 — **머리도 눕는다** 【사장님 지시 2026-08-02】 ────
      //   꼬리는 누웠는데 머리만 빙글빙글 돌면 **거기서 선이 끊긴다.** 머리가 선의
      //   끝이므로 같이 눕혀야 한 줄로 읽힌다. 다른 다섯 돌은 그대로 구른다 —
      //   구르는 것이 그 돌들의 성격이고, 눕는 것은 고른 돌 하나의 성격이다.
      const lineL = (M.pierceInf && !shot.shard) ? this.lineness(M) : 0;
      const sc = this.shotVisScale(shot) * this.artScale * (shot.shard ? 1 : (M.stoneSize ?? 1));
      const spr = this.pooledSprite(this.shotPool, i, 'sling-stone', 31)
        .setPosition(shot.x, shot.y - hop)
        .setTint(col)
        .setVisible(true);
      if (lineL > 0.02) {
        spr.setScale(sc * (1 + LINEV.core.stretch * lineL), sc)
           .setRotation(Math.atan2(shot.vy, shot.vx));
      } else {
        spr.setScale(sc)
           .setRotation(this._artClock * (shot.shard ? 15 : (hop > 0 ? 22 : 10)) + i * 2.399);
      }
    }
    this.hidePoolTail(this.shotPool, shots.length);
    this.drawLineStones(shots, col, M);
  }

  // ── 성긴 고른 돌 「선」 — **돌이 촘촘한 선**  (A-1) ────────────────
  // ⚠⚠ **트레일을 밝히지 않는다.** *"빛나면 판타지고 돌이 빽빽하면 고증이다"* 이므로
  //   같은 `sling-stone` 을 날아온 방향으로 더 놓기만 한다. 간격은 `mods.lineGap` —
  //   등급이 그 값을 좁히고(●1 0.89 → ●5 0.39), 성긴 세트가 뜨면 0 이 되어 **닿는다.**
  // ⚠ 이 그림은 **고른 돌에만** 붙는다(`pierceInf`). 다른 다섯 돌은 루프를 안 돈다 —
  //   `hidePoolTail(…, 0)` 한 번이 전부다.
  // 「선」이 얼마나 이어졌는가 — 0 = 돌 낱개 · 1 = 완전히 이어진다.
  //   **새 축을 안 만든다.** 이 저장소가 이미 쓰는 「한 칸 ÷ 돌 지름」 그대로다.
  lineness(M) {
    // 고른 돌이 아니면 **여기서 끝난다** — 다른 다섯 돌은 이 그림을 아예 안 탄다.
    if (!M?.pierceInf) return 0;
    const C = LINEV.core;
    const ratio = LINEV.touch + LINEV.step * Math.max(0, M.lineGap ?? 1);
    const k = Math.max(0, Math.min(1, (C.ratioOpen - ratio) / (C.ratioOpen - C.ratioShut)));
    // **바닥을 깔고 그 위를 등급이 채운다**(사장님 지시 2026-08-02 — LINEV.core 머리말).
    //   ●1 이라도 이 돌은 이미 「선」이다. 등급은 그 선을 굵고 길게 만들 뿐이다.
    return C.base + (1 - C.base) * k;
  }

  drawLineStones(shots, col, M) {
    if (!M.pierceInf) { this.hidePoolTail(this.lineStonePool, 0); this.gLine.clear(); return; }
    const size0 = this.artScale * (M.stoneSize ?? 1);
    // 한 칸을 **돌 지름**에서 잰다. `lineGap` 0 이면 `touch` 만 남아 서로 닿는다.
    // ⚠ 12 는 **실제 스프라이트 크기**다(assets/art/objects/sling-stone/v1/stone.png = 12×12).
    //   지어낸 상수가 아니라 그림에서 잰 값이므로 아트가 바뀌면 이 값도 같이 바뀐다.
    const D = 12 * size0;
    const baseStep = D * (LINEV.touch + LINEV.step * Math.max(0, M.lineGap ?? 1));
    const physicalDiamond = M.stoneId === 'goreun' && (M.tier ?? 1) >= 6;
    // ◆는 빛의 폭이 아니라 실제 돌 밀도로 한 단계 올라간다. 기존 실은 남기되 뒤로
    // 물리고, 같은 길이에 돌을 더 촘촘히 놓아 판타지 광선보다 물성이 먼저 보인다.
    const step = physicalDiamond ? Math.max(D * 0.56, baseStep * 0.78) : baseStep;
    // ── **길이는 고정.** 등급이 미는 것은 「한 칸 ÷ 돌 지름」 비다 ──────────
    // ⚠ 【실측 정정 2026-07-30】 전 주석은 *"개수만 는다"* 였는데 그린 스프라이트를
    //   실제로 세니 ●1 12알 → ●5 12알로 **개수가 평평**했다. `D` 가 `stoneSize` 를 타서
    //   등급마다 +13% 커지고 그것이 `lineGap` 축소를 상쇄한다.
    //   움직이는 채널은 **겹침**이다: 한 칸/지름 1.16(개별 돌) → 0.70(겹쳐서 선) →
    //   성긴 세트 0.34(완전히 붙는다). 문턱은 이 저장소가 이미 갖고 있던 것이다.
    //   개수가 실제로 뛰는 것은 세트뿐이다(●●●●○ 16알 · ●●●●● 26알).
    const N = Math.max(3, Math.min(LINEV.nMax, Math.round(LINEV.span / step)));
    // **총량 난간** — `nMax` 는 발 하나만 잘랐고 총량은 발 수 × N 이었다(실측 최악 640).
    //   자취를 받는 발을 `LINEV.maxLines`(= `drawTrailBatch` 의 `maxTrails` 와 같은 수)로
    //   자른다. 넘는 발은 **본체만** 난다 — 자취를 짧게 깎지 않는다(짧아지면 등급이
    //   내려간 것처럼 보이고, 그것이 이 그림이 고치려던 바로 그 오독이다).
    // ── 「진짜 선」 — 돌 사이를 실이 잇는다 【사장님 지시 2026-08-02】 ──────
    //   세기는 `lineness` 하나가 정한다(0 = 낱개 · 1 = 완전히 이어짐).
    //   ⚠ **돌을 지우지 않는다.** 실은 `gLine`(depth 29) 이라 돌 **뒤**에 깔린다 —
    //     보이는 것은 여전히 돌이고, 실은 그 틈만 메운다. 그것이 「과하지 않은 레이저」다.
    const C = LINEV.core;
    const L = this.lineness(M);
    const gl = this.gLine;
    gl.clear();
    const drawCore = L > 0.02;
    let beamWidth = 0, beamAlpha = 0, filamentWidth = 0, filamentAlpha = 0;
    if (drawCore) {
      beamWidth = this.artScale * (C.wMin + (C.wMax - C.wMin) * L);
      beamAlpha = C.aMin + (C.aMax - C.aMin) * L;
      // 기존 색선 중앙에만 가는 밝은 심선을 겹친다. 돌들이 이어져 보이는 성격은
      // 유지하고 레이저 같은 직진성만 살짝 보태는 정도로 폭과 광량을 묶는다.
      filamentWidth = Math.max(this.artScale * C.filamentMin, beamWidth * C.filamentW);
      filamentAlpha = C.filamentAMin + (C.filamentAMax - C.filamentAMin) * L;
      if (physicalDiamond) {
        beamAlpha *= 0.34;
        filamentAlpha *= 0.28;
      }
    }

    let idx = 0, lines = 0;
    for (const s of shots) {
      if (s.shard) continue;
      if (++lines > LINEV.maxLines) break;
      const sp = Math.hypot(s.vx, s.vy) || 1;
      const ux = -s.vx / sp, uy = -s.vy / sp;      // 날아온 쪽
      const sc0 = this.shotVisScale(s) * size0;
      // ⓐ 실 — 머리에서 꼬리까지 한 줄. **꼬리로 갈수록 사라진다**(돌의 fade 와 같은 문법).
      if (drawCore) {
        const tx = s.x + ux * step * N, ty = s.y + uy * step * N;
        gl.lineStyle(beamWidth, col, beamAlpha);
        gl.beginPath(); gl.moveTo(s.x, s.y); gl.lineTo(tx, ty); gl.strokePath();
        gl.lineStyle(filamentWidth, 0xfff4dc, filamentAlpha);
        gl.beginPath(); gl.moveTo(s.x, s.y); gl.lineTo(tx, ty); gl.strokePath();
      }
      // ⓑ 돌 — **날아가는 쪽으로 눕고 늘어난다.** 회전(빙글빙글)을 멈춘다.
      //    구르는 돌은 낱개로 읽히고, 누운 돌은 **잔상**으로 읽힌다. 그 차이가 레이저다.
      const ang = Math.atan2(s.vy, s.vx);
      const stretch = 1 + C.stretch * L;
      for (let n = 1; n <= N; n++) {
        const k = n / N;
        const sc = sc0 * (1 - LINEV.shrink * k);
        this.pooledSprite(this.lineStonePool, idx++, 'sling-stone', 30)
          .setPosition(s.x + ux * step * n, s.y + uy * step * n)
          .setScale(sc * stretch, sc)
          .setRotation(ang)
          .setTint(col).setAlpha(1 - LINEV.fade * k).setVisible(true);
      }
    }
    this.hidePoolTail(this.lineStonePool, idx);
  }

  // ── 묵은 차돌 — **바닥에 남은 돌**  (A-3) ─────────────────────────
  // 안 그리면 A-3 의 순환(던진다 → 남는다 → **주우러 간다** → 커져 있다)이 화면에서
  // 끊긴다. 셋을 한꺼번에 말한다:
  //   **어디 있나**   — 스프라이트 위치
  //   **얼마나 컸나** — 크기(성장 배율 `st.g`). 식으면 그대로 작아진다
  //   **끌려오는가**  — `st.pull` 이면 옅은 선이 몸까지 이어진다(조각 흡인과 같은 문법)
  // ⚠ 구 `drawKeepStones` — **삭제됐다.** 차돌이 근접(궤도)이 되면서 바닥에 남는
  //   돌이 없어졌다(2026-07-30). 궤도 자체는 `drawSling`·`drawOrbitRing` 이 그린다.

  tierColor() {
    // ◆는 여섯 번째 일반색이 아니라 차갑게 반짝이는 다이아 광택이다.
    const TC = [0xf1e6cf, 0x6fa4c1, 0xd89452, 0x9a77b8, 0xc85f57, 0xbff4ff];
    return TC[Math.max(0, Math.min(5, (this.world.mods?.tier || 1) - 1))];
  }

  // ── 피 얼룩 ─────────────────────────────────────────────────────
  // 지면에 남는다. **적보다 아래**에 깔린다. 수명 동안 서서히 마른다.
  drawBlood(g) {
    const w = this.world;
    if (!w.blood?.length) return;
    const A = this.artScale;
    for (const b of w.blood) {
      const a = Math.max(0, 1 - (w.t - b.t) / BLOOD.life);
      const R = 6 * b.r * A;
      g.fillStyle(0x8e1f16, 0.42 * a);
      g.fillCircle(b.x, b.y, R * 0.9);
      // 튄 방울 — 한 방향으로 길게. 방향은 개체마다 고정
      g.fillStyle(0xa8291c, 0.34 * a);
      for (let i = 0; i < 5; i++) {
        const ang = b.a + (i - 2) * 0.17 + Math.sin(b.x * 0.07 + i * 2.3) * 0.05;
        const d = R * (0.9 + i * 0.42);
        g.fillCircle(b.x + Math.cos(ang) * d, b.y + Math.sin(ang) * d, R * (0.34 - i * 0.05));
      }
    }
  }

  // ── 소년 발밑 불티 ──────────────────────────────────────────────
  // 넓은 키라이트는 CombatVfx가 다층 ADD 블렌드로 만든다. 이 레이어는 중심에 작은
  // 불티만 보태서 단단한 원 경계와 중복 조명을 만들지 않는다.
  drawHearth(g, p) {
    const A = this.artScale, R = HEARTH.r * A;
    // 불티 — 결정적 궤적(시계 하나로 돈다). 개체를 안 만드므로 비용이 0에 가깝다.
    const t = this._artClock;
    for (let i = 0; i < HEARTH.embers; i++) {
      const ph = (t / HEARTH.life + i * 0.137) % 1;
      const ang = i * 2.399 + t * 0.35;
      const d = R * (0.15 + 0.75 * ph);
      const a = (1 - ph) * 0.55;
      g.fillStyle(0xffc46a, a);
      g.fillCircle(p.x + Math.cos(ang) * d, p.y + Math.sin(ang) * d - ph * HEARTH.drift * A * 0.3,
                   (1.6 - ph) * A);
    }
  }

  // ── 시체 ────────────────────────────────────────────────────────
  // 같은 아틀라스 프레임을 **어둡게 틴트해서** 잠깐 눕힌다. 새 아트가 0장이다.
  drawCorpses() {
    const w = this.world;
    let used = 0;
    for (const c of (w.corpses ?? [])) {
      if (!c.fn) continue;
      const frame = c.fn[c.fx] ?? c.fn[0];
      if (!frame) continue;
      const a = Math.max(0, 1 - (w.t - c.t) / BLOOD.corpseLife);
      const img = this.pooledSprite(this.corpsePool ??= [], used++, ATLAS_KEY, 19);
      img.setFrame(frame);
      img.setPosition(c.x, c.y + (c.oy ?? 0)).setScale(this.enemyScale)
         .setAlpha(0.30 + 0.45 * a).setTint(0x6b5a4a).setVisible(true);
    }
    this.hidePoolTail(this.corpsePool ??= [], used);
  }

  // ── 뜨는 피해 숫자 ──────────────────────────────────────────────
  // 개 짖음은 정보뿐 아니라 전장의 생동감과 동료의 존재감을 만드는 연출 채널이다.
  // 물량층은 작고 옅게, 정예·보스는 크게 — 동시 270마리에서도 위계를 유지한다.
  drawDamageText() {
    const w = this.world;
    if (w.finale) {
      this.dmgPool?.forEach((o) => o.setVisible(false));
      return;
    }
    // 정예·보스는 개별 숫자를 보존하고, 같은 셀의 물량 피해는 `값×횟수` 하나로
    // 합친다. 최대 여섯 문장만 남겨 다윗과 행동 실루엣이 숫자에 묻히지 않는다.
    const list = compactDamageText(w.dmgTexts, this._damageTextScratch ??= []);
    this.dmgPool ??= [];
    for (let i = 0; i < list.length; i++) {
      const d = list[i], u = (w.t - d.t) / DMGTEXT.life;
      while (this.dmgPool.length <= i) {
        this.dmgPool.push(this.add.text(0, 0, '', {
          fontFamily: 'ui-monospace, Consolas, monospace',
          fontSize: '26px', color: '#ffffff',
          stroke: '#1a1208', strokeThickness: 4,
        }).setOrigin(0.5, 1).setDepth(60).setVisible(false));
      }
      // **층을 크기와 밝기로 가른다.** 물량층 숫자가 정예 숫자와 같은 무게로 뜨면
      // 화면이 숫자로 덮이고 정작 궁금한 큰 놈의 숫자가 묻힌다.
      const k = d.big ? 1.35 : d.small ? 0.62 : 1;
      // 같은 좌표에 연속 타격이 포개질 때만 작은 결정적 지터를 준다. Math.random 을
      // 쓰지 않아 같은 전투는 같은 그림을 내며, 숫자가 적의 몸에서 멀어지지도 않는다.
      const seed = Math.sin(d.x * 0.071 + d.y * 0.113 + d.t * 17.3) * 43758.5453;
      const jitter = (seed - Math.floor(seed) - 0.5) * (d.small ? 14 : 8);
      let labelX = d.x + jitter;
      let labelY = d.y;
      // 근접 타격 숫자는 다윗의 몸 위가 아니라 공격이 들어온 방향 가장자리에 둔다.
      // 46px은 HP바 폭(64px)보다 작아 표식이 전투에서 이탈하지 않으면서 몸은 비운다.
      const dx = labelX - w.player.x, dy = labelY - w.player.y;
      const distance = Math.hypot(dx, dy);
      if (distance < 46) {
        const angle = distance > 1e-6
          ? Math.atan2(dy, dx)
          : (seed - Math.floor(seed)) * Math.PI * 2;
        labelX = w.player.x + Math.cos(angle) * 46;
        labelY = w.player.y + Math.sin(angle) * 46;
      }
      this.dmgPool[i]
        .setText(d.count > 1 ? `${d.v}×${d.count}` : String(d.v))
        .setPosition(labelX,
          labelY - DMGTEXT.rise * u * this.artScale * (d.small ? 0.7 : 1))
        .setScale(k * this.artScale * (1.15 - 0.15 * u))
        .setAlpha(Math.max(0, 1 - u * u) * (d.small ? 0.66 : 1))
        .setVisible(true);
    }
    for (let i = list.length; i < this.dmgPool.length; i++) this.dmgPool[i].setVisible(false);
  }

  // ── 【v2 D5】 밀집도 채널 — 원딜 조준선 ──────────────────────────
  // 투사체를 늘리면 확산이 넓어져 명중률이 **떨어진다**(10-B 2-2가 유도탄 폭주를
  // 막으려 일부러 만든 음의 결합). 그런데 지금은 **플레이어가 그 결합을 관리할
  // 수단이 없다** — 정렬이 순전히 운이다. 조준선 위에 겹친 적 수만큼 밝아진다:
  // **지금 몇 겹인지가 보인다.** 재미 6(*손은 한가하고 머리는 바쁘다*)이 여기서 켜진다.
  //   **적 쪽에는 그리지 않는다** — 350마리 화면에서 개체 표식은 무조건 묻힌다.
  drawAimLine(f, p) {
    const w = this.world;
    const n = w._aligned ?? 0;
    if (!w.mods || w.mods.alignDmg <= 0) return;
    const a = Math.atan2(p.ay ?? 0, p.ax ?? 1);
    const R = this._vis?.range ?? 400;
    const k = Math.min(1, n / 8);
    f.lineStyle(1 + 2.5 * k, 0xffb347, 0.12 + 0.55 * k);
    f.beginPath(); f.moveTo(p.x, p.y);
    f.lineTo(p.x + Math.cos(a) * R, p.y + Math.sin(a) * R);
    f.strokePath();
  }

  // ── **바닥 자국** — 내 공격이 지나온 자리가 남는다 ─────────────
  // 착탄 광역이 0.3초 만에 사라져 「여기를 쓸었다」가 안 보였다.
  // 피 얼룩은 남는데(BLOOD) 내 공격 자국만 안 남았다.
  drawDecals(g) {
    const w = this.world, col = this.tierColor();
    for (const d of w.decals ?? []) {
      const a = 1 - (w.t - d.t) / 7;
      if (a <= 0) continue;
      g.fillStyle(col, 0.055 * a);
      g.fillCircle(d.x, d.y, d.r);
    }
  }

  // ── 거친 납작한 돌 「물수제비」 — **자국이 앞으로 줄줄이**  (A-2) ────
  // ⚠⚠ **이 돌만 시각 채널이 0 이었다.** 자국은 광역이 금지돼 있어(gear.SKIP) `explodeAt`
  //   을 안 타므로 `decals` 에 한 톨도 안 남았다 — 예산 25 가 화면에서 통째로 안 보였다.
  //   재설계의 목적이 *"화면에 안 보이는 셋을 보이게 하는 것"* 인데 이 돌만 그대로였다.
  //
  //  세 가지를 한꺼번에 말한다:
  //    **어디를 때렸나** — 자국(작은 원). `decals` 와 같은 문법이고 크기만 작다
  //    **맞았나 헛쳤나** — 몸을 때린 자국은 진하고 조금 크다. 실측 자국의 97~98%가
  //                      빈 땅이라(검증 지적) 그 사실이 화면에 그대로 보여야 한다
  //    **튀는가**       — 자국 사이에서 돌이 **떠오른다.** 아래 그림자가 그것을 받친다
  //  ⚠ **선을 안 긋는다.** 자국 사이를 이으면 「레이저」가 되고, A-1 이 금지한
  //    *"빛나면 판타지"* 와 같은 자리다. 점이 줄지어 있는 것 자체가 「줄줄이」다.
  drawSkipMarks(g) {
    const w = this.world;
    w._presentationFrame = this._artClock ?? w.t;
    const M = w.mods || {};
    const col = this.tierColor(), A = this.artScale;
    const diamondClarity = diamondLocalAlpha(w);
    for (const d of w.skipMarks ?? []) {
      const age = w.t - d.t, a = 1 - age / SKIPV.life;
      if (a <= 0) continue;
      const R = SKIPV.r * A * (d.hit ? 1.35 : 1);
      g.fillStyle(col, (d.hit ? 0.30 : 0.13) * a);
      g.fillCircle(d.x, d.y, R);
      // 난 순간 톡 — 물을 때린 것이 보인다. 짧아서 겹쳐도 화면을 안 덮는다
      if (age < SKIPV.ringSec) {
        const k = age / SKIPV.ringSec;
        g.lineStyle(1.6 * (1 - k) * A, col, 0.55 * (1 - k));
        g.strokeCircle(d.x, d.y, R * (1 + 2.2 * k));
      }
      const phrase = d.diamond && M.stoneId === 'napjak'
        ? diamondSkipAccent(d.n, d.total) : 0;
      if (phrase) {
        // 32개를 줄이지 않는다. 8·16·24·32번째는 굵은 본 고리 뒤에 0.13초 늦은
        // 굴절 고리와 네 방향 픽셀 파편이 답해 「네 구절」을 멀리서도 센다.
        const beat = diamondBeat(age, 0.24, 0.40);
        const q = beat.total * diamondClarity;
        if (q > 0) {
          g.lineStyle((1.8 + phrase * 0.58 + q * 3.2) * A, 0xf4ffff, 0.82 * q * a);
          g.strokeCircle(d.x, d.y, R * (1.72 + phrase * 0.18 + (1 - q) * 2.25));
          if (beat.echo > 0) {
            g.lineStyle((1.2 + phrase * 0.22) * A, col,
              0.62 * beat.echo * diamondClarity * a);
            g.strokeCircle(d.x, d.y, R * (2.35 + phrase * 0.22 + (1 - beat.echo) * 2.7));
          }
          g.fillStyle(0xf4ffff, 0.78 * q * a);
          g.fillCircle(d.x, d.y, Math.max(1.8, R * 0.48));
          const pixels = 4 + phrase * 2;
          for (let i = 0; i < pixels; i++) {
            const ang = i / pixels * Math.PI * 2 + phrase * 0.23;
            const dist = R * (1.5 + (1 - q) * (2.2 + (i % 3) * 0.35));
            const s = Math.max(1.3, (1.8 + i % 3) * A * q);
            g.fillRect(d.x + Math.cos(ang) * dist - s * 0.5,
              d.y + Math.sin(ang) * dist - s * 0.5, s, s);
          }
        }
      }
    }
    // ── **튐 그림자** — 떠오른 돌 밑에 남는다 ──────────────────────
    // 그림자가 없으면 y 오프셋이 「뜬 것」이 아니라 「빗나간 것」으로 읽힌다.
    if (!(M.skips > 0)) return;
    for (const s of w.shots) {
      if (s.shard) continue;
      const h = this.skipHop(s);
      if (h <= 0) continue;
      const k = h / (SKIPV.hop * A);            // 0(바닥) ~ 1(제일 높이)
      g.fillStyle(0x000000, 0.26 * (1 - 0.45 * k));
      g.fillEllipse(s.x, s.y, 10 * A * (1 - 0.3 * k), 5 * A * (1 - 0.3 * k));
    }
  }

  // 이번 프레임에 그 돌이 **얼마나 떠 있나**(px). 자국 사이를 반원으로 넘는다.
  // ⚠ 판정을 한 톨도 안 건드린다 — `world` 는 이 값을 모른다. y 오프셋은 **그림만**이다.
  // ⚠ 이번 구간의 길이는 `s.sgap ÷ s.sdec` 다: `markSkip` 이 `sarm = sgap` 을 잡은
  //   **뒤에** `sgap *= sdec` 로 다음 간격을 줄여 두므로 감쇠 전 값이 이 구간의 길이다.
  skipHop(s) {
    if (!s.wet || !(s.sgap > 0)) return 0;
    const span = s.sdec > 0 ? s.sgap / s.sdec : s.sgap;
    if (!(span > 0)) return 0;
    const k = Math.max(0, Math.min(1, 1 - s.sarm / span));
    return Math.sin(k * Math.PI) * SKIPV.hop * this.artScale;
  }

  // ── 필드 드랍 「그러모으기」 ────────────────────────────────────
  // 드문 처치 사건이라 화면에서 확실히 눈에 띄어야 한다.
  // 주우면 그 자리에서 조각이 터지고 화면 전체가 빨려온다.
  drawDrops(g) {
    const w = this.world, A = this.artScale;
    for (const d of w.drops ?? []) {
      const age = w.t - d.t, left = 14 - age;
      const pulse = 0.5 + 0.5 * Math.sin(w.t * 6);
      // 사라지기 3초 전부터 깜빡인다 — 「지금 가야 하나」
      const a = left < 3 ? (0.35 + 0.65 * pulse) : 1;
      const R = (9 + 3 * pulse) * A;
      g.fillStyle(0xffd166, 0.22 * a); g.fillCircle(d.x, d.y, R * 2.2);
      g.fillStyle(0xffe9a8, 0.75 * a); g.fillCircle(d.x, d.y, R);
      g.lineStyle(2 * A, 0xfff0c8, 0.9 * a); g.strokeCircle(d.x, d.y, R * 1.6);
    }
    // 터지는 순간 — 큰 고리가 한 번 퍼진다
    const gk = w.t - (w._gatherAt ?? -9);
    if (gk >= 0 && gk < 0.5) {
      const k = gk / 0.5;
      g.lineStyle(5 * (1 - k) * A, 0xffe9a8, 0.8 * (1 - k));
      g.strokeCircle(w.player.x, w.player.y, 40 + 420 * k);
    }
  }

  // ── **비네트** — 화면 전체가 판돈을 말한다 ─────────────────────
  // 안정성이 이 게임의 판돈인데 **화면이 한 번도 그것을 말하지 않았다** — 숫자로만
  // 있었다. 밤 내내 같은 색이라 위험·안전이 안 읽혔다.
  //   반경 안에 적이 있으면 → 가장자리가 **붉게 조인다**
  //   비어서 회복 중이면   → 옅은 **푸른빛**
  //   HP 가 낮으면         → 맥동
  // 회복 상태를 읽히면서도 젖은 밤의 색 대비를 만드는 기능·분위기 겸용 광원이다.
  drawVignette(g) {
    const w = this.world, W = 1920, H = 1080;
    // N40 starts one tonal step below the other nights and settles darker over
    // its final minute. The finale can then close around David without a hard
    // bright-field-to-black cut.
    if (this.nightKey === 'N40') {
      const closing = smooth01((w.t - (NIGHT_SEC - 60)) / 60);
      g.fillStyle(0x02050b, 0.105 + closing * 0.075);
      g.fillRect(0, 0, W, H);
      if (w.finale) return;
    }
    const hpRatio = Math.max(0, Math.min(1, w.player.hp / w.maxHp));
    const hpDanger = Math.max(0, Math.min(1, (0.45 - hpRatio) / 0.45));
    const flockDanger = Math.max(0, Math.min(1, (70 - w.stability) / 35));
    const criticalPulse = hpRatio < 0.25 ? 0.5 + 0.5 * Math.sin(w.t * 6) : 0;
    // 붉은색은 오직 저체력 신호다. 양 떼 불안과 밤 분위기는 차가운 남색으로 분리한다.
    const hpActive = hpDanger > 0;
    const col = hpActive ? 0x8a1420 : 0x263d55;
    const A = hpActive
      ? 0.18 + hpDanger * 0.42 + criticalPulse * hpDanger * 0.18
      : 0.035 + flockDanger * 0.12;
    // 네 변에서 안쪽으로 좁아지는 띠 — 중앙은 절대 안 가린다(적이 안 보이면 안 된다)
    const D = hpActive ? 220 + 90 * hpDanger : 150 + 60 * flockDanger;
    for (let i = 0; i < 7; i++) {
      const k = (1 - i / 7), t = D * (i + 1) / 7;
      g.fillStyle(col, A * k * 0.22);
      g.fillRect(0, 0, W, t); g.fillRect(0, H - t, W, t);
      g.fillRect(0, 0, t, H); g.fillRect(W - t, 0, t, H);
    }
  }

  // ── **레벨 게이지** — 상점이 예고 없이 뜬다 ────────────────────
  // 차오르는 것이 보이면 *"곧 온다"* 가 생긴다. HUD 를 안 늘리고 **발밑 링**으로
  // 그린다 — 디제시스 안이고, 조각을 주울 때마다 자기 발밑이 차는 것이 보인다.
  drawLevelRing(g, p) {
    const w = this.world;
    const need = w.levelNeed || 1;
    const k = Math.max(0, Math.min(1, (w.gauge ?? 0) / need));
    if (k <= 0.01) return;
    const R = 26 * this.artScale;
    g.lineStyle(3 * this.artScale, 0x2a3350, 0.5);
    g.strokeCircle(p.x, p.y + 6, R);
    g.lineStyle(3 * this.artScale, k > 0.85 ? 0xffe08a : 0x8fd0ff, 0.85);
    g.beginPath();
    g.arc(p.x, p.y + 6, R, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * k, false);
    g.strokePath();
  }

  // ── 연쇄 처치 — **한꺼번에 몇을 지웠는지 화면이 말한다** ────────
  // world 가 0.12초 창으로 세어 두는데 화면에는 안 나왔다. 다섯을 동시에 지운
  // 순간이 **다섯을 따로 지운 것과 똑같이 보이면** 겹치는 쾌감이 안 생긴다.
  drawCombo(g, p) {
    const w = this.world;
    if (w.finale) {
      this._comboTxt?.setVisible(false);
      return;
    }
    const n = w._comboN ?? 0;
    if (n < 3) return;                       // 셋 미만은 일상이다 — 안 띄운다
    const age = w.t - (w._comboT ?? -9);
    if (age > 0.45) return;
    const k = age / 0.45, a = (1 - k) * (1 - k);
    const R = (14 + n * 3.2) * this.artScale * (0.7 + 0.6 * k);
    g.lineStyle(2.5 * a * this.artScale, this.tierColor(), 0.55 * a);
    g.strokeCircle(p.x, p.y, R);
    if (!this._comboTxt) {
      this._comboTxt = this.add.text(0, 0, '', {
        fontFamily: 'ui-monospace, Consolas, monospace', fontSize: '30px', color: '#fff0c8',
      }).setOrigin(0.5, 1).setDepth(120);
    }
    this._comboTxt.setPosition(p.x, p.y - 34 - 26 * k)
      .setText('×' + n).setAlpha(a).setScale(1 + 0.5 * (1 - k) * Math.min(2, n / 4))
      .setVisible(true);
  }

  // ── 장착 순간 — 바뀐 부위가 맡은 채널만 짧게 반응한다 ──────────
  // 기존 팽창 크기와 상위 등급 배율은 보존하되 끈 장착에만 반경을 연결한다.
  // 돌·받침·걸이는 drawSling 안에서 각각 돌 크기·테두리·가는 잔상으로 반응한다.
  drawEquipFlash(g) {
    const w = this.world;
    if (w.finale) { this._equipPartPulse = null; return 0; }
    const t = w.t - (w._equipAt ?? -9);
    const dur = EQUIP_FX.sec;
    if (t < 0 || t >= dur) { this._equipPartPulse = null; return 0; }
    const k = t / dur, a = (1 - k) * (1 - k);
    const big = (w._equipTier || 1) >= 6 ? 2.25 : (w._equipTier || 1) >= 5 ? 1.8 : 1;
    const strength = a * big;
    const amount = 0.55 * strength;
    this._equipPartPulse = { slot: w._equipSlot, strength, amount };
    // 화면 전체 색 플래시는 CombatVfx의 단일 중재 채널로 옮겼다. 여기의 무기 자체
    // 팽창은 그대로 남아 동시 콤보/정예 처치 때도 장착 반응이 사라지지 않는다.
    return w._equipSlot === 'cord' ? 1 + amount : 1;
  }

  // ── 착탄 광역 · 회전 링 ─────────────────────────────────────────
  // ⚠ **「오버킬 파문」(v1 5-1)은 2026-07-30 에 지웠다.** 그리던 대상인 오버킬 이월
  //   자체가 사라졌으므로(충돌 2 — 사장님 승인) `world._splashAt/_splashAmt/_splashXY`
  //   가 영영 안 채워진다. 값이 없는 그림을 남겨 두는 것이 규율 5 가 금지한 자리다.
  drawImpacts(g) {
    const w = this.world;
    if (w.finale) return;
    // ── 회전 광역 「도는 돌이 친다」 — **퍼지는 링** ────────────────
    // 판정 반경이 그림(SLING.R)보다 넓으므로, **넓힌 만큼을 이 링이 말한다.**
    // 안 그리면 「왜 저기 있는 놈이 죽었나」가 설명이 안 된다.
    const ob = w.t - (w._orbitAt ?? -9);
    const contact = orbitContactFeedback(ob, w._orbitR,
      w.mods?.orbitBand, w._orbitN);
    if (contact.visible) {
      const k = contact.progress, p = w.player;
      const cordColor = gearTierColor(w.mods?.tierBySlot?.cord ?? 1);
      const alpha = 1 - k, cy = p.y + ORBIT.cy;
      if (w.mods?.meleeOnly && contact.bandRatio > 0) {
        // 차돌의 두 경계는 world.orbitStrike의 R와 R*(1-band)를 그대로 쓴다.
        // 타격 순간 0.18초에만 떠서 상시 궤도선과 겹쳐 화면을 채우지 않는다.
        g.lineStyle(2.2 * alpha, cordColor, 0.58 * alpha);
        g.strokeEllipse(p.x, cy, contact.outerR * 2, contact.outerR * 2 * ORBIT.ky);
        g.lineStyle(1.4 * alpha, cordColor, 0.36 * alpha);
        g.strokeEllipse(p.x, cy, contact.innerR * 2, contact.innerR * 2 * ORBIT.ky);
      } else {
        g.lineStyle((3 + 1.6 * contact.ticks) * alpha, cordColor, 0.62 * alpha);
        g.strokeEllipse(p.x, cy, contact.outerR * 2 * (0.62 + 0.42 * k),
          contact.outerR * 2 * ORBIT.ky * (0.62 + 0.42 * k));
      }

      // 굵기만으로는 한 마리와 무리를 구분하기 어려웠다. world가 남긴 실제 동시
      // 타격 수를 현행 상한 4 안에서 짧은 사각 눈금으로만 번역한다.
      const tickLen = 4 * this.artScale;
      g.lineStyle(1.5 * alpha, cordColor, 0.68 * alpha);
      for (let i = 0; i < contact.ticks; i++) {
        const a = -Math.PI / 2 + i * Math.PI * 2 / contact.ticks;
        const ca = Math.cos(a), sa = Math.sin(a);
        const r0 = contact.outerR - tickLen;
        const r1 = contact.outerR;
        g.lineBetween(p.x + ca * r0, cy + sa * r0 * ORBIT.ky,
          p.x + ca * r1, cy + sa * r1 * ORBIT.ky);
      }
    }
  }

  // ── 「기름을 붓는다」 화염 지대 ──────────────────────────────────
  // **지나온 자리에 남는다**(world.stepFire 주석 — 배치 규칙은 정본에 없다).
  // 꺼져 가는 것이 보여야 한다: 수명이 곧 밝기다. 겹친 자리는 자연히 더 밝아진다.
  // 화염의 **광원.** 밤이라 대비가 극적이다 — 지대 위치가 곧 정보이므로 빛도 정보다.
  // 스프라이트는 지대 자체를 그리고, 이건 그 **바깥으로 번지는 빛**이다(두 겹).
  drawFireGlow(g) {
    const w = this.world;
    if (!w.fires?.length) return;
    for (const f of w.fires) {
      const R = f.r ?? FIRE.radius;
      const a = Math.max(0, Math.min(1, f.life / (f.r ? FIRE.emberLife : FIRE.life)));
      g.fillStyle(0xff7a2a, 0.045 + 0.055 * a); g.fillCircle(f.x, f.y, R * 1.85);
      g.fillStyle(0xff8c3a, 0.055 + 0.075 * a); g.fillCircle(f.x, f.y, R * 1.35);
    }
  }

  drawFires() {
    const w = this.world;
    let used = 0;
    for (const [index, f] of (w.fires ?? []).entries()) {
      const R = f.r ?? FIRE.radius;
      const lifeMax = f.r ? FIRE.emberLife : FIRE.life;
      const a = Math.max(0, Math.min(1, f.life / lifeMax));
      const band = Math.min(3, Math.floor((1 - a) * 4));
      // 수명 = 밝기. 루프 프레임과 별개로 네 단계가 차례로 식는다.
      const tint = [0xffffff, 0xe5b899, 0xa66d58, 0x5a4642][band];
      const alpha = [1.00, 0.78, 0.52, 0.28][band];
      // 「불티」는 별도 아트가 아니다. 같은 96×96 시트를 반경 비율만큼 줄인다.
      const scale = (2 * R) / 96;
      this.pooledSprite(this.firePool, used++, 'fire-zone', 6, 0.68)
        .setFrame(Math.floor(this._artClock * 8 + index * 1.7) % 4)
        .setPosition(f.x, f.y)
        .setScale(scale)
        .setTint(tint)
        .setAlpha(alpha)
        .setVisible(true);
    }
    this.hidePoolTail(this.firePool, used);
  }

  slingPose(p) {
    const face = slingFaceFromVector(p.fx, p.fy);
    const idle = !this._playerMoving && this._idleFrameCount > 0;
    const frame = idle
      ? Math.floor((this._artClock ?? 0) * IDLE_FPS) % this._idleFrameCount
      : this._playerMoving
        ? Math.floor((this._walkClock ?? 0) * WALK_FPS) % 4
        : 0;
    return { face, frame, idle };
  }

  // 회전 중심 = 방향·걷기 프레임마다 달라지는 **든손의 검지·엄지 자리.**
  // 프레임 원점 (0.5, 0.9375) = (32, 60)을 월드 좌표로 되돌린다.
  orbitCenter(p) {
    const A = this.artScale;
    const { face, frame, idle } = this.slingPose(p);
    const table = idle ? SLING_IDLE_HAND_SOCKET : SLING_HAND_SOCKET;
    const [sx, sy] = table[face][frame];
    return {
      x: p.x + (sx - 32) * A,
      y: p.y + (sy - BODY_ANCHOR_Y) * A,
    };
  }

  // ── 무릿매 ──────────────────────────────────────────────────────
  // 돌 **하나**가 손 옆에서 짧고 빠르게 돈다. 회전은 **고정**이고 강화를 안 탄다.
  //   · 픽셀아트에서 흐림은 알파 블러가 아니라 **유령 복사**로 낸다(계약: hard pixel
  //     edges / no anti-aliasing). 뒤로 GHOST_ARC 만큼 유령을 깔면 그것이 잔상이다.
  //   · 줄을 같이 그린다 — 줄이 없으면 돌이 그냥 떠 있는 점으로 읽힌다.
  //     무릿매가 「돌아가는 물건」으로 읽히는 것은 사실 **줄** 때문이다.
  // ── 【2026-07-29】 **판정과 그림을 일치시킨다** ─────────────────────
  // 회전 광역(`ORBIT.r`)이 판정을 갖는데 그림은 손 옆 27.6px에 머물러 있어서
  // **왜 저기 있는 놈이 죽는지 설명이 안 됐다.** 그리고 화면이 허전했다 —
  // 뱀서류는 **상시로 켜져 있는 것**이 화면을 채우는데 우리는 돌 하나 날아가고 끝이었다.
  // 지팡이는 이미 그렇게 하고 있다(호가 alpha 0.10으로 **항상** 떠 있고 스윙 때 진해진다).
  // → 물맷돌도 **같은 문법**: 궤도가 판정 크기로 돌고, 범위 원이 상시 은은하게,
  //   칠 때 진해진다. 새 규칙이 아니라 근딜이 쓰던 것을 원딜에도 준다.
  drawSling(f, p) {
    const A = this.artScale, w = this.world, M = w.mods || {};
    const c = this.orbitCenter(p);
    // **머리 위로 돌린다** — 중심은 엄지·검지 사이(SLING.HAND)에서 조금 더 위.
    const cy = c.y + ORBIT.cy;
    // **판정 반경 그대로.** 끈이 길면 크게 돈다(gear.orbitR).
    // 원거리 보조 궤도는 orbitLook을 쓸 수 있지만, 차돌 T1~T4는 orbitLook === orbitR로
    // 그림과 실제 회전 타격 판정을 정확히 맞춘다.
    const R = ORBIT.r * (M.orbitLook || M.orbitR || 1) * (this._equipPulse || 1), KY = ORBIT.ky;
    // **회전 속도 ← 걸이.** 고정값이라 걸이가 화면에서 죽어 있었다.
    const ph = this._slingPhase ?? 0;
    const at = (k) => ({ x: c.x + Math.cos(ph - k) * R, y: cy + Math.sin(ph - k) * R * KY });
    // ── 등급 색 — 네 부위가 실제로 담당하는 그림 채널에 1:1로 연결한다 ──
    const tiers = M.tierBySlot ?? {};
    const tier = Math.max(1, Math.min(6, tiers.stone ?? M.tier ?? 1));
    const cordTier = Math.max(1, Math.min(6, tiers.cord ?? 1));
    const colors = {
      stone: gearTierColor(tier),
      cradle: gearTierColor(tiers.cradle ?? 1),
      cord: gearTierColor(cordTier),
      loop: gearTierColor(tiers.loop ?? 1),
    };
    const col = colors.stone;
    const since = w.t - (w._orbitAt ?? -9);
    const hot = Math.max(0, 1 - since / 0.30);
    // ●●●●● 는 맥동한다 — **잭팟의 시각적 보상**
    const orbitPulse = cordTier >= 6 ? 0.16 + 0.12 * Math.sin(w.t * 8)
      : cordTier >= 5 ? 0.10 + 0.10 * Math.sin(w.t * 7) : 0;
    // **상시 범위 타원** — 이것이 화면을 채운다(지팡이 호와 같은 문법)
    f.lineStyle(1.5 + 2.5 * hot + (cordTier >= 4 ? 1 : 0), colors.cord,
                0.09 + 0.51 * hot + orbitPulse * 0.75);
    f.strokeEllipse(c.x, cy, R * 2, R * 2 * KY);
    if (cordTier >= 5) {
      f.lineStyle(1, colors.cord, 0.07 + orbitPulse * 0.65);
      f.strokeEllipse(c.x, cy, R * 2.16, R * 2.16 * KY);
    }
    if (cordTier >= 6) {
      f.lineStyle(1, 0xf4ffff, 0.10 + orbitPulse * 0.55);
      f.strokeEllipse(c.x, cy, R * 2.25, R * 2.25 * KY);
    }
    if (hot > 0) { f.fillStyle(colors.cord, 0.10 * hot); f.fillEllipse(c.x, cy, R * 2, R * 2 * KY); }
    this.drawDiamondPartProofs(f, c, cy, R, KY, M, hot, colors);
    if (tier >= 6 && M.stoneId === 'chadol' && since >= 0 && since < 0.68) {
      // 차돌 ◆는 맞는 순간 크게 수축하고, 0.13초 뒤 바깥으로 굴절 고리가 풀린다.
      const beat = diamondBeat(since, 0.25, 0.42);
      const clarity = diamondLocalAlpha(w);
      const main = beat.main * clarity, echo = beat.echo * clarity;
      const rr = R * (1 - 0.28 * main);
      f.lineStyle(2.8 + 5.2 * main, 0xf4ffff, 0.42 + 0.54 * main);
      f.strokeEllipse(c.x, cy, rr * 2, rr * 2 * KY);
      if (echo > 0) {
        const er = R * (1.04 + (1 - echo) * 0.32);
        f.lineStyle(1.8 + 4.4 * echo, col, 0.72 * echo);
        f.strokeEllipse(c.x, cy, er * 2, er * 2 * KY);
      }
    }
    // ── 세트 표시 — **궤도에 이펙트**  【E】 ────────────────────────
    // ⚠⚠ **색으로 하지 않는다**(충돌 6). 위 `col` 다섯 색이 이미 등급을 말하므로
    //   세트가 색을 또 쓰면 두 정보가 한 채널에서 싸운다.
    //   → 쓰는 채널은 **방향과 겹**이다: 궤도 바깥에 **거꾸로 도는 토막 링**이 생긴다.
    //     돌은 시계 방향, 이 링은 반시계 — 그 어긋남 자체가 「지금 뭔가 다르다」다.
    //   작게(●●●●) 는 한 겹 · 크게(●●●●●) 는 두 겹이고 토막이 두 배 촘촘하다.
    // ⚠ 회차에 한 번 볼까 말까 한 사건이므로(실측 상한 3.3%) **평시에는 이 블록을
    //   한 번도 안 돈다** — `M.setId` 가 null 이면 그대로 지나간다.
    if (M.setId) {
      const big = !!M.setBig;
      const segs = SETFX.ringSegs * (big ? 2 : 1);
      const rr = R * SETFX.ringGap;
      const spin = w.t * SETFX.ringSpin;
      for (let lay = 0; lay <= (big ? 1 : 0); lay++) {
        const lr = rr * (1 + lay * 0.10);
        f.lineStyle(1.6 - lay * 0.5, col, 0.55 - lay * 0.18);
        for (let n = 0; n < segs; n++) {
          const a0 = spin * (lay ? -0.75 : 1) + (n / segs) * Math.PI * 2;
          const a1 = a0 + (Math.PI * 2 / segs) * 0.52;    // 토막 — 반쯤 비운다
          f.beginPath();
          f.moveTo(c.x + Math.cos(a0) * lr, cy + Math.sin(a0) * lr * KY);
          for (let q = 1; q <= 4; q++) {
            const a = a0 + (a1 - a0) * (q / 4);
            f.lineTo(c.x + Math.cos(a) * lr, cy + Math.sin(a) * lr * KY);
          }
          f.strokePath();
        }
      }
    }
    this.drawDiamondSetBond(f, c, cy, R, KY, M, col);
    // ── 도는 돌 — **개수 ← 받침(발 수) · 크기 ← 돌(무게) · 색 ← 등급** ──
    // **화면을 덮는다** — 상한 8은 잔돌 계열의 밀도를 잘라 먹었다.
    const shots = Math.max(1, Math.min(14, (M._gear?.shots) || 1));
    const stoneEquipAmount = this._equipPartPulse?.slot === 'stone'
      ? this._equipPartPulse.amount : 0;
    const size = A * (M.stoneSize || 1) * (1 + stoneEquipAmount);
    const trailNow = this._artClock ?? w.t;
    if (this._orbitTrailShots !== shots) {
      for (const trail of this.orbitTrails) trail?.reset();
      this.orbitTrails.length = shots;
      this._orbitTrailShots = shots;
    }

    // Record the actual rendered coordinate of every stone. This combines
    // rotation, player movement, eight-direction hand sockets and live radius
    // changes into one truthful world-space path.
    for (let n = 0; n < shots; n++) {
      const base = (n / shots) * Math.PI * 2;
      const q = at(base);
      const trail = this.orbitTrails[n]
        ??= new TrajectoryTrail(18, 8, 4);
      trail.sample(q.x, q.y, trailNow);
      trail.trim(trailNow, 0.19 + tier * 0.006, Math.max(52, R * 1.02));
    }
    const loopEquipAmount = this._equipPartPulse?.slot === 'loop'
      ? this._equipPartPulse.amount : 0;
    this._orbitTrailPasses[0].color = colors.stone;
    this._orbitTrailPasses[1].color = colors.stone;
    this._orbitTrailPasses[2].color = colors.loop;
    this._orbitTrailPasses[2].width = 0.85 * (1 + loopEquipAmount);
    this._orbitTrailPasses[2].alpha = Math.min(1, 0.72 * (1 + loopEquipAmount));
    drawTrailBatch(f, this.orbitTrails, this._orbitTrailPasses, {
      alphaScale: Math.min(1, 0.72 + 0.28 * Math.sqrt(4 / Math.max(4, shots))),
      widthScale: Math.sqrt(size),
      smooth: true,
      maxTrails: 14,
    });

    let idx = 0;
    for (let n = 0; n < shots; n++) {
      const base = (n / shots) * Math.PI * 2;
      const trail = this.orbitTrails[n];
      // 유령도 현재 타원에서 역산하지 않고 실제 과거 좌표를 꺼낸다.
      const GH = orbitGhostCount(tier);
      for (let i = GH; i >= 1; i--) {
        if (!trail || trail.count <= i) continue;
        const offset = Math.max(1, Math.round(i * (trail.count - 1) / (GH + 1)));
        const history = Math.max(0, trail.count - 1 - offset);
        this.pooledSprite(this.orbitStonePool, idx++, 'sling-stone', 31)
          .setPosition(trail.getX(history), trail.getY(history))
          .setScale(size * (1 - 0.09 * i))
          .setRotation(this._artClock * 10 + n * 2.399 - i * 0.24)
          .setTint(col).setAlpha(0.44 * (1 - i / (GH + 1))).setVisible(true);
      }
      const q = at(base);
      this.pooledSprite(this.orbitStonePool, idx++, 'sling-stone', 31)
        .setPosition(q.x, q.y).setScale(size)
        .setRotation(this._artClock * 10 + n * 2.399)
        .setTint(col).setAlpha(1).setVisible(true);
    }
    this._orbitCount = idx;
    const h = at(0);
    const volley = Math.max(0, 1 - (w.t - (w._volleyAt ?? -9)) / 0.20);
    const cradleEquip = this._equipPartPulse?.slot === 'cradle'
      ? Math.min(1, this._equipPartPulse.strength) : 0;
    const cradleFlash = Math.max(volley, cradleEquip);
    if (cradleFlash > 0) {
      f.lineStyle(1.2, colors.cradle, 0.62 * cradleFlash);
      f.strokeCircle(h.x, h.y, (4.5 + cradleFlash * 2) * size);
    }
    // 줄 — **가늘고 어둡다.** 굵고 밝게 그렸더니 화면에서 **칼을 휘두르는 것**으로
    // 읽혔다(실측 소감). 유령 쪽 줄은 아예 뺀다 — 두 줄이 쓸리면 그게 곧 칼자국이다.
    // 보이는 것은 줄이 아니라 **끝의 돌**이어야 한다.
    f.lineStyle(SLING.CORD_W * A, 0x8c7f66, SLING.CORD_A);
    f.lineBetween(c.x + (h.x - c.x) * SLING.CORD_FROM, cy + (h.y - cy) * SLING.CORD_FROM, h.x, h.y);
    this.hidePoolTail(this.orbitStonePool, this._orbitCount ?? 1);
  }

  // 돌 외 세 부위의 ◆도 구매 플래시가 끝난 뒤 계속 보인다. 각 부위의 공용 등급색을
  // 담당 축(받침=발 묶음, 끈=반경, 고리=회전)의 기하에만 제한해 쓴다.
  drawDiamondPartProofs(f, c, cy, R, KY, M, hot, colors) {
    const T = M.tierBySlot ?? {};
    const aim = Math.atan2(this.world.player.ay ?? 0, this.world.player.ax ?? 1);
    if ((T.cradle ?? 1) >= 6) {
      const volley = Math.max(0, 1 - (this.world.t - (this.world._volleyAt ?? -9)) / 0.20);
      const r0 = 8 + volley * 5;
      f.lineStyle(1.4 + volley * 1.8, 0xf4ffff, 0.48 + volley * 0.42);
      for (const off of [-0.34, 0, 0.34]) {
        f.lineBetween(c.x + Math.cos(aim + off) * 3, c.y + Math.sin(aim + off) * 3,
          c.x + Math.cos(aim + off) * r0, c.y + Math.sin(aim + off) * r0);
      }
      f.lineStyle(1.2, colors.cradle, 0.62);
      f.strokeCircle(c.x, c.y, 4.5 + volley * 2);
    }
    if ((T.cord ?? 1) >= 6) {
      const rr = R * 1.31;
      f.lineStyle(1.25 + hot, 0xf4ffff, 0.30 + hot * 0.28);
      for (let seg = 0; seg < 4; seg++) {
        const a0 = seg * Math.PI / 2 + 0.18;
        const a1 = a0 + 0.48;
        f.beginPath();
        for (let q = 0; q <= 4; q++) {
          const a = a0 + (a1 - a0) * q / 4;
          const x = c.x + Math.cos(a) * rr;
          const y = cy + Math.sin(a) * rr * KY;
          if (q === 0) f.moveTo(x, y); else f.lineTo(x, y);
        }
        f.strokePath();
      }
    }
    if ((T.loop ?? 1) >= 6) {
      const spin = (this._slingPhase ?? 0) * 1.55;
      for (const off of [0, Math.PI]) {
        const a = spin + off;
        const x = c.x + Math.cos(a) * R * 1.17;
        const y = cy + Math.sin(a) * R * 1.17 * KY;
        f.fillStyle(0xf4ffff, 0.62 + 0.30 * Math.sin(this._artClock * 12 + off) ** 2);
        f.fillCircle(x, y, 2.2 + hot * 1.8);
        f.lineStyle(1, colors.loop, 0.50);
        f.strokeCircle(x, y, 5 + hot * 3);
      }
    }
  }

  drawDiamondSetBond(f, c, cy, R, KY, M, col) {
    if (!isDiamondSetPresentation(M)) return;
    const w = this.world;
    const clarity = diamondLocalAlpha(w);
    const clock = this._artClock ?? w.t;
    const wsRate = Math.max(0.01, Number(w._ws?.rate) || 1);
    const dayRate = Math.max(0.01, Number(w.dayIn?.fireRateMul) || 1);
    const interval = Math.max(0.04, (Number(M.slowMul) || 1) / (wsRate * dayRate));
    const fireProgress = clamp01((Number(w._fire) || 0) / interval);
    // 발사 직전 마지막 42% 구간에서 네 ◆가 바깥 궤도에서 손으로 수렴한다.
    // 차돌처럼 투사체가 없는 경우에는 실제 궤도 접촉 박자가 같은 역할을 맡는다.
    let gather = clamp01((fireProgress - 0.58) / 0.42);
    gather = gather * gather * (3 - 2 * gather);
    if (M.meleeOnly) {
      const contact = diamondBeat(w.t - (w._orbitAt ?? -9), 0.25, 0.42);
      gather = Math.max(gather, contact.main * 0.9);
    }
    const release = diamondBeat(w.t - (w._volleyAt ?? -9), 0.22, 0.36);
    const spin = -(this._slingPhase ?? clock * 7) * 0.34;
    const rr = R * (1.38 - gather * 0.72 + release.echo * 0.12);
    const nodeAlpha = (0.48 + gather * 0.46 + release.total * 0.22) * clarity;
    const nodes = [];
    for (let i = 0; i < 4; i++) {
      const a = spin + i * Math.PI * 0.5;
      nodes.push({ x: c.x + Math.cos(a) * rr, y: cy + Math.sin(a) * rr * KY, a });
    }

    // 네 칸이 한 구조로 묶였다는 증거. 면을 채우지 않고 얇은 결속선과 네 ◆만 쓴다.
    f.lineStyle(1.1 + gather * 1.8, col, 0.22 * nodeAlpha);
    for (let i = 0; i < 4; i++) {
      const n = nodes[i], next = nodes[(i + 1) % 4];
      f.lineBetween(n.x, n.y, next.x, next.y);
    }
    f.lineStyle(1.2 + gather * 2.8, 0xf4ffff, 0.48 * nodeAlpha);
    for (const n of nodes) {
      const inward = 0.28 + gather * 0.46;
      f.lineBetween(n.x, n.y, n.x + (c.x - n.x) * inward, n.y + (cy - n.y) * inward);
      const size = (4.8 + gather * 5.8 + release.total * 2.2) * this.artScale;
      f.lineBetween(n.x, n.y - size, n.x + size, n.y);
      f.lineBetween(n.x + size, n.y, n.x, n.y + size);
      f.lineBetween(n.x, n.y + size, n.x - size, n.y);
      f.lineBetween(n.x - size, n.y, n.x, n.y - size);
      const core = Math.max(1.5, (2.2 + gather * 2.6) * this.artScale);
      f.fillStyle(0xf4ffff, 0.82 * nodeAlpha);
      f.fillRect(n.x - core * 0.5, n.y - core * 0.5, core, core);
    }

    if (release.total > 0) {
      const q = release.total * clarity;
      const ring = R * (0.48 + (1 - q) * 1.18);
      f.lineStyle(1.8 + 5.2 * q, 0xbff4ff, 0.76 * q);
      f.strokeEllipse(c.x, cy, ring * 2, ring * 2 * KY);
      if (release.echo > 0) {
        const echoR = R * (0.74 + (1 - release.echo) * 1.34);
        f.lineStyle(1.2 + 3.4 * release.echo, 0xf4ffff,
          0.54 * release.echo * clarity);
        f.strokeEllipse(c.x, cy, echoR * 2, echoR * 2 * KY);
      }
    }
  }

  drawPieces() {
    let used = 0;
    for (const piece of this.world.pieces) {
      if (piece.dead) continue;
      this.pooledSprite(this.piecePool, used++, 'piece-shard', 23)
        .setFrame(piece.v > 5 ? 1 : 0)
        .setPosition(piece.x, piece.y)
        .setVisible(true);
    }
    this.hidePoolTail(this.piecePool, used);
  }

  drawWater() {
    const w = this.world;
    for (let i = 0; i < w.water.length; i++) {
      const water = w.water[i];
      const dry = water.dry > 0;
      const fill = dry ? 1 - water.dry / w.waterCfg.cooldown : 1;
      const frame = dry ? (fill < 0.15 ? 0 : 1) : 2;
      if (!this.waterSprites[i]) {
        this.waterSprites[i] = this.add.sprite(water.x, water.y, 'water-puddle')
          .setOrigin(0.5, 0.54).setDepth(8);
      }
      this.waterSprites[i]
        .setFrame(frame)
        .setPosition(water.x, water.y)
        .setVisible(true);
    }
    for (let i = w.water.length; i < this.waterSprites.length; i++)
      this.waterSprites[i].setVisible(false);
  }

  drawDogs() {
    const w = this.world;
    for (let i = 0; i < w.dogs.length; i++) {
      const dog = w.dogs[i];
      const sprite = this.dogSprites[i];
      const px = dog._artX ?? dog.x;
      const py = dog._artY ?? dog.y;
      const dx = dog.x - px, dy = dog.y - py;
      dog._artX = dog.x; dog._artY = dog.y;
      dog._artVx = (dog._artVx ?? (i === 0 ? 0.5 : -0.5)) * 0.82 + dx * 0.18;
      if (dog._artVx > 0.01) dog._artRow = 0;
      else if (dog._artVx < -0.01) dog._artRow = 1;

      const moving = Math.hypot(dx, dy) > 0.05;
      const biteAge = dog.biteAt == null ? Infinity : Math.max(0, w.t - dog.biteAt);
      // 피해는 연속 DPS지만 `biteAt`은 DOGFX.bite 간격의 명시적 공격 박자이고,
      // `attack`은 그중 화면에 보이는 DOGFX.hold 구간이다. 그 짧은 구간을 3칸에
      // 나눠 준비→돌진→물기를 끝까지 보여 준다. 구 구현은
      // `dog.attack > 0` 동안 5번 칸에 붙박아 모든 종이 정지 그림으로만 물었다.
      const attackActive = dog.attack > 0 && biteAge < DOGFX.hold;
      const attackU = attackActive
        ? Math.max(0, Math.min(0.999, biteAge / Math.max(DOGFX.hold, 1e-6)))
        : 0;
      let column = 0;
      if (attackActive) {
        column = DOG_FRAME.attack[Math.min(DOG_FRAME.attack.length - 1,
          Math.floor(attackU * DOG_FRAME.attack.length))];
        // 세로로 달려들 때도 마지막으로 문 방향의 좌/우 실루엣을 유지한다.
        if (Math.abs(dog.biteDx ?? 0) > 0.05) dog._artRow = dog.biteDx >= 0 ? 0 : 1;
      }
      else if (dog.mode === '요격') {
        column = dog.kind === 'seori' ? 4
          : DOG_FRAME.intercept[Math.floor(this._artClock * 8 + i) % DOG_FRAME.intercept.length];
      } else if (dog.kind === 'seori' && w.inRad > 0) {
        column = 3;
      } else if (moving) {
        column = DOG_FRAME.walk[Math.floor(this._artClock * 6 + i) % DOG_FRAME.walk.length];
      }

      // 시트 프레임만 넘기면 작은 48px 실루엣에서는 공격이 다시 보행처럼 읽힌다.
      // 몸을 무는 방향으로 짧게 보내고 눌렀다 되돌린다. 전부 렌더 좌표/배율뿐이다.
      const lunge = attackActive ? Math.sin(Math.PI * attackU) : 0;
      const breedScale = dogVisualScale(dog.kind);
      const attackScale = 1;
      const sx = this.dogScale * breedScale * attackScale * (1 + lunge * 0.08);
      const sy = this.dogScale * breedScale * attackScale * (1 - lunge * 0.055);
      const lungePx = 7 * this.artScale * this.dogMul * lunge;
      // 스케일이 큰 품종/공격 자세도 발바닥은 같은 월드 y에 둔다. 중앙 원점인 48px
      // 시트의 공통 발 기준선(중심 아래 약 19px)만큼 늘어난 몫을 위로 보정한다.
      const baselineY = dog.y - DOG_FOOT_OFFSET * (sy - this.dogScale);
      sprite
        .setFrame((dog._artRow ?? 0) * DOG_FRAME.cols + column)
        .setPosition(dog.x + (dog.biteDx ?? 0) * lungePx,
                     baselineY + (dog.biteDy ?? 0) * lungePx)
        .setScale(sx, sy)
        .setDepth(22 + dog.y / 10000)
        .setVisible(true);
    }
  }

  // ══════════════════════════════════════════════════════════════
  //  개 — **아군 표식과 물기.**   【사장님 지시 2026-07-30】
  //    *"이게 몹인지 내편인지 모른다고. 나는 알지만 다른 유저들은 모를수도 있어."*
  //
  //  ■ **두 가지를 서로 다른 시간에 말한다.**
  //      상시 — 발밑 아군 고리. 「저건 내 것이다」
  //      물 때 — 무는 쪽으로 펴지는 호. 「저것이 지금 **저놈을** 물고 있다」
  //    상시 표식만으로는 부족하다(가만있는 개는 여전히 배경이다). 물기 표시만으로도
  //    부족하다(안 물고 있는 동안이 대부분이다). **둘 다 있어야 채널이 닫힌다.**
  //
  //  ■ **새 색을 만들지 않는다.** `DOGFX.ally` 는 개 짖음 고리가 쓰던 `0x8fd0ff` 그대로다.
  //    적 쪽 채널(붉은 눈동자 · 흰 타격 섬광 · 피)과 색이 안 겹친다.
  //
  //  ■ **디자인방향 0장의 우선순위에서 3순위다** — *위험 예고 → 공격 인과 → **배우 식별**
  //    → 분위기*. 배우 식별은 이름이 붙은 정당한 기능이고, 이 표식이 그 자리다.
  //    그래서 위험 예고(예고 고리·그림자)보다 **약하게** 그린다.
  //
  //  ■ 층: 발밑 표식은 `gUnit`(10) — **개 스프라이트(22) 아래**라 그림을 안 가린다.
  //        무는 호는 `gFx`(30) — 개 위. 무는 것은 개보다 앞의 사건이다.
  // ══════════════════════════════════════════════════════════════
  drawDogFx(u, f) {
    const w = this.world, A = this.artScale, t = w.t;
    for (const [dogIndex, d] of w.dogs.entries()) {
      // ── 상시: 발밑 광원 ──────────────────────────────────
      // ⚠ **개보다 커야 한다.** 스프라이트(48 × dogScale)보다 작으면 통째로 뒤에 숨는다 —
      //   처음 값이 그래서 화면에서 안 보였다(config.js DOGFX 참조).
      // 겹 셋으로 부드럽게 — 딱딱한 테두리를 두르면 RTS 선택 고리처럼 읽히고,
      // 그러면 「목축의 언어」(기둥 3)를 벗어난다. **빛이어야 한다.**
      const breathe = 0.88 + 0.12 * Math.sin(t * 2.6 + d.x * 0.01);
      const rx = DOGFX.markR * A * this.dogMul, ry = rx * 0.40;
      const foot = d.y + 24 * this.dogScale * 0.62;      // 발이 닿는 자리
      for (const [k, a] of [[1.00, 0.10], [0.68, 0.16], [0.38, 0.24]]) {
        u.fillStyle(DOGFX.ally, DOGFX.mark * a * breathe);
        u.fillEllipse(d.x, foot, rx * 2 * k, ry * 2 * k);
      }
      // A slowly drifting, broken braid keeps the mark organic while making
      // separate dogs legible when their soft pools overlap.
      const phase = t * 0.48 + dogIndex * 1.37;
      u.lineStyle(Math.max(1, DOGFX.edge * 0.55 * A), DOGFX.ally, 0.20 * breathe);
      for (let q = 0; q < 4; q++) {
        const a0 = phase + q * Math.PI / 2;
        const a1 = a0 + 0.38;
        u.lineBetween(d.x + Math.cos(a0) * rx * 0.82, foot + Math.sin(a0) * ry * 0.82,
                      d.x + Math.cos(a1) * rx * 0.82, foot + Math.sin(a1) * ry * 0.82);
      }
      // Tiny role rune: a cup-shaped shield for guard dogs, twin forward
      // chevrons for interceptors. It communicates function without labels.
      const guard = d.role === '방어' || (d.role == null && d.kind === 'seori');
      const runeY = foot + ry * 0.22;
      u.lineStyle(Math.max(1, A * 0.75), 0xdff0ff, 0.30 * breathe);
      if (guard) {
        u.lineBetween(d.x - 3 * A, runeY - A, d.x, runeY + 2 * A);
        u.lineBetween(d.x, runeY + 2 * A, d.x + 3 * A, runeY - A);
      } else {
        const dir = (d._artVx ?? 1) < 0 ? -1 : 1;
        for (const off of [-2, 2]) {
          u.lineBetween(d.x + (off - 2 * dir) * A, runeY - 2 * A,
                        d.x + (off + 1 * dir) * A, runeY);
          u.lineBetween(d.x + (off + 1 * dir) * A, runeY,
                        d.x + (off - 2 * dir) * A, runeY + 2 * A);
        }
      }

      // ── 물 때: 무는 쪽으로 펴지는 호 ─────────────────────
      const bite = d.biteAt == null ? -9 : t - d.biteAt;
      if (bite < 0 || bite > DOGFX.hold) continue;
      const k = 1 - bite / DOGFX.hold;              // 1 → 0
      const ang = Math.atan2(d.biteDy ?? 0, d.biteDx ?? 1);
      const R = DOGFX.reach * UNIT * A * this.dogMul * (0.72 + 0.28 * (1 - k));
      // 호가 **열리면서 옅어진다** — 「무는 순간」이 앞에 오고 여운이 뒤에 온다.
      //   겹 둘: 바깥은 넓고 흐리게(빛), 안쪽은 좁고 진하게(이빨 자국).
      const a0 = ang - DOGFX.arc / 2, a1 = ang + DOGFX.arc / 2;
      // Two lean motion streaks start behind the shoulder so the attack reads
      // as a committed lunge rather than a detached slash graphic.
      const nx = -Math.sin(ang), ny = Math.cos(ang);
      f.lineStyle(Math.max(1, A * 0.8), DOGFX.ally, 0.45 * k);
      for (const side of [-1, 1]) {
        f.lineBetween(d.x - Math.cos(ang) * 13 * A + nx * side * 4 * A,
                      d.y - Math.sin(ang) * 13 * A + ny * side * 4 * A,
                      d.x - Math.cos(ang) * 3 * A + nx * side * 3 * A,
                      d.y - Math.sin(ang) * 3 * A + ny * side * 3 * A);
      }
      f.lineStyle(Math.max(2, DOGFX.edge * 2.2 * k * A), DOGFX.ally, 0.30 * k);
      f.beginPath(); f.arc(d.x, d.y, R, a0, a1, false); f.strokePath();
      f.lineStyle(Math.max(1.5, DOGFX.edge * k * A), 0xdff0ff, 0.95 * k);
      f.beginPath(); f.arc(d.x, d.y, R, a0, a1, false); f.strokePath();
      // 이빨 셋 — 호를 가로지르는 짧은 선. 「고리」가 아니라 **문 것**으로 읽히게 한다
      for (const s of [-1, 0, 1]) {
        const a2 = ang + s * DOGFX.arc * 0.30;
        f.lineStyle(Math.max(1.5, DOGFX.edge * 0.9 * k * A), 0xffffff, 0.85 * k);
        f.lineBetween(d.x + Math.cos(a2) * R * 0.72, d.y + Math.sin(a2) * R * 0.72,
                      d.x + Math.cos(a2) * R * 1.10, d.y + Math.sin(a2) * R * 1.10);
      }
      // Contact star lands at the bite edge.  Its short asymmetric rays keep
      // the visual attached to the victim-facing side of the dog.
      const ix = d.x + Math.cos(ang) * R, iy = d.y + Math.sin(ang) * R;
      f.fillStyle(0xffffff, 0.78 * k);
      f.fillCircle(ix, iy, Math.max(1.2, 1.9 * A * k));
      for (let s = -1; s <= 1; s++) {
        const sa = ang + s * 1.08;
        f.lineStyle(Math.max(1, A * 0.75), s === 0 ? 0xffffff : DOGFX.ally, 0.72 * k);
        f.lineBetween(ix + Math.cos(sa) * 3 * A, iy + Math.sin(sa) * 3 * A,
                      ix + Math.cos(sa) * (7 + Math.abs(s) * 2) * A,
                      iy + Math.sin(sa) * (7 + Math.abs(s) * 2) * A);
      }
    }
  }

  drawEagles() {
    const eagles = this.world.eagles ?? [];
    for (let i = 0; i < eagles.length; i++) {
      const eagle = eagles[i];
      const altitude = Math.max(0, Math.min(1, eagle.altitude ?? 1));
      const shadowFrame = altitude > 2 / 3 ? 0 : altitude > 1 / 3 ? 1 : 2;
      this.pooledSprite(this.eagleShadowPool, i, 'eagle-shadow', 19, 68 / 96)
        .setFrame(shadowFrame)
        .setPosition(eagle.x, eagle.y)
        .setVisible(true);

      // The 1.5s World-owned low state is split visually in half: prepare then dive.
      const groundedElapsed = Number.isFinite(eagle.impactAt)
        ? Math.max(0, this.world.t - eagle.impactAt)
        : Math.max(0, Number(eagle.stateT) || 0);
      const column = eagle.state === 'low'
        ? ((Number(eagle.stateT) || 0) < EAGLE_LOW_PREPARE_HOLD ? 1 : 2)
        : eagle.state === 'grounded' ? (groundedElapsed < EAGLE_IMPACT_HOLD ? 3 : 4)
        : eagle.state === 'takeoff' ? 5
        : 0;
      const row = eagle.facing < 0 ? 1 : 0;
      const body = this.pooledSprite(
        this.eagleBodyPool, i, EAGLE_COMBAT_KEY, 20, EAGLE_BODY_ANCHOR_Y,
      )
        .setOrigin(0.5, EAGLE_BODY_ANCHOR_Y)
        .setFrame(row * 6 + column)
        .setPosition(eagle.x, eagle.y - altitude * EAGLE_CELL)
        .setScale(this.artScale)
        .setVisible(true);
      const hitAge = this.world.t - (eagle.hitT ?? -9);
      if (hitAge >= 0 && hitAge < 0.09) {
        body.setTint(0xffffff).setTintMode(Phaser.TintModes.FILL);
      } else {
        body.clearTint().setTintMode(Phaser.TintModes.MULTIPLY);
      }
    }
    this.hidePoolTail(this.eagleShadowPool, eagles.length);
    this.hidePoolTail(this.eagleBodyPool, eagles.length);

    const preview = EAGLE_PREVIEW;
    const previewAge = this.world.t - preview.start;
    const previewActive = this.nightKey === preview.night
      && previewAge >= 0 && previewAge < preview.duration;
    if (!previewActive) {
      this.eaglePreviewSprite.setVisible(false);
      return;
    }
    const u = previewAge / preview.duration;
    this.eaglePreviewSprite
      .setFrame(preview.frame)
      .setPosition(preview.x0 + (preview.x1 - preview.x0) * u, preview.y)
      .setTint(preview.tint)
      .setAlpha(preview.alpha)
      .setVisible(true);
  }

  // ══════════════════════════════════════════════════════════════
  //  【7-2 / 0-E】 구출 밤 — 필드 3배 · 카메라 추적 · 방향 패널 · 양
  //  명세 6-3이 정본이고, 명세 11장이 0-E의 검증 대상으로 지정한 항목이다.
  //  로직(6:40 스폰 · 패널 · 10:00 자동 발견 · 접촉 회수)은 **전부 world.js 에 이미 있다.**
  //  여기 있는 것은 그것을 화면에 내놓는 일뿐이다.
  // ══════════════════════════════════════════════════════════════
  // ══════════════════════════════════════════════════════════════
  //  **마지막 밤의 끝 — 황금 화살표와 모여 있는 양**  【사장님 지시 2026-08-03】
  //    *"빈 들판에서 황금색 화살표가 나오는거지. 거기로 이동하다 보면 … 그동안
  //      잃어버린 양들이 모여있는 방식"*
  //
  //  ⚠ **자리와 방향은 월드가 든다**(`world.finale`). 여기는 그리기만 한다 —
  //    화면이 좌표를 계산하기 시작하면 규칙이 두 곳에 살게 된다.
  //  ⚠ 양은 **이미 있는 `lost-sheep` 스프라이트**를 쓴다. 새 그림을 안 만든다.
  //  ⚠ 화살표는 화면 고정 렌더다. 위치·방향·도착 판정은 여전히 월드만 소유한다.
  // ══════════════════════════════════════════════════════════════
  drawFinale() {
    const f = this.world.finale;
    const q = this.gPanel;
    if (!f) {
      this._finaleSheep?.forEach((o) => o.setVisible(false));
      this._finaleSheepPose = [];
      this._finaleDarkOverlay?.setVisible(false);
      this._finaleArrowPresence = 0;
      this._finaleReunionLight = 0;
      return;
    }
    const t = f.t;
    const state = f.state ?? (f.arrived ? 'sheepApproach' : 'walk');
    const sheepApproaching = state === 'sheepApproach';
    const reunionHolding = state === 'reunionHold';
    const dt = Math.max(0, Math.min(0.05, this._dtReal ?? 1 / 60));
    // 수색에는 고정 목적지가 없다. 월드가 명시적으로 표식을 줄 때만 화살표를 그린다.
    const arrowTarget = state === 'walk' && f.arrow ? 1 : 0;
    const arrowEase = 1 - Math.exp(-(arrowTarget ? 3.2 : 2.25) * dt);
    this._finaleArrowPresence += (arrowTarget - this._finaleArrowPresence) * arrowEase;
    const reunionTarget = reunionHolding ? 1 : sheepApproaching ? 0.18 : 0;
    const reunionEase = 1 - Math.exp(-(reunionTarget > this._finaleReunionLight ? 1.4 : 2.8) * dt);
    this._finaleReunionLight += (reunionTarget - this._finaleReunionLight) * reunionEase;

    // 암전은 카메라 이동에 흔들리지 않는 화면 좌표에서 그린다.
    const reveal = smooth01(t / 1.35);
    const cam = this.cameras.main;
    const angle = Math.atan2(f.dir[1], f.dir[0]);
    const playerScreenX = W / 2 + (this.world.player.x - cam.midPoint.x) * cam.zoom;
    const playerScreenY = H / 2 + (this.world.player.y - cam.midPoint.y) * cam.zoom;
    this.drawFinaleDarkness(reveal, playerScreenX, playerScreenY, this._finaleReunionLight);
    const arrowAlpha = smooth01((t - 0.28) / 0.85) * this._finaleArrowPresence;
    if (f.arrow && arrowAlpha > 0.004) {
      const ax = W / 2 + (f.arrow.x - cam.midPoint.x) * cam.zoom;
      const ay = H / 2 + (f.arrow.y - cam.midPoint.y) * cam.zoom;
      this.drawFinaleArrow(q, ax, ay, angle, t, arrowAlpha);
    }

    // ── 모여 있는 양 ─────────────────────────────────────────
    // **표식을 안 얹는다** — 구출 밤 주석과 같은 이유다. 목표물이 아니라 만남이다.
    while (this._finaleSheep.length < f.flock.length) {
      this._finaleSheep.push(this.add.image(0, 0, 'lost-sheep').setOrigin(0.5, 0.62).setDepth(21));
    }
    this._finaleSheep.forEach((o, i) => {
      const m = f.flock[i];
      if (!m) { o.setVisible(false); return; }
      const pose = this._finaleSheepPose[i] ??= {
        x: m.x, y: m.y, phase: i * 1.41, flipX: i % 2 === 1,
      };
      const dx = m.x - pose.x, dy = m.y - pose.y;
      const travel = Math.hypot(dx, dy);
      const speed = travel / Math.max(dt, 1 / 120);
      const walking = sheepApproaching && travel > 0.025;
      if (walking) {
        pose.phase += Math.min(8, travel) * 0.32;
        if (Math.abs(dx) > Math.max(0.025, Math.abs(dy) * 0.18)) pose.flipX = dx < 0;
      }
      pose.x = m.x; pose.y = m.y;

      const baseScale = (14 * 2.2) / 34 * this.artScale;
      const walkWeight = walking ? clamp01(speed / 34) : 0;
      const bob = -Math.abs(Math.sin(pose.phase)) * 1.7 * walkWeight;
      const stride = Math.sin(pose.phase * 2) * 0.016 * walkWeight;
      const breathe = reunionHolding ? 1 + Math.sin(t * 2.1 + i * 0.7) * 0.018 : 1;
      o.setVisible(true).setPosition(m.x, m.y + bob)
        .setScale(baseScale * breathe * (1 + stride), baseScale * breathe * (1 - stride * 0.7))
        .setFlipX(pose.flipX);
    });
    if (this._finaleReunionLight > 0.025) {
      this.drawFinaleReunion(q, playerScreenX, playerScreenY, t, this._finaleReunionLight);
    }
  }

  ensureFinaleDarkOverlay() {
    if (this._finaleDarkOverlay?.scene) return;
    const key = '__finale-cinematic-darkness';
    if (!this.textures.exists(key)) this.textures.createCanvas(key, W, H);
    this._finaleDarkTexture = this.textures.get(key);
    this._finaleDarkOverlay = this.add.image(W / 2, H / 2, key)
      .setOrigin(0.5).setDepth(94).setScrollFactor(0).setVisible(false);
    this._finaleDarkStamp = '';
  }

  drawFinaleDarkness(reveal, playerX, playerY, reunionLight = 0) {
    this.ensureFinaleDarkOverlay();
    const lightStep = Math.round(clamp01(reunionLight) * 48);
    const stamp = `${Math.round(playerX)}:${Math.round(playerY)}:${lightStep}`;
    if (stamp !== this._finaleDarkStamp) {
      const tex = this._finaleDarkTexture;
      const ctx = tex.getContext();
      const reunion = lightStep / 48;
      // One David-centred circular gradient: no corridor, ellipse, hard rim,
      // concentric decoration, or gold outline.
      const radius = 330 + reunion * 92;
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(0,0,0,0.988)'; ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'destination-out';
      const light = ctx.createRadialGradient(playerX, playerY, 0, playerX, playerY, radius);
      light.addColorStop(0, 'rgba(0,0,0,0.985)');
      light.addColorStop(0.30, 'rgba(0,0,0,0.965)');
      light.addColorStop(0.58, 'rgba(0,0,0,0.79)');
      light.addColorStop(0.80, 'rgba(0,0,0,0.35)');
      light.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = light;
      ctx.fillRect(playerX - radius, playerY - radius, radius * 2, radius * 2);
      ctx.globalCompositeOperation = 'source-over';
      tex.refresh();
      this._finaleDarkStamp = stamp;
    }
    this._finaleDarkOverlay.setVisible(reveal > 0).setAlpha(reveal);
  }

  drawFinaleArrow(g, cx, cy, angle, t, alpha) {
    if (alpha <= 0) return;
    const ux = Math.cos(angle), uy = Math.sin(angle);
    const nx = -uy, ny = ux;
    const pt = (along, across = 0) => ({
      x: cx + ux * along + nx * across,
      y: cy + uy * along + ny * across,
    });
    const polygon = (points, color, localAlpha) => {
      g.fillStyle(color, localAlpha * alpha);
      g.fillPoints(points.map(([along, across]) => pt(along, across)), true);
    };
    const shimmer = 0.94 + 0.06 * Math.sin(t * 1.65);

    // A restrained 132px way-marker: one forged shaft, one notched spearhead,
    // and a single pair of short feathers. No floor bloom, trails, or motes.
    const spineA = pt(-70, 0), spineB = pt(22, 0);
    g.lineStyle(6, 0x3b250e, 0.86 * alpha);
    g.lineBetween(spineA.x, spineA.y, spineB.x, spineB.y);
    g.lineStyle(3, 0xd7a33b, shimmer * alpha);
    g.lineBetween(spineA.x, spineA.y, spineB.x, spineB.y);

    // The concave heel and two restrained facets read as a forged spear point,
    // not the stock shaft-plus-triangle icon used elsewhere in the HUD.
    polygon([[15, -11], [31, -7], [62, 0], [31, 7], [15, 11], [23, 0]],
      0x4b2d0d, 0.96);
    polygon([[22, -6], [34, -4], [56, 0], [34, 4], [22, 6], [28, 0]],
      0xd9a33a, shimmer);
    const facetA = pt(30, -3), facetB = pt(53, 0);
    g.lineStyle(1.25, 0xffe6a0, 0.58 * alpha * shimmer);
    g.lineBetween(facetA.x, facetA.y, facetB.x, facetB.y);

    const featherRoot = pt(-51, 0);
    const featherTop = pt(-69, -6);
    const featherBottom = pt(-69, 6);
    g.lineStyle(2, 0xc78e2d, 0.72 * alpha * shimmer);
    g.lineBetween(featherTop.x, featherTop.y, featherRoot.x, featherRoot.y);
    g.lineBetween(featherBottom.x, featherBottom.y, featherRoot.x, featherRoot.y);
  }

  drawFinaleReunion(g, cx, cy, t, strength) {
    const a = clamp01(strength);
    // Sparse deterministic motes rise inside the widening circular light. The
    // reunion stays quiet instead of turning into a reward explosion.
    for (let i = 0; i < 11; i++) {
      const cycle = (t * (0.075 + i * 0.003) + i * 0.137) % 1;
      const angle = i * 2.399963229728653 + Math.sin(t * 0.19 + i) * 0.10;
      const radius = 48 + cycle * 156;
      const x = cx + Math.cos(angle) * radius;
      const y = cy + Math.sin(angle) * radius * 0.76 - cycle * 28;
      const fade = Math.sin(Math.PI * cycle) * a;
      const s = i % 3 === 0 ? 3.5 : 2.25;
      g.fillStyle(i % 4 === 0 ? 0xffefb0 : 0xd9a441, fade * 0.38);
      g.fillPoints([
        { x, y: y - s }, { x: x + s, y }, { x, y: y + s }, { x: x - s, y },
      ], true);
    }
  }

  drawRescue(p) {
    const w = this.world, s = this.gSheep, q = this.gPanel;
    s.clear(); q.clear();
    // ⚠ **화살표 구간은 구출 밤과 같은 그래픽스를 쓴다** — `clear()` 뒤에 그려야
    //   지워지지 않는다. 순서가 뒤바뀌면 화살표가 매 프레임 사라진다.
    if (w.finale) { this.drawFinale(); return; }

    // 숨소리(큐 7)의 시각 이중화 — 화면 가장자리 어두워짐.
    // ⚠ **충돌**: 디자인방향 10장은 이것을 *"기본 탑재"* 라 하고, 이 파일 34~39행의
    //   G10은 *"비네트를 만들지 않는다"* 고 못박는다. 둘 다 정본이라 내가 고를 수 없다.
    //   → **잠긴 쪽(G10)을 기본으로 두고**, 접근성 토글에서만 켠다. 보고 항목.
    if (this.audio.a11y) {
      const hpr = Math.max(0, p.hp) / w.maxHp;
      if (hpr < 0.5) {
        const k = (0.5 - hpr) / 0.5;
        q.fillStyle(0x000000, 0.35 * k);
        for (let i = 0; i < 6; i++) {          // 가장자리로 갈수록 짙어지는 띠 6겹
          const t = i / 6;
          q.fillRect(0, 0, W, 60 * (1 - t)); q.fillRect(0, H - 60 * (1 - t), W, 60 * (1 - t));
          q.fillRect(0, 0, 60 * (1 - t), H); q.fillRect(W - 60 * (1 - t), 0, 60 * (1 - t), H);
        }
      }
    }
    if (!this.rescue) return;

    // ⚠ 【벽 제거 2026-08-02】 여기 **필드 경계선**을 그리고 있었다 — *"어디가 끝인지
    //   보이지 않고 벽에 몰리는 것이 이 밤의 사고다"* 라는 이유였다. **그 벽이 없어졌다.**
    //   없는 선을 그리면 사람이 「여기가 끝」이라고 읽고 그 앞에서 멈춘다 — 있지도 않은
    //   벽을 화면이 만들어 내는 셈이다. 그래서 지운다.
    //   ⚠ 대신 **어디가 집인지**는 여전히 정보다(물·올무가 거기 있다). 그 표시는
    //     아직 없다 — 정본 미기재라 지어내지 않는다. 사장님 결정 대기.

    // ── 잃은 양 ─────────────────────────────────────────────
    // 6:40에 놓인다. 구 구현은 **흰 타원**이었다(아트 대기) — 이제 스프라이트다.
    //   **표식을 얹지 않는다.** 화살표·느낌표·후광을 붙이면 그 순간 이 양이
    //   「목표물」이 되고, 이 게임이 하려던 말(*"건져냈다"*)이 *"수거했다"*가 된다.
    //   숨 쉬듯 옅은 고리 하나만 남긴다 — **방향은 이미 패널이 준다**(6-3).
    if (w.sheep && !w.foundAt) {
      const r = w.sheep.r;
      s.lineStyle(1.5, 0xe8e2d4, 0.18 + 0.10 * Math.sin(w.t * 1.6));
      s.strokeCircle(w.sheep.x, w.sheep.y, r + 10);
      this.sheepSprite ??= this.add.image(0, 0, 'lost-sheep')
        .setOrigin(0.5, 0.62).setDepth(21);
      this.sheepSprite
        .setPosition(w.sheep.x, w.sheep.y)
        .setScale((r * 2.2) / 34 * this.artScale)   // 34px = 실측 몸 길이
        .setVisible(true);
    } else if (this.sheepSprite) {
      this.sheepSprite.setVisible(false);
    }

    // ── 방향 패널 — **화살표 하나. 숫자 없음. 거리는 진하기로만.**(명세 6-3 507행)
    if (w.panelOn && w.sheep && !w.foundAt) {
      const d = Math.hypot(w.sheep.x - p.x, w.sheep.y - p.y);
      const a = Math.atan2(w.sheep.y - p.y, w.sheep.x - p.x);
      // 진하기 ← 거리. **절대 거리로 잰다** — 시작 거리로 정규화하면 어느 판이든
      // 화살표가 같은 진하기로 시작해 「멀다」가 화면에서 사라진다.
      //   FAR = 화면 대각 ×1.5. **임의값 — 미검증**(0-E 측정 대상).
      const FAR = Math.hypot(1920, 1080) * 1.5;
      const k = Math.max(0, 1 - d / FAR);
      const al = 0.18 + 0.82 * k;
      const cx = W / 2, cy = 150;
      q.fillStyle(0x000000, 0.35 * al);
      q.fillRoundedRect(cx - 120, cy - 60, 240, 120, 12);
      arrow(q, cx, cy, a, 74, 0xe8e2d4, al);
      // 청각 이중화의 시각 대체(옵트인) — 「방향 파문」. 울음이 그친 2초는 파문도 멎는다.
      if (this.audio.showDual('lamb') && !this._lambHush) {
        const ph = (w.t * 0.9) % 1;
        q.lineStyle(2, 0xe8e2d4, al * (1 - ph) * 0.7);
        q.strokeCircle(cx, cy, 40 + ph * 70);
      }
    }
  }

  // ══════════════════════════════════════════════════════════════
  //  적 — **G10의 두 번째 계층.** 형체가 다 보인다(A8).
  //  스프라이트는 **렌더 전용**이다. 충돌·타격은 여전히 config 의 e.r 을 쓴다 —
  //  그래야 10-B 측정값이 아트 규칙에 흔들리지 않는다.
  // ══════════════════════════════════════════════════════════════
  // ── 보스 렌더 (6-2) ─────────────────────────────────────────
  // **상태/접촉 → 프레임 계약.** 곰 v2와 사자는 모두 6×2(행=우/좌)다.
  // 곰: 대기·보행 / 땅 긁기 / 돌진 / 앞발 충격 / 회수.
  // 사자: 보행 / 웅크림 / 도약 / 앞발 / 회수. 사자는 상태가 `active` 하나라
  // BossFSM.attackKind가 없던 구 판에서는 2~5열이 런타임에 영원히 닿지 않았다.
  BOSS_FRAME = Object.freeze({
    bear: Object.freeze({
      roam: Object.freeze([0]), walk: Object.freeze([0, 1, 2, 3]), tell: Object.freeze([1, 2]),
      dash: Object.freeze([2, 3]), charge: Object.freeze([2, 3]),
      maul: Object.freeze([1, 2, 3, 4]), rampage: Object.freeze([1, 2, 3, 4, 3, 4]),
      impact: Object.freeze([4]), window: Object.freeze([5]),
    }),
    lion: Object.freeze({
      active: Object.freeze([0]), walk: Object.freeze([0, 1, 2, 3]),
      tell: Object.freeze([1, 2]),
      pounce: Object.freeze([1, 2, 3, 4]),
      rake: Object.freeze([1, 2, 4]), frenzy: Object.freeze([1, 2, 3, 4, 2, 3, 4, 5]),
      claw: Object.freeze([1, 2, 4]), dash: Object.freeze([2, 3]),
      window: Object.freeze([5]),
    }),
  });

  drawBoss(g, b, p) {
    if (this.world.finale) {
      this.bossSprite?.setVisible(false);
      return;
    }
    if (b.dead) { this.bossSprite?.setVisible(false); return; }
    const attackGeometry = resolveBossAttackGeometry(b, this._bossGeometryScratch);
    const geometryVisible = attackGeometry.visible === true;
    const geometryStage = geometryVisible ? attackGeometry.stage : null;
    const groundedPursuit = b.state === (b.kind === 'lion' ? 'active' : 'roam');
    // signal active=false는 충돌이 아직 없다는 뜻이지 보행하라는 뜻이 아니다. 실제
    // 기하가 보이는 동안에는 전용 6열 시트로 고정해 발 미끄러짐을 막는다.
    const walking = groundedPursuit && b.moving === true && !b.attackActive && !geometryVisible;
    const key = b.kind === 'lion'
      ? (walking ? 'boss-lion-walk' : 'boss-lion')
      : (walking ? 'boss-bear-walk' : 'boss-bear');
    if (!this.bossSprite) {
      this.bossSprite = this.add.sprite(0, 0, key).setOrigin(0.5, 0.75).setDepth(22);
      this.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST);
    } else if (this.bossSprite.texture?.key !== key) {
      // 예고전과 본편이 다른 종이어도 이전 씬의 텍스처를 붙들지 않는다.
      this.bossSprite.setTexture(key);
      this.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST);
    }
    const poseU = geometryVisible ? clamp01(attackGeometry.progress)
      : b.attackActive ? clamp01(b.attackProgress) : 0;
    const attackU = geometryStage === 'commit' || (!geometryVisible && b.attackActive) ? poseU : 0;
    const phase = Math.max(1, Math.min(BOSS_PHASES, Number(b.phase) || 1));
    const table = this.BOSS_FRAME[b.kind] ?? this.BOSS_FRAME.bear;
    const fallbackAttack = b.kind === 'lion' ? 'claw' : 'charge';
    const motion = geometryStage === 'signal' ? 'tell'
      : geometryStage === 'recovery' ? 'window'
        : (geometryStage === 'commit' || b.attackActive)
          ? (table[b.attackKind] ? b.attackKind : fallbackAttack)
          : (walking ? 'walk' : b.state);
    const seq = table[motion] ?? table[b.state] ?? table.roam ?? table.active;
    // Dedicated 4-frame contact/pass/contact/pass sheets are driven by real
    // ground distance so slow bear and fast lion steps never slide in place.
    const walkStride = Math.max(1, b.cfg.r * (b.kind === 'lion' ? 1.45 : 1.05));
    const gaitU = walking ? ((Number(b.walkDistance) || 0) / walkStride) % 1 : 0;
    const stagedPose = geometryVisible || b.attackActive;
    const col = stagedPose
      ? seq[Math.min(seq.length - 1, Math.floor(Math.min(0.999, poseU) * seq.length))]
      : walking
        ? seq[Math.floor(gaitU * seq.length) % seq.length]
      : seq[Math.floor(this._artClock * 4) % seq.length];
    const row = b.fx >= 0 ? 0 : 1;                    // 행 = 바라보는 좌/우
    const source = b.kind === 'lion' ? 256 : 128;
    // 히트 반지름의 지름을 셀 크기에 맞춘다. 곰(128)과 사자(256)가 같은 판정 크기인데
    // 구 판은 scale=1이라 곰만 절반 크기로 보였다. 이것도 렌더 크기뿐이다.
    // Match visible alpha occupancy, not the padded source cell. Keep both bosses
    // close to their 192px collision diameter: imposing, but not screen-filling.
    const bossVisualScale = b.kind === 'lion' ? 1.40 : 1.22;
    const baseScale = (b.cfg.r * 2) / source * bossVisualScale;
    const pulse = attackU > 0 ? Math.sin(Math.PI * attackU) : 0;
    const lionAttack = b.kind === 'lion'
      && (geometryStage === 'commit' || (!geometryVisible && b.attackActive));
    const lionBeat = lionAttack && phase >= 3
      ? Math.sin(Math.PI * ((attackU * 2) % 1))
      : pulse;
    const lionLunge = lionAttack ? Math.max(0, lionBeat) : 0;
    const bearCharge = b.kind === 'bear'
      && (geometryStage === 'commit' || (!geometryVisible && b.attackActive))
      && (b.state === 'dash' || ['charge', 'rampage'].includes(b.attackKind)) ? 1 : 0;
    const bearImpact = b.kind === 'bear' && ['impact', 'maul'].includes(b.attackKind)
      && (geometryStage === 'commit' || (!geometryVisible && b.attackActive)) ? pulse : 0;
    const signalCoil = geometryStage === 'signal' ? poseU : 0;
    const recoveryBeat = geometryStage === 'recovery' ? Math.sin(Math.PI * poseU) : 0;
    const lateral = lionAttack && phase >= 2 ? Math.sin(attackU * Math.PI * 2) * 5 * phase : 0;
    const forward = (lionLunge * (16 + phase * 5) - bearImpact * 5
      - signalCoil * (b.kind === 'lion' ? 7 : 4) - recoveryBeat * 6) * this.artScale;
    const footLift = walking ? Math.abs(Math.sin(gaitU * Math.PI * 2)) : 0;
    const walkBob = walking ? -Math.round(footLift * (b.kind === 'lion' ? 2 : 1) * this.artScale) : 0;
    const stretchX = (1 + lionLunge * (0.08 + phase * 0.018) + bearCharge * 0.06
      - bearImpact * 0.04 + signalCoil * 0.07 - recoveryBeat * 0.04)
      * (1 + footLift * 0.012);
    const stretchY = (1 - lionLunge * (0.055 + phase * 0.012) - bearCharge * 0.05
      + bearImpact * 0.08 - signalCoil * 0.10 + recoveryBeat * 0.06)
      * (1 - footLift * 0.016);
    const tint = phase === 3 ? 0xffb18a : phase === 2 ? 0xffddbd : 0xffffff;
    this.bossSprite
      .setPosition(b.x + b.fx * forward - b.fy * lateral,
                   b.y + b.fy * forward + b.fx * lateral + walkBob)
      .setFrame(row * (walking ? 4 : 6) + col)
      .setScale(baseScale * stretchX, baseScale * stretchY)
      .setTint(tint)
      .setDepth(22 + b.y / 10000)
      .setVisible(true);

    if (geometryVisible) this.drawBossAttackGeometry(g, attackGeometry, b);
    if (b.windowOpen) {
      // ── 급소 창 ─────────────────────────────────────────────
      // *"급소는 조준이 아니라 위치다."* 그래서 **「지금 눌러라」가 아니라 「거기 서라」**를
      // 그린다 — 바라보는 방향의 **반대 반구**를 띄운다. 플레이어가 그 안에 서 있으면
      // 진해진다. 버튼이 없으므로(2-1) 신호도 자리로만 말해야 한다.
      const back = Math.atan2(-b.fy, -b.fx), half = b.cfg.critArc / 2;
      const inside = b.isBehind(p.x, p.y);
      g.fillStyle(0x7ee081, inside ? 0.22 : 0.10);
      g.slice(b.x, b.y, b.cfg.r * 2.6, back - half, back + half, false);
      g.fillPath();
      g.lineStyle(inside ? 3 : 1.5, 0x7ee081, inside ? 0.9 : 0.45);
      g.strokeCircle(b.x, b.y, b.cfg.r + 4);
    }
  }

  // BossFSM owns every coordinate. The renderer draws only physical traces of
  // the supplied body, paw and claw paths: no replacement ring, cone, target
  // bracket or inferred endpoint is allowed here.
  drawBossAttackGeometry(g, a, b) {
    const progress = clamp01(a.progress);
    const commit = a.stage === 'commit';
    // Signal is carried by the boss's authored body pose and audio. Drawing
    // locked route/limb coordinates here would expose a future developer path;
    // recovery likewise has no live physical contact to render.
    if (!commit) return;
    const phase = Math.max(1, Math.min(BOSS_PHASES, Number(b.phase) || 1));
    const color = phase >= 3 ? 0xe87948 : phase === 2 ? 0xe0a056 : 0xd5b06c;
    const stageA = 0.88;

    const line = (x0, y0, x1, y1, width, alpha = stageA) => {
      g.lineStyle(width + 5, 0x080504, alpha * 0.42);
      g.lineBetween(x0, y0, x1, y1);
      g.lineStyle(width, color, alpha);
      g.lineBetween(x0, y0, x1, y1);
    };
    const arc = (x, y, radius, start, end, width, alpha) => {
      if (!(radius > 0)) return;
      g.lineStyle(width + 5, 0x080504, alpha * 0.42);
      g.beginPath();
      g.arc(x, y, radius, start, end, false);
      g.strokePath();
      g.lineStyle(width, color, alpha);
      g.beginPath();
      g.arc(x, y, radius, start, end, false);
      g.strokePath();
    };
    // A pair of broken parallel ground tracks follows only ground already
    // crossed by the live body. The current segment ends at the boss's actual
    // simulation position; locked future segments never reach Graphics.
    const track = (from, to, current) => {
      const dx = to.x - from.x, dy = to.y - from.y;
      const distance = Math.hypot(dx, dy);
      if (distance <= 1e-6) return;
      const ux = dx / distance, uy = dy / distance;
      const nx = -uy, ny = ux;
      const alpha = current ? 0.86 : 0.20;
      const offset = Math.max(5, (a.bodyRadius || b.cfg.r) * 0.18);
      const dashN = 4;
      for (let dash = 0; dash < dashN; dash++) {
        const u0 = dash / dashN;
        const u1 = Math.min(1, u0 + 0.14);
        for (const side of [-1, 1]) {
          line(from.x + dx * u0 + nx * offset * side,
            from.y + dy * u0 + ny * offset * side,
            from.x + dx * u1 + nx * offset * side,
            from.y + dy * u1 + ny * offset * side,
            current ? 3.2 : 1.7, alpha);
        }
      }
    };

    const segmentN = Math.max(0, a.points.length - 1);
    const currentSegment = Math.max(0,
      Math.min(segmentN - 1, Math.floor(Number(a.hopIndex) || 0)));
    for (let i = 0; i < segmentN && i <= currentSegment; i++) {
      track(a.points[i], i === currentSegment ? b : a.points[i + 1],
        i === currentSegment);
    }

    const limbAlpha = (limb) => {
      const activeTo = Number.isFinite(Number(limb.activeTo))
        ? Number(limb.activeTo) : 1;
      const active = progress <= activeTo;
      return active ? 0.96 : 0.18;
    };
    const contactStarted = (limb) => {
      const activeFrom = Number.isFinite(Number(limb.activeFrom))
        ? Number(limb.activeFrom) : 0;
      return progress + 1e-9 >= activeFrom;
    };
    const facing = Math.atan2(a.facingY, a.facingX);

    // Paw contacts are two short leading-edge scuffs at the exact supplied
    // centres and radii. They never become a filled radial damage zone.
    for (const paw of a.paws) {
      if (!contactStarted(paw)) continue;
      const alpha = limbAlpha(paw);
      arc(paw.x, paw.y, paw.r, facing - 0.92, facing + 0.92,
        4.2, alpha);
      const ux = Math.cos(facing), uy = Math.sin(facing);
      const nx = -uy, ny = ux;
      for (const toe of [-0.34, 0.34]) {
        const sx = paw.x + nx * paw.r * toe;
        const sy = paw.y + ny * paw.r * toe;
        line(sx - ux * paw.r * 0.18, sy - uy * paw.r * 0.18,
          sx + ux * paw.r * 0.28, sy + uy * paw.r * 0.28,
          2.8, alpha * 0.88);
      }
    }

    // Claw capsules are already the real contact geometry. A dark shoulder and
    // a thin ivory cut expose those exact segments without drawing a fan.
    for (const claw of a.claws) {
      if (!contactStarted(claw)) continue;
      const alpha = limbAlpha(claw);
      line(claw.ax, claw.ay, claw.bx, claw.by,
        Math.max(3, claw.r * 0.12), alpha);
    }
  }

  drawEnemies(g, p) {
    const w = this.world, list = w.enemies, n = list.length;
    const sprite = this.renderMode === 'sprite';
    const pool = this.enemyPool, clock = this._artClock;

    for (let i = 0; i < n; i++) {
      const e = list[i];
      let R;
      if (sprite && e._sp === undefined) this.initEnemyArt(e);
      const behavior = resolveEnemyBehavior(e, this._enemyBehaviorScratch);
      const pose = enemyBehaviorPose(e, clock, this._enemyPoseScratch, behavior);

      if (sprite) {
        R = e.r;                       // 스프라이트 모드에서는 크기 지터를 안 쓴다(아래 주석)
        // ── 좌/우 = **이동 벡터** ────────────────────────────────
        // 부호만 보면 separate() 의 밀림에 1프레임 단위로 뒤집힌다. 그래서 가로 성분을
        // 지수 평활(α 0.15 ≈ 시정수 100ms)한 뒤 부호를 읽는다. **렌더 평활 계수이지
        // 게임 수치가 아니다** — world 는 이 값을 모른다.
        e._vx = e._vx * 0.85 + (e.x - e._px) * 0.15;
        e._px = e.x;
        if (e._vx > 0) e._fx = 0; else if (e._vx < 0) e._fx = 4;
        // Locked behavior direction outranks separation jitter while the body is
        // signaling/committing. Vertical routes keep the last stable side.
        if ((behavior.active || behavior.species === 'bat') && Math.abs(behavior.bodyDx) > 0.04) {
          e._fx = behavior.bodyDx >= 0 ? 0 : 4;
        }
        // ── 걷기 3프레임(1~3) · 대기(0) ────────────────────────
        // 대기 프레임이 뜨는 것은 **하이에나 돌진 예고로 멈춰 섰을 때뿐**이다.
        // 회색박스의 적은 그 순간 말고는 항상 움직인다(2-4 — 전부 플레이어에게 온다).
        // 그래서 예고가 「멈춤」으로도 읽힌다 — 6-1 신호가 한 겹 두꺼워진다.
        const motionFrame = e._sp === 'grasshopper'
          ? GRASSHOPPER_FRAME[e.motionState]
          : undefined;
        const fi = pose.attackFrame ?? motionFrame ?? (pose.frame == null
          ? (pose.idle ? 0 : 1 + (Math.floor(clock * e._fps + e._ph) % 3))
          : pose.frame);
        const img = i < pool.length ? pool[i] : this.growPool(i);
        const fn = pose.attackFrame == null
          ? e._fn[e._fx + fi]
          : e._afn[e._fx + fi];
        if (img._fn !== fn) { img.setFrame(fn); img._fn = fn; }
        const poseForward = Math.max(-ENEMY_POSE_FORWARD_MAX,
          Math.min(ENEMY_POSE_FORWARD_MAX, Number(pose.forward) || 0)) * this.enemyScale;
        img.x = e.x + behavior.bodyDx * poseForward;
        img.y = e.y + e._oy
          + behavior.bodyDy * poseForward
          - (e._sp === 'grasshopper' ? GRASSHOPPER.visualLift * grasshopperAirArc(e) : 0)
          - pose.lift * this.enemyScale;
        if (!img.visible) img.visible = true;
        // ── **맞은 적이 번쩍인다** ────────────────────────────
        // 플레이어 피격은 3프레임 모션이 있는데 **적 쪽은 통째로 비어 있었다.**
        // 초당 수십 번 일어나는 이 번쩍임이 뱀서류 화면 소음의 몸통이다 —
        // 없으면 「때렸는지」가 화면에 안 나온다.
        const hf = w.t - (e.hitT ?? -9);
        const hitPulse = hf >= 0 && hf < 0.13 ? Math.sin(hf / 0.13 * Math.PI) : 0;
        const scaleX = this.enemyScale * (1 + hitPulse * 0.14) * pose.scaleX;
        const scaleY = this.enemyScale * (1 - hitPulse * 0.09) * pose.scaleY;
        img.setScale(scaleX, scaleY).setRotation(pose.rotation);
        if (!SPECIES[e._sp].fly) {
          // Scale and rotation both pivot visually around the measured bottom
          // alpha point. This is the exact inverse transform of that point, so
          // a dramatic lean never makes a grounded enemy appear to slide off
          // its collision position. (The tiny authored fore/aft shift above is
          // the sole deliberate exception and is hard-capped.)
          const footLocal = FRAME_ALPHA_BOTTOM[e._sp] - 0.5 - SPECIES[e._sp].cell / 2;
          const sin = Math.sin(pose.rotation), cos = Math.cos(pose.rotation);
          img.x += footLocal * scaleY * sin;
          img.y += footLocal * (this.enemyScale - scaleY * cos);
        }
        if (hf >= 0 && hf < 0.09) {
          if (!img._lit) {
            img.setTint(0xffffff).setTintMode(Phaser.TintModes.FILL);
            img._lit = 1;
          }
        } else if (img._lit) {
          img.clearTint().setTintMode(Phaser.TintModes.MULTIPLY);
          img._lit = 0;
        }
      } else {
        // ── 구 회색박스(원) — 성능·가독성 대조군 ─────────────────
        if (e.vr === undefined) e.vr = e.r * (0.9 + 0.2 * ((Math.sin(e.x * 12.9898 + e.y * 78.233) * 43758.5453) % 1 + 1) / 2);
        R = e.vr;
        g.fillStyle(COL[e.kind] ?? COL.swarm, 1);
        g.fillCircle(e.x, e.y, R);
        g.fillStyle(0xff4a3a, 1);
        g.fillCircle(e.x - R * 0.3, e.y - R * 0.25, 1.8);
        g.fillCircle(e.x + R * 0.1, e.y - R * 0.25, 1.8);
        if (e.kind !== 'swarm') { g.lineStyle(1.5, 0xff6b5a, 0.5); g.strokeCircle(e.x, e.y, R + 3); }
      }
    }
    // 죽은 자리는 **숨길 뿐 없애지 않는다.** 밤 하나에 수천 마리가 나고 죽는다.
    if (sprite) {
      for (let i = n; i < this._poolUsed; i++) pool[i].visible = false;
      this._poolUsed = n;
    }
  }

  // 종 추첨용 결정적 난수(LCG). 같은 밤·같은 시드면 같은 그림이 나온다.
  artRand() {
    this._artRnd = (Math.imul(this._artRnd, 1664525) + 1013904223) >>> 0;
    return this._artRnd / 4294967296;
  }

  // 개체가 처음 화면에 나타날 때 **한 번만** 정해지는 것들. 이후 죽을 때까지 안 바뀐다.
  initEnemyArt(e) {
    // World owns gameplay species. Fall back only for legacy/fake actors that
    // do not provide an atlas-backed species. Always consume the legacy picker
    // draw so existing phase/FPS render RNG remains byte-for-byte ordered.
    const speciesU = this.artRand();
    const sp = this._frameNames[e.species]
      ? e.species
      : this.pickSpecies(e.kind, speciesU);
    e._sp = sp;
    e._oy = anchorOffsetY(sp);
    e._fn = this._frameNames[sp];
    e._afn = this._attackFrameNames[sp];
    // **위상을 개체마다 흩뿌린다** — 전부 같은 발이면 무리가 기계로 읽힌다.
    e._ph = this.artRand() * 3;
    // 개체별 속도 지터 ±10% — 구 회색박스의 크기 지터(±10%)와 같은 자리·같은 폭이다.
    //   ⚠ 크기 지터는 **스프라이트에서 폐기**했다: 0.9~1.1배 확대는 픽셀 격자를 깨고
    //     (아트 계약 *"hard pixel edges / no anti-aliasing"*), 인스턴싱의 목적인
    //     「개체가 서로 달라 보인다」는 이제 **종 2~4가지 × 위상 3 × 좌우 2**가 낸다.
    e._fps = this.walkFps[e.kind] * (0.9 + 0.2 * this.artRand());
    e._px = e.x;
    e._vx = this.world.player.x >= e.x ? 0.5 : -0.5;   // 첫 프레임은 플레이어 쪽을 본다
    e._fx = e._vx > 0 ? 0 : 4;
  }

  growPool(i) {
    const f0 = this._frameNames['field-rat'][0];
    while (this.enemyPool.length <= i) {
      const img = this.add.image(0, 0, ATLAS_KEY, f0)
        .setOrigin(0.5, 0.5).setDepth(20).setScale(this.enemyScale).setVisible(false);
      img._fn = f0;
      this.enemyPool.push(img);
    }
    return this.enemyPool[i];
  }

  drawPlayerHp(p) {
    const g = this.gPlayerHp;
    if (!g) return;
    g.clear();
    const state = playerHpBarState({
      hp: p.hp,
      maxHp: this.world.maxHp,
      shopOpen: this.shop.open,
      escPaused: this.escPaused,
      finale: !!this.world.finale,
      over: !!this.world.over,
    });
    if (!state.visible) {
      g.setVisible(false);
      return;
    }

    g.setVisible(true);
    const B = PLAYER_HP_BAR;
    const x = Math.round(p.x - B.width / 2);
    const y = Math.round(p.y - B.offsetY);
    // 숫자는 없다. 어두운 받침 + 즉시 HP 비율 하나로만 읽힌다.
    g.fillStyle(0x080707, 0.88);
    g.fillRoundedRect(x - 2, y - 2, B.width + 4, B.height + 4, B.radius + 1);
    g.fillStyle(0x30231a, 1);
    g.fillRect(x, y, B.width, B.height);
    if (state.ratio > 0) {
      const fillW = B.width * state.ratio;
      g.fillStyle(0xb45f4c, 1);
      g.fillRect(x, y, fillW, B.height);
      g.fillStyle(0xf1e6cf, 0.16);
      g.fillRect(x, y, fillW, 1);
    }
    g.lineStyle(1, 0xf1e6cf, 0.38);
    g.strokeRoundedRect(x - 2, y - 2, B.width + 4, B.height + 4, B.radius + 1);
  }

  drawPlayer(p, vis) {
    const finale = this.world.finale;
    const finalePlayerLocked = !!finale?.playerLocked;
    if (finalePlayerLocked) this._playerMoving = false;
    const hideBase = () => {
      this.playerWalk.setVisible(false);
      this.playerIdle?.setVisible(false);
    };
    this.playerHit.setVisible(false);
    this.playerDown.setVisible(false);

    // 다운은 월드 시간이 멈춘 뒤에도 실시간으로 끝까지 재생한다.
    if (this.world.over && this.world.result === '다운') {
      hideBase();
      const face = faceFromVector(p.fx, p.fy);
      const phase = this._downClock < 0.11 ? 0 : this._downClock < 0.25 ? 1 : 2;
      this.playerDown
        .setPosition(p.x, p.y)
        .setFrame(FACE_ROW[face] * 3 + phase)
        .setVisible(true);
      return;
    }

    // 피격 틴트 대신 몸이 실제로 밀리고 웅크리는 3프레임을 쓴다.
    const hitElapsed = p.inv > 0 ? PLAYER.iframe - p.inv : Infinity;
    if (!finale && hitElapsed >= 0 && hitElapsed < 0.31) {
      hideBase();
      const face = faceFromVector(p.fx, p.fy);
      const phase = hitElapsed < 0.09 ? 1 : hitElapsed < 0.19 ? 2 : 0;
      this.playerHit
        .setPosition(p.x, p.y)
        .setFrame(FACE_ROW[face] * 3 + phase)
        .setVisible(true);
      return;
    }

    if (finale) {
      this.playerIdle?.setVisible(false);
      // Use only the field walk body; the separate sling rig/orbit/cord is hidden
      // by hideFinaleWeaponVisuals. Avoiding the sling idle sheet also prevents a
      // combat-ready pose from leaking into this quiet search sequence.
      const face = slingFaceFromVector(p.fx, p.fy);
      const frame = finalePlayerLocked ? 0 : this._playerMoving
        ? Math.floor((this._walkClock ?? 0) * WALK_FPS) % 4
        : 0;
      this.playerWalk
        .setPosition(p.x, p.y)
        .setTint(0xffffff)
        .setFrame(SLING_FACE_ROW[face] * 4 + frame)
        .setVisible(true);
      return;
    }

    // 물맷돌 걷기 시트는 8방향이다. 상단 3방향은 후면, 좌우는 측면, 하단 3방향은 정면 계열.
    // 【2026-07-31】 지팡이 리그 렌더(스윙 위상·정면 v2·그립 피벗·깊이 교차)가 여기 있었다 — 무기 삭제.
    const { face, frame, idle } = this.slingPose(p);
    if (idle) {
      this.playerWalk.setVisible(false);
      this.playerIdle.setPosition(p.x, p.y).setTint(0xffffff)
        .setFrame(SLING_FACE_ROW[face] * this._idleFrameCount + frame).setVisible(true);
    } else {
      this.playerIdle?.setVisible(false);
      this.playerWalk.setPosition(p.x, p.y).setTint(0xffffff)
        .setFrame(SLING_FACE_ROW[face] * 4 + frame).setVisible(true);
    }
  }

  // ── 8-6a 이펙트 파라미터 계산 (측정 ⑦) ─────────────────────
  // 'soft' = f(x) = cap × tanh(x/cap) 점근 / 'linear' = 상한 없는 선형(대조군).
  // 둘을 한 화면에서 갈아 끼우고 육안으로 비교하는 것이 ⑦의 측정 방법이다.
  effect() {
    const w = this.world;
    // 【10-B 6-2】 **월드가 실제로 쓰는 스탯을 그린다.** 구 구현은 HUD 셀렉터(buildKey)의
    // 고정 빌드를 그려서, 밤 중에 실제로 세지는 것이 화면에 안 나타났다 —
    // 8-6a("스탯 창 없이 내 빌드가 캐릭터에 그대로 보인다")가 그 자리에서 깨져 있었다.
    // 실제 성장(ramp/max)은 월드 판정과 같은 스탯을 그대로 읽는다. 무강화 월드는
    // ⑦의 시각 대조군 버튼을 위해서만 buildKey 오버라이드를 허용한다.
    // 【2026-07-31】 구 `weaponStats` 우회(무강화 HUD 대조군)를 지웠다 — 파수는 gear 가
    //   `_ws` 를 **항상** 세우므로(refreshWeapon) 무강화 대조군 = 시작 장비 ●1 그 자체다.
    const st = w._ws;
    {
      const orbitLin = EFFECT.ORBIT_BASE * (st.range / PASU.range);
      const orbit = this.effectMode === 'linear' ? orbitLin : orbitRadius(st.range);
      const spinLin = 2 * Math.PI * st.rate * EFFECT.SPIN_REV_PER_SHOT;
      const spin = this.effectMode === 'linear' ? spinLin : spinRate(st.rate, orbit);
      // 멈춘 동안에는 돌도 안 돈다 — 전투 정지가 화면에서도 정지여야 한다
      const weaponFrozen = this._frozen || !!w.finale;
      this._phase = (this._phase ?? 0) + spin * (weaponFrozen ? 0 : (this._dtReal ?? 1 / 60));
      // 무릿매 위상 — **회전 속도가 걸이를 탄다**(2026-07-29).
      //   구 구현은 SLING.SPIN 고정이라 **걸이가 화면에서 죽어 있었다** — 네 부품 중
      //   하나가 시각적으로 아무 일도 안 했다. 「연사 = 회전 속도」는 물리적으로도 맞다.
      this._slingPhase = (this._slingPhase ?? 0)
        + SLING.SPIN * Math.max(0.35, this.world.mods?.spinMul ?? 1)
        * (weaponFrozen ? 0 : (this._dtReal ?? 1 / 60));
      return { orbit, spin, spinLin, orbitLin, phase: this._phase,
               stones: st.projectiles, stoneCapK: maxStones(orbit),
               range: st.range, rate: st.rate,
               tangential: spin * orbit, cap: spinCap(orbit) };
    }
  }

  // ── 성능 실측 표시 — **FPS 가 아니라 프레임 작업 시간(ms)이다.** ──
  // 0-A 1·2차 측정이 FPS로 실패했다(vsync 천장에서 12배 부하까지 같은 값). 그 결론을 승계한다.
  // ⚠ 여기서 work.stats() 를 부르지 않는다 — 그것은 표본 전체를 매번 정렬한다(밤 끝 43,200개).
  //   측정 도구가 측정 대상을 흔드는 것을 피한다. p95 는 리포트에서 한 번만 낸다.
  perfLine() {
    const P = this._perf;
    const hi = P.hiCnt ? P.hiSum / P.hiCnt : null;
    const cHi = P.hiCnt ? P.cHiSum / P.hiCnt : null;
    const all = P.allCnt ? P.allSum / P.allCnt : null;
    const cAll = P.allCnt ? P.cSum / P.allCnt : null;
    return `[${this.renderMode === 'sprite' ? '스프라이트' : '원(구)'}] `
      + `제출 ${cAll?.toFixed(2) ?? '…'}ms / 총작업 ${all?.toFixed(2) ?? '…'}ms`
      + `  · 상한근접(적≥${this._capHi | 0}) ${hi ? `제출 ${cHi.toFixed(2)} / 총 ${hi.toFixed(2)}ms ×${P.hiCnt}` : '미도달'}`
      + `  최악 ${P.cWorst.toFixed(2)}/${P.worst.toFixed(2)}`
      + `  / 60fps 예산 ${BUDGET_60.toFixed(1)}ms  (${this.work.mode})`;
  }
  // 그 밤에 실제로 화면에 있는 종 — 30밤시트 카드 「적 구성」이 정본이다.
  rosterLine() {
    if (this.renderMode !== 'sprite') return '(원 모드 — 종 없음)';
    const cnt = {};
    for (const e of this.world.enemies) if (e._sp) cnt[e._sp] = (cnt[e._sp] ?? 0) + 1;
    const parts = Object.entries(cnt).sort((a, b) => b[1] - a[1])
      .map(([k, v]) => `${SPECIES[k].ko} ${v}`);
    return `종 ${parts.join(' · ') || '-'}   ← 카드: ${this.speciesRoster?.card ?? '(없음)'}`;
  }

  // 상점이 그 밤에 실제로 무엇을 했나 — 전체 HUD 한 줄.
  // ⚠ 구 이름은 `카드 실측` 이었고 **폐기된 카드 중첩 모델**(`world.stack`)을 같이 찍었다.
  //   그 게터가 카드 체계와 함께 사라졌으므로 항을 지웠다 — 위 밤 끝 보고와 같은 이유다.
  cardLine() {
    const w = this.world, s = this.shop;
    if (!this.shopActive)
      return `상점 안 열림 — world 자동 구매`
        + `  ← ${this.cardMode !== 'real' ? '「자동 구매」 대조군' : `고정 빌드 ${w.build} 대조군`}`;
    return `부품 실측 — 상점 ${s.m.sessions}회 · 구매 ${s.m.buys}`
      + ` · 리롤 ${s.m.rerolls}(${s.m.rerollSpent}치즈)`
      + (s.m.underfilled ? `   ⚠3장 미달 ${s.m.underfilled}회(최소 ${s.m.minOffered})` : '');
  }
  // ── 돌 여섯의 축 — **지금 무엇이 돌고 있나** ──────────────────────
  // 계측 줄이다(전체 HUD 전용). **0 이 뜨는 축이 곧 끊긴 배선**이므로 화면에서 바로 잡힌다.
  gearAxisLine() {
    const w = this.world, M = w.mods || {}, m = w.m;
    const bit = [];
    if (M.pierceInf) bit.push(`무한관통 · 발간격×${(M.lineGap ?? 1).toFixed(2)}`
      + `(${M.lineGap === 0 ? '**붙는다**' : M.lineGap < 0.71 ? '선' : '개별 돌'})`);
    // ⚠⚠ **발당으로 나눈다.**   【검증 2026-07-30 — 단위가 섞여 있었다】
    //   `M.skips` 는 **발 하나의 예산**이고 `m.skips` 는 **밤 누적**이다. 전엔 그 둘을
    //   「→ 실제」로 나란히 놓아 `장비 튐 횟수 → 실제 누적 횟수`처럼 시간이 갈수록 오른쪽이
    //   무한히 커졌다 — 실현율로 읽히는 표기인데 실현율이 아니었다.
    //   `m.shots` 가 이미 있으므로 나누면 **같은 단위**가 된다.
    //   ⚠ 거리도 `/ 36` 이 아니라 `UNIT`(32)으로 나눈다 — 이 파일이 다른 줄에서 쓰는
    //     환산과 12.5% 어긋나 있었다(같은 파일이 `UNIT` 을 import 해 두고 있다).
    if (M.skips > 0) {
      const per = Math.max(1, m.shots);
      bit.push(`튐 예산 ${M.skips} → 발당 실제 ${(m.skips / per).toFixed(1)}`
        + ` · 발당 나아간 거리 ${(m.skipDist / UNIT / per).toFixed(1)}유닛`
        + ` (누적 자국 ${m.skips})`);
    }
    if (M.shardChain > 0) bit.push(`조각 연쇄 ${M.shardChain}대`);
    // ⚠ 위와 같은 이유 — 예약 횟수(발당)와 누적을 갈라 적는다
    if (M.splashAgain > 0) bit.push(`재폭발 예약 ${M.splashAgain}회 · 누적 ${m.blasts}회`);
    bit.push(`동시 발 ${w.shots.length}(최고 ${m.maxShots})`);
    // 세트 — **표시는 궤도 이펙트**이고 이 줄은 계측이다(둘이 같은 것을 말한다)
    const g = M._gear;
    const seen = M.setSeen?.n ? `${M.setSeen.ko} ${M.setSeen.n}/4` : '-';
    return `  [축] ${bit.join('  |  ')}\n`
      + `  [세트] ${g?.set ? `**${g.set.ko}(${g.set.level === 'large' ? '크게' : '작게'})** ${g.set.fx}`
                            + ` · 켠 시간 ${m.setSec.toFixed(0)}초` : `안 걸림 (${seen})`}`;
  }
  // 소리가 **실제로 울렸는지**를 화면에서 본다. 자리표시자 삑 소리라 귀로만 세면
  // 놓친다 — 「판정이 들리는가」를 재려면 발화 횟수가 눈에도 있어야 한다.
  soundLine() {
    const A = this.audio;
    if (!A.on) return `사운드 — 꺼짐${this.speed > 1 ? ' (배속에서는 자동으로 끈다)' : ''}`;
    const one = Object.entries(A.m.played).map(([k, v]) => `${CUES[k].n}${CUES[k].ko} ${v}`);
    const lp = Object.keys(A.loops).map(k => `${CUES[k].n}${CUES[k].ko}`);
    return `사운드 — 발화 ${one.join(' · ') || '-'}`
      + (A.m.dropped ? `  (겹침 버림 ${A.m.dropped})` : '')
      + `   지속 ${lp.join(' · ') || '-'}`
      + (A.a11y ? '   [청각 이중화 켬]' : '');
  }
  rescueLine() {
    const w = this.world, p = w.player;
    if (!w.sheep) return `구출 — 양은 ${RESCUE.spawnAt}초에 놓인다 (지금 ${w.t.toFixed(0)}초)`;
    const d = Math.hypot(w.sheep.x - p.x, w.sheep.y - p.y) / UNIT;
    return `구출 — 필드 ${w.W}×${w.H}(×${RESCUE.fieldMult}) · 패널 ${w.panelOn ? '켜짐' : '꺼짐'}`
      + ` · 양까지 ${d.toFixed(0)}유닛 (초기 ${(w.rescueDist0 / UNIT).toFixed(0)})`
      + (w.foundAt ? ` · **${w.foundBy} ${w.foundAt.toFixed(0)}초**` : '');
  }

  setBossHudVisible(visible) {
    this.bossHudTitle?.setVisible(visible);
    this.bossHudMeta?.setVisible(visible);
  }

  drawBossHud(g) {
    const w = this.world, b = w.boss;
    const downAge = b?.dead && Number.isFinite(w.bossDownAt) ? w.t - w.bossDownAt : 0;
    if (!b || (b.dead && downAge > 2.2) || w.finale) {
      this.setBossHudVisible(false);
      return;
    }
    const H0 = BOSS_HUD;
    const state = bossHudState(b);
    if (!state) { this.setBossHudVisible(false); return; }
    const fatigueY = H0.y + 39, attackY = H0.y + 57, barH = 10;
    g.fillStyle(0x080706, 0.90);
    g.fillRoundedRect(H0.x, H0.y, H0.w, H0.h, 7);
    g.lineStyle(1, 0x8e7047, 0.56);
    g.strokeRoundedRect(H0.x, H0.y, H0.w, H0.h, 7);
    // 위: 시간이 보스를 약화시키는 12칸. 아래: 차오를수록 위험한 다음 도약 게이지.
    g.fillStyle(0x251d16, 1); g.fillRect(H0.barX, fatigueY, H0.barW, barH);
    g.fillStyle(0xc19a55, 1); g.fillRect(H0.barX, fatigueY, H0.barW * state.fatigueRatio, barH);
    g.fillStyle(0xffedbf, 0.18); g.fillRect(H0.barX, fatigueY, H0.barW * state.fatigueRatio, 2);
    g.fillStyle(0x241713, 1); g.fillRect(H0.barX, attackY, H0.barW, barH);
    g.fillStyle(0x9f493b, 1); g.fillRect(H0.barX, attackY, H0.barW * state.attackRatio, barH);
    g.fillStyle(0xffe8bc, 0.15); g.fillRect(H0.barX, attackY, H0.barW * state.attackRatio, 2);
    this.bossHudTitle.setText(state.title).setVisible(true);
    this.bossHudMeta.setText(state.meta).setVisible(true);
  }

  // ── 계측 HUD 표시 단계 ──────────────────────────────────────────
  //   'full' 전부 · 'bars' 바만 · 'off' 없음.
  //   **바와 글은 다른 물건이다.** 안정성 바는 *"화면의 주인공"*(아래 주석)이고
  //   판돈이 보이는 자리다. 아래 글 뭉치는 계측이다 — 게임을 보려면 그쪽만 치우면 된다.
  drawHud() {
    const w = this.world;
    const mode = this.hudMode ?? 'full';
    // 타이머와 밤 이름은 HUD 단계와 별개인 화면 정보다. 먼저 상태를 복원해 두면
    // 상점·ESC 모달을 닫은 바로 그 프레임에 다시 나타난다.
    const left = Math.max(0, NIGHT_SEC - w.t);
    this.timeTxt
      .setText(`${(left / 60 | 0)}:${String(Math.floor(left % 60)).padStart(2, '0')}`)
      .setVisible(true);
    const titleAlpha = this._externalNightTitle
      ? 0 : Math.max(0, Math.min(1, (5.2 - w.t) / 2.4));
    this.qText.setVisible(titleAlpha > 0).setAlpha(titleAlpha);
    // 상점은 완전한 전용 화면이고 ESC 패널도 모달이다. 반투명 딤 뒤로 계기판과
    // qText 가 비치면 두 화면이 한꺼번에 읽힌다. 관련 HUD를 명시적으로 접는다.
    if (this.shop.open || this.escPaused || w.finale) {
      this.gBar.clear().setVisible(false);
      for (const t of [this.stabTxt, this.lossTxt, this.hudSheep, this.hud, this.timeTxt, this.qText]) {
        t.setVisible(false);
      }
      this.setBossHudVisible(false);
      return;
    }
    this.gBar.setVisible(true);

    if (mode === 'off') {
      this.gBar.clear();
      for (const t of [this.stabTxt, this.lossTxt, this.hudSheep, this.hud]) t.setVisible(false);
      this.setBossHudVisible(false);
      return;
    }
    const compact = mode !== 'full';
    this.stabTxt.setVisible(!compact);
    this.lossTxt.setVisible(true);
    this.hudSheep.setVisible(!compact);
    this.hud.setVisible(!compact);
    const m = w.m, p = w.player;
    // 실제 여정의 bars HUD는 안정도/예상 손실을 노출하지 않는다.
    // settle() 계측과 안정도 바는 개발자 full HUD에서만 살려 둔다.
    const st = compact ? null : w.settle(w.avgStability);
    const vis = this._vis ?? this.effect();

    const bx = compact ? 24 : 20;
    const by = compact ? 50 : 58;
    const bw = compact ? 420 : 620;
    const bh = compact ? 15 : 26;
    const g = this.gBar; g.clear();
    if (compact) {
      const C = COMPACT_STATUS_HUD;
      g.fillStyle(0x0d0b0a, 0.72);
      g.fillRoundedRect(C.x, C.y, C.w, C.h, C.radius);
      g.lineStyle(1, 0xd6c4a3, 0.24);
      g.strokeRoundedRect(C.x, C.y, C.w, C.h, C.radius);
      g.lineStyle(1, 0xf1e6cf, 0.05);
      g.lineBetween(C.x + 12, C.y + 1, C.x + C.w - 14, C.y + 1);
    } else {
      g.fillStyle(0x0d0b0a, 0.7); g.fillRoundedRect(bx - 7, by - 7, bw + 14, bh + 14, 5);
      g.lineStyle(1, 0xd6c4a3, 0.18); g.strokeRoundedRect(bx - 7, by - 7, bw + 14, bh + 14, 5);
      g.fillStyle(0x30231a, 1); g.fillRect(bx, by, bw, bh);
      const col = w.stability >= 75 ? 0x91b578 : w.stability >= 60 ? 0xe5bd68
                : w.stability >= 40 ? 0xd18a55 : 0xb45f4c;
      g.fillStyle(col, 1); g.fillRect(bx, by, bw * w.stability / 100, bh);
      g.fillStyle(0xf1e6cf, 0.10); g.fillRect(bx, by, bw * w.stability / 100, 2);
      // 정산표 구간 눈금 — full 계측에서만 현재 구간을 본다.
      g.lineStyle(1, 0xf1e6cf, 0.36);
      for (const r of LOSS_TABLE) if (r.min > 0) {
        const x = bx + bw * r.min / 100;
        g.lineBetween(x, by - 4, x, by + bh + 4);
      }
    }
    // HP 바(보조) — 최대치는 밤마다 주입될 수 있으므로 w.maxHp 기준
    const hpY = compact ? 45 : by + bh + 42;
    const levelY = compact ? 64 : by + bh + 58;
    g.fillStyle(0x30231a, 1); g.fillRect(bx, hpY, bw, compact ? 8 : 10);
    g.fillStyle(0xb45f4c, 1); g.fillRect(bx, hpY, bw * Math.max(0, p.hp) / w.maxHp, compact ? 8 : 10);
    // ══════════════════════════════════════════════════════════
    //  레벨 게이지 — **다 차면 진짜로 열려야 한다** 【사장님 지시 2026-08-02】
    //    *"진짜 다 찼는데 레벨업이 안떠서 개 배신감 들거든?"*
    //
    //  ⚠⚠ 분모가 `pieceReq(cfg, level)` 이었다 — **밤 내내 30 고정**이다.
    //    그런데 진짜 요구량(`levelNeed`)은 레벨마다 `growth 1.35` 로 복리로 붙는다.
    //    실측(N1·시드3·완주): HUD 가 「100%」라 부른 값 30 에 대해 진짜 필요량은
    //        ●1 149(×5) · ●5 493(×16) · ●9 1540(×51) · ●19 2635(×88)
    //    → **막대가 꽉 찬 채로 멈춰 있던 시간이 밤의 89%**(합계 585초 / 661초).
    //      레벨업 19번이 전부 「이미 꽉 찬 상태」에서 떴다. 막대가 거의 항상 거짓이었다.
    //
    //  **역산할 것이 없다.** 세계가 이미 정확한 값을 들고 있고, 발밑 링(1261행)은
    //  그것을 제대로 읽고 있었다 — HUD 막대만 다른 것을 보고 있었다(표가 둘).
    //  ⚠ 고친 뒤 막대는 **레벨마다 다른 속도로** 찬다. 초반엔 빨리, 후반엔 천천히 —
    //    그것이 실제 규칙이고 지금까지 화면이 그 사실을 통째로 가리고 있었다.
    // ══════════════════════════════════════════════════════════
    const need = w.levelNeed || 1;
    g.fillStyle(0x30231a, 1); g.fillRect(bx, levelY, bw, compact ? 6 : 8);
    g.fillStyle(0xe5bd68, 1); g.fillRect(bx, levelY, bw * Math.min(1, w.gauge / need), compact ? 6 : 8);
    this.drawBossHud(g);

    if (compact) {
      // ⚠ `w.cheese`(총 환전액)가 아닌 상점과 같은 실제 잔액 `w.wallet`을 본다.
      this.lossTxt.setPosition(bx, 15).setFontSize(18)
        .setText(`HP ${Math.max(0, p.hp | 0)}/${w.maxHp}   Lv ${w.level}   치즈 ${w.wallet}`);
    } else {
      this.stabTxt.setPosition(bx, 18).setFontSize(30)
        .setText(`양 떼 안정도 ${w.stability.toFixed(0)}${w.panic ? '  ⚠ 패닉' : ''}`);
      const lossY = 96;
      this.hudSheep.setPosition(bx, lossY + 11).setScale(0.62);
      this.lossTxt.setPosition(bx + 38, lossY).setFontSize(24)
        .setText(`현재 예상 손실 ${st.loss}  (×${st.mult} · ${st.band})`
          + `      HP ${Math.max(0, p.hp | 0)}/${w.maxHp}   Lv ${w.level}   치즈 ${w.wallet}`);
    }

    const kills = m.killsPlayer + m.killsDog;
    const wname = `물맷돌  사거리 ${(PASU.range / UNIT).toFixed(1)}유닛 · ${PASU.rate}발/초`;
    if (mode !== 'full') return;
    this.hud.setText([
      `${w.phase}   ${w.t.toFixed(0)}s / ${NIGHT_SEC}   ${this.speed > 1 ? `[×${this.speed} 배속]` : ''}`,
      `무기 ${wname}`,
      ``,
      `적 ${w.enemies.length}  (계획 ${m.spawned}/${w.plannedSpawnTotal})   ${this.perfLine()}`,
      `  ${this.rosterLine()}`,
      `안정성 반경 ${(w.stabRadius / UNIT).toFixed(0)}유닛 안의 적 ${w.inRad}`
        + `  → ${w.inRad > 0 ? `−${Math.min(STAB.cap * w.flockMult, w.inRad * STAB.coef * w.flockMult).toFixed(3)}%p/초`
                             : `+${STAB.rec}%p/초 회복`}`,
      `조각 ${m.pickedNight.toFixed(0)}줍고 ${(m.dropped - m.pickedNight).toFixed(0)} 필드에 남음`
        // 【08-02】 여기도 `pieceReq`(밤 내내 30 고정)를 쓰고 있었다 — 위 막대와 같은 거짓말이다.
        //   **남은 양**을 말한다. 「다음 N조각」은 이미 주운 몫을 빼야 뜻이 맞는다.
        + `  · 레벨업 ${m.levelUpsNight}회 (다음까지 ${Math.max(0, w.levelNeed - w.gauge).toFixed(0)}조각)`,
      this.cardLine(),
      this.soundLine(),
      ...(this.rescue ? [this.rescueLine()] : []),
      `피격 ${m.hits}회 / ${m.dmgTaken}뎀  (물량 ${m.hitsSwarm} · 정예 ${m.hitsElite})`
        + `  — **3-1: 피격은 안정성을 안 건드린다**`,
      `무피격 최장 ${m.noHitMax.toFixed(0)}s   동시 상한 점유 ${(m.atCapSec / Math.max(w.t, 1) * 100).toFixed(0)}%`,
      `킬 나 ${m.killsPlayer} / 개 ${m.killsDog} = 기여율 ${kills ? (m.killsPlayer / kills * 100).toFixed(0) : 0}%`,
      `명중률 ${m.shots ? (m.shotHits / m.shots * 100).toFixed(0) : 0}%`,
      `정지 ${(m.idleSec / Math.max(w.t, 1) * 100).toFixed(0)}%`,
      ``,
      // ── 8-6a 이펙트 = 강화 수치의 함수 (측정 ⑦) ────────────
      `[8-6a] 빌드 ${BUILD[this.buildKey].label} · 이펙트 ${this.effectMode === 'soft' ? '점근(tanh)' : '선형(대조군)'}`,
      ...(w.weapon === 'pasu' ? [
        `  연사 ${vis.rate.toFixed(2)}발/초 → 회전 ${(vis.spin / (2 * Math.PI)).toFixed(2)}회전/초`
          + `  (선형이면 ${(vis.spinLin / (2 * Math.PI)).toFixed(2)}, 상한 ${(vis.cap / (2 * Math.PI)).toFixed(2)})`,
        `  사거리 ${(vis.range / UNIT).toFixed(2)}유닛 → 궤도 반경 ${(vis.orbit / UNIT).toFixed(2)}유닛`
          + `  (선형이면 ${(vis.orbitLin / UNIT).toFixed(2)})`,
        `  투사체 ${vis.stones}개 → 도는 돌 ${vis.stones}개 (궤도가 감당하는 최대 ${vis.stoneCapK}개)`,
        `  **잔상 판정**: 접선 속도 ${vis.tangential.toFixed(0)}px/초 = 프레임당 ${(vis.tangential / 60).toFixed(1)}px`
          + `  / 돌 지름 ${(STONE_R * 2).toFixed(1)}px → ${vis.tangential / 60 <= STONE_R * 2 ? '개별 돌로 읽힘' : '**잔상**'}`,
        // ── 돌 여섯의 **네 축**이 지금 실제로 무엇을 하고 있나  【2026-07-30】 ──
        // ⚠ 이 줄이 없으면 튐·성장·세트가 **화면에서 관측 불가**다 — 프로브만 볼 수 있고
        //   사람은 못 본다. 강화 트리에서 「강해졌다는 느낌이 안 든다」가 났던 자리와 같다.
        this.gearAxisLine(),
      ] : []),
      ``,
      `WASD/←→ 이동 — 이게 전부다. 양은 없다.`,
      `안정성은 반경 안에 적이 있으면 계속 깎인다 — **맞아도 안 깎인다(3-1). HP만 깎인다.**`,
    ].join('\n'));
  }
}
